/*
------------------------------------------------------------------------------
Wonderful Walls Studio — Map Designer
Release: 2.1.0 — Shared Design Record Candidate R9 Production Handoff
File: assets/map-designer.js

ROLE
Client-side application logic for the shared Shopify Map Designer. This file
owns live composition state, MapLibre interaction, responsive/assisted-editing behaviour,
detail-inset geometry, review/acceptance, Shopify product/variant binding,
cart submission, Design Capture submission, the shared customer Design Record, and the QGIS production handoff.

MAINTENANCE MAP
1. DOM references and page-mode/product setup
2. Main MapLibre map and responsive stage management
3. Universal assisted editing / enlarged-workspace behaviour
4. Frame/orientation preservation
5. Detail-inset geometry and interaction
6. Main and inset QGIS production handoff
7. Accepted payload, validation and review state
8. Shopify cart/variant integration
9. Shared customer Design Record / static-map rendering
10. Etsy/Design Capture contact-form integration
11. Debug/diagnostic helpers
12. Search and final event wiring

PRODUCTION INVARIANTS
- Customer map bearing and pitch remain locked to zero for Release 2.1.0.
- The accepted payload is immutable after "Looks good" until editing resumes.
- EPSG:3857 extents plus mapped-artwork dimensions are the QGIS source of truth.
- Inset production data must preserve source/detail circle positions, detail
  extent, frame attachment and connector geometry.
- Shopify cart, Design Capture output and customer Design Records are derived from
  the accepted payload, never from an unaccepted live composition.

DIAGNOSTICS
The browser console API is exposed as window.WonderfullyWoodMapDesigner.
That legacy namespace is intentionally retained for compatibility with the
existing production/test instructions despite the Wonderful Walls Studio rebrand.
Use getReleaseInfo(), getProductionValidation(), getAcceptedProductionValidation(),
getCartReadiness(), getShopifyProductBinding(), verifyCartProductionRecordIntegrity(),
and the QGIS helper methods first when diagnosing a reported problem.

CART DIAGNOSTIC NOTE
Release 2.0.x deliberately stores a compact, exact production record in Shopify
cart properties rather than the complete accepted browser payload. Use
getCartProductionRecord(), reconstructProductionRecordFromCartProperties(), or
verifyCartProductionRecordIntegrity() when checking cart persistence.

HISTORICAL NOTES
Older Release/Unit/Stage comments remain near complex code where they explain why
behaviour exists. They are implementation history, not the current release number.
------------------------------------------------------------------------------
*/
document.addEventListener("DOMContentLoaded", () => {
    const MAP_DESIGNER_RELEASE = "2.1.0-candidate-r9-production-handoff";
    const MAP_DESIGNER_BRAND = "Wonderful Walls Studio";
    const MAP_DESIGNER_PRODUCTION_SCHEMA = "2.0";
    // Stable legacy identifier retained so historical production records and
    // any external fulfilment tooling can continue to recognise the record.
    const MAP_DESIGNER_PRODUCTION_RECORD_TYPE = "wonderfully_wood_qgis_production";
    const MAP_DESIGNER_MAINTENANCE_CONTRACT = {
        sharedSourceOfTruth: [
            "assets/map-designer.js",
            "assets/map-designer.css",
            "sections/map-designer.liquid",
            "snippets/map-designer-search.liquid",
            "snippets/map-designer-toolbar.liquid",
            "snippets/map-designer-map.liquid",
            "snippets/map-designer-product.liquid"
        ],
        sharedHelpSurfaces: [
            "sections/map-designer-help-entry.liquid",
            "sections/map-designer-help.liquid",
            "templates/page.map-designer-help.json"
        ],
        isolatedUtilityBoundary: [
            "layout/map-designer-etsy.liquid",
            "templates/page.map-designer-etsy.json",
            "templates/page.map-designer-help.json"
        ],
        etsyBoundaryOnly: [
            "sections/map-designer-etsy-intro.liquid",
            "templates/page.map-designer-etsy.json"
        ],
        rule: "New Map Designer capabilities belong in the shared source-of-truth files. Channel-boundary files may restrict, omit or explain shared capabilities but must not fork mapping, geometry or production logic. Shared help remains channel-neutral and uses the isolated utility layout when reached from the Etsy workflow.",
        referenceSchemaRule: "If a future Etsy order-significant option cannot be represented by E1, introduce a new reference schema rather than silently changing E1 semantics.",
        designRecordRule: "The customer Design Record is a shared post-completion feature derived from the accepted payload. It records composition/configuration, not finished-art detail or transactional authority. New composition features must extend the shared Design Record renderer rather than fork channel-specific renderers."
    };
    //--------------------------------------------------
    // Locate required elements
    //--------------------------------------------------
    const mapContainer = document.getElementById("map-designer-map");
    if (!mapContainer) {
        return;
    }
    const canvas = document.querySelector(".map-designer__canvas");
    if (!canvas) {
        return;
    }
    const orientationButtons = document.querySelectorAll(".map-designer__toggle");
    const sizeButtons = document.querySelectorAll(".map-designer__size");
    const insetToggleButtons = document.querySelectorAll(".map-designer__inset-toggle");
    const insetControls = document.querySelector("[data-inset-controls]");
    const insetSourceDiameterInput = document.querySelector("[data-inset-source-diameter]");
    const insetDetailDiameterInput = document.querySelector("[data-inset-detail-diameter]");
    const insetSourceDiameterOutput = document.querySelector("[data-inset-source-diameter-output]");
    const insetDetailDiameterOutput = document.querySelector("[data-inset-detail-diameter-output]");
    const insetDiameterStepButtons = document.querySelectorAll("[data-inset-diameter-step]");
    const insetPrecisionControls = document.querySelector("[data-inset-precision]");
    const insetPrecisionTargetButtons = document.querySelectorAll("[data-inset-precision-target]");
    const insetSourcePrecisionPanel = document.querySelector("[data-inset-source-precision]");
    const insetDetailPrecisionPanel = document.querySelector("[data-inset-detail-precision]");
    const insetSourceNudgeButtons = document.querySelectorAll("[data-inset-source-nudge]");
    const insetDetailNudgeButtons = document.querySelectorAll("[data-inset-detail-nudge]");
    const insetStatus = document.querySelector("[data-inset-status]");
    const selectionStatus = document.querySelector("[data-selection-status]");
    const mapTitleInput = document.querySelector("[data-map-title-input]");
    const customerGuidanceInput = document.querySelector("[data-customer-guidance-input]");
    const insetLayer = document.querySelector("[data-inset-layer]");
    const insetSourceHandle = document.querySelector("[data-inset-source]");
    const insetDetailHandle = document.querySelector("[data-inset-detail]");
    const insetConnectorSvg = document.querySelector("[data-inset-connectors]");
    const insetConnectorBands = document.querySelectorAll("[data-inset-connector-band]");
    const insetDetailMapContainer = document.getElementById("map-designer-inset-map");
    const zoomInButton = document.querySelector('[data-map-zoom="in"]');
    const zoomOutButton = document.querySelector('[data-map-zoom="out"]');
    const searchForm = document.querySelector("[data-map-search-form]");
    const searchInput = document.querySelector("[data-map-search-input]");
    const searchButton = document.querySelector("[data-map-search-button]");
    const searchMessage = document.querySelector("[data-map-search-message]");
    const mappedArea = document.querySelector(".map-designer__mapped-area");
    const productionPayloadField = document.querySelector("[data-production-payload-field]");
    const acceptedProductionPayloadField = document.querySelector("[data-accepted-production-payload-field]");
    const mapDesignerRoot = document.querySelector(".map-designer");
    const configurationControls = document.querySelector("[data-configuration-controls]");
    const mapWorkspace = document.querySelector("[data-map-workspace]");
    // These data hooks were introduced during the mobile-UX work and are kept
    // stable for markup compatibility. Release 2.0.1 makes their behaviour
    // universal rather than limiting it by viewport width.
    const assistedConfigSummary = document.querySelector("[data-mobile-config-summary]");
    const assistedChangeOptionsButton = document.querySelector("[data-mobile-change-options]");
    const assistedReturnToMapButton = document.querySelector("[data-mobile-return-to-map]");
    const inspectionViewport = document.querySelector("[data-inspection-viewport]");
    const inspectionStartWrap = document.querySelector("[data-inspection-start-wrap]");
    const inspectionStartButton = document.querySelector("[data-inspection-start]");
    const inspectionControls = document.querySelector("[data-inspection-controls]");
    const inspectionZoomOutButton = document.querySelector('[data-inspection-zoom="out"]');
    const inspectionZoomInButton = document.querySelector('[data-inspection-zoom="in"]');
    const inspectionScaleOutput = document.querySelector("[data-inspection-scale]");
    const inspectionFullButton = document.querySelector("[data-inspection-full]");
    const shopifyProductDataElement = document.querySelector("[data-map-designer-product-json]");
    const variantPrice = document.querySelector("[data-map-designer-variant-price]");
    const deliveryNote = document.querySelector("[data-map-designer-delivery-note]");
    const reviewPreviewButton = document.querySelector("[data-review-preview-button]");    const reviewPanel = document.querySelector("[data-review-panel]");
    const reviewLocation = document.querySelector("[data-review-location]");
    const reviewDetails = document.querySelector("[data-review-details]");
    const reviewStatus = document.querySelector("[data-review-status]");
    const reviewAcceptButton = document.querySelector("[data-review-accept-button]");
    const reviewEditButton = document.querySelector("[data-review-edit-button]");
    const cartPanel = document.querySelector("[data-cart-panel]");
    const cartSummary = document.querySelector("[data-cart-summary]");
    const cartReference = document.querySelector("[data-cart-reference]");
    const cartStatus = document.querySelector("[data-cart-status]");
    const cartSubmitButton = document.querySelector("[data-cart-submit]");
    const cartLink = document.querySelector("[data-cart-link]");
    const designCapturePanel = document.querySelector("[data-design-capture-panel]");
    const designCaptureForm = document.getElementById("MapDesignerDesignCaptureForm");
    const designCaptureStatus = document.querySelector("[data-design-capture-status]");
    const designCaptureSuccessMessage = document.querySelector("[data-design-capture-success]");
    const designCaptureReferencePanel = document.querySelector("[data-design-capture-reference-panel]");
    const designCaptureVisibleReference = document.querySelector("[data-design-capture-visible-reference]");
    const designCaptureConfigurationPanel = document.querySelector("[data-design-capture-configuration]");
    const designCaptureConfigurationSummary = document.querySelector("[data-design-capture-configuration-summary]");
    const designCaptureConfigurationOrientation = document.querySelector("[data-design-capture-configuration-orientation]");
    const designCaptureEtsyConfigurationField = document.querySelector("[data-design-capture-etsy-configuration]");
    const designCaptureReturnStatus = document.querySelector("[data-design-capture-return-status]");
    const designCaptureReturnLink = document.querySelector("[data-design-capture-return-link]");
    const designCaptureSubmitButton = document.querySelector("[data-design-capture-submit]");
    const designCapturePageModeField = document.querySelector("[data-design-capture-page-mode]");
    const designCaptureReferenceField = document.querySelector("[data-design-capture-reference]");
    const designCaptureReceiptSummaryField = document.querySelector("[data-design-capture-receipt-summary]");
    const designCaptureProductionSummaryField = document.querySelector("[data-design-capture-production-summary]");
    const designCapturePlainSummaryField = document.querySelector("[data-design-capture-plain-summary]");
    const designCaptureTechnicalEmailField = document.querySelector("[data-design-capture-technical-email]");
    const designRecordPanel = document.querySelector("[data-design-record-panel]");
    const designRecordIntro = document.querySelector("[data-design-record-intro]");
    const designRecordOpenButton = document.querySelector("[data-design-record-open]");
    const designRecordStatus = document.querySelector("[data-design-record-status]");
    const designRecordSheet = document.querySelector("[data-design-record-sheet]");
    const designRecordCanvas = document.querySelector("[data-design-record-canvas]");
    const designRecordAccessibleSummary = document.querySelector("[data-design-record-accessible-summary]");
    const designRecordPrintButton = document.querySelector("[data-design-record-print]");
    const designRecordSaveButton = document.querySelector("[data-design-record-save]");
    //--------------------------------------------------
    // Guard against missing map library
    //--------------------------------------------------
    if (typeof maplibregl === "undefined") {
        return;
    }
    //--------------------------------------------------
    // Product definitions
    //--------------------------------------------------
    const PRODUCTS = {
        "40x50": {
            label: "Gallery",
            physicalSizeLabel: "40 × 50 cm",
            outerWidthCm: 42,
            outerHeightCm: 52,
            exposedWidthCm: 38,
            exposedHeightCm: 48,
            artworkWidthCm: 40,
            artworkHeightCm: 50,
            mappedWidthCm: 37,
            mappedHeightCm: 47,
            blackFrameCm: 2,
            basswoodRevealCm: 0.5,
            mountingBorderCm: 1.5
        },
        "30x40": {
            label: "Studio",
            physicalSizeLabel: "30 × 40 cm",
            outerWidthCm: 32,
            outerHeightCm: 42,
            exposedWidthCm: 28,
            exposedHeightCm: 38,
            artworkWidthCm: 30,
            artworkHeightCm: 40,
            mappedWidthCm: 27,
            mappedHeightCm: 37,
            blackFrameCm: 2,
            basswoodRevealCm: 0.5,
            mountingBorderCm: 1.5
        }
    };
    //--------------------------------------------------
    // Channel boundary / page mode policy
    // Release 2.1.0 Etsy Isolation Candidate
    //--------------------------------------------------
    const ETSY_ISOLATED_BOUNDARY = "etsy_isolated";
    const DESIGN_REFERENCE_SCHEMA = "E1";
    const DESIGN_REFERENCE_CHARACTERS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    const DESIGN_REFERENCE_SIZE_CODES = {
        "30x40": "S",
        "40x50": "G"
    };
    const DESIGN_REFERENCE_ORIENTATION_CODES = {
        portrait: "P",
        landscape: "L"
    };
    const DESIGN_REFERENCE_DETAIL_CODES = {
        false: "N",
        true: "D"
    };
    function getChannelBoundary() {
        return mapDesignerRoot && mapDesignerRoot.dataset.mapDesignerChannelBoundary
            ? mapDesignerRoot.dataset.mapDesignerChannelBoundary
            : "shopify_store";
    }
    function isEtsyIsolatedBoundary() {
        return getChannelBoundary() === ETSY_ISOLATED_BOUNDARY;
    }

    //--------------------------------------------------
    // Shopify product binding
    // Release 0.9.0 Unit 1
    //--------------------------------------------------
    const SHOPIFY_PRODUCT_BINDING = {
        optionNames: {
            size: ["Frame size", "Size"],
            orientation: ["Orientation"],
            inset: ["Detail inset", "Inset"]
        },
        optionValues: {
            size: {
                "30x40": "Studio",
                "40x50": "Gallery"
            },
            orientation: {
                portrait: "Portrait",
                landscape: "Landscape"
            },
            inset: {
                false: "No inset",
                true: "Add detail inset"
            }
        }
    };
    function normaliseCommerceValue(value) {
        return String(value || "")
            .trim()
            .toLocaleLowerCase();
    }
    function readShopifyProductData() {
        if (isEtsyIsolatedBoundary()) {
            return null;
        }
        if (!shopifyProductDataElement) {
            return null;
        }
        const source = shopifyProductDataElement.textContent.trim();
        if (!source) {
            return null;
        }
        try {
            const product = JSON.parse(source);
            if (!product ||
                typeof product !== "object" ||
                !Array.isArray(product.options) ||
                !Array.isArray(product.variants)) {
                return null;
            }
            return product;
        }
        catch (error) {
            return null;
        }
    }
    const initialShopifyProduct = readShopifyProductData();
    //--------------------------------------------------
    // Display settings
    //--------------------------------------------------
    const REFERENCE_OUTER_LONG_CM = 52;
    const REFERENCE_LONG_SIDE_PERCENT = 92;
    //--------------------------------------------------
    // Universal assisted editing workspace
    // Release 2.0.1; originated in Mobile UX Stage 4a
    //--------------------------------------------------
    const INSPECTION_DEFAULT_SCALE = 2;
    const INSPECTION_MIN_SCALE = 1.5;
    const INSPECTION_MAX_SCALE = 3;
    const INSPECTION_SCALE_STEP = 0.5;
    // Assisted inset interaction geometry. These values affect only the screen
    // hit area; they do not alter the visible or production circle sizes.
    // The source-circle allowance is measured OUTWARD FROM ITS VISIBLE
    // PERIMETER so a fingertip can remain clear of the visual target.
    const INSET_TOUCH_TARGET_MIN_PX = 48;
    const INSET_SOURCE_TOUCH_MARGIN_PX = 44;
    // Active inset collision handling. Contact between the framed circles is
    // valid; only actual penetration is blocked. The source circle can glide
    // around the detail-circle exclusion boundary, while the perimeter-bound
    // detail circle may skip across the blocked perimeter interval created by
    // the source circle rather than forcing a long trip around the frame.
    const INSET_COLLISION_EPSILON_MM = 0.001;
    const INSET_COLLISION_PATH_SAMPLE_MM = 4;
    const INSET_COLLISION_BINARY_ITERATIONS = 14;
    //--------------------------------------------------
    // Detail inset production constants
    // Release 2.0.0 Unit 1
    //--------------------------------------------------
    const INSET_SOURCE_MIN_DIAMETER_MM = 11;
    const INSET_SOURCE_MAX_DIAMETER_MM = 50;
    const INSET_SOURCE_DEFAULT_DIAMETER_MM = 25;
    const INSET_SOURCE_FRAME_MM = 1;
    const INSET_DETAIL_MIN_DIAMETER_MM = 90;
    const INSET_DETAIL_MAX_DIAMETER_MM = 115;
    const INSET_DETAIL_DEFAULT_DIAMETER_MM = 100;
    const INSET_DETAIL_FRAME_MM = 2.75;
    // Geometric penetration of the detail inset outer frame past the artwork edge.
    // This deliberately uses a very small penetration value: across the supported
    // 90–115 mm detail diameters it produces an attachment chord of approximately
    // 1.95–2.20 mm. Do not substitute the chord width itself for this value.
    const INSET_FRAME_OVERLAP_MM = 0.01;
    const INSET_KEYBOARD_STEP_MM = 1;
    const INSET_KEYBOARD_LARGE_STEP_MM = 5;
    const INSET_NUDGE_REPEAT_DELAY_MS = 450;
    const INSET_NUDGE_REPEAT_INTERVAL_MS = 120;
    //--------------------------------------------------
    // Search settings
    //--------------------------------------------------
    const SEARCH_ENDPOINT = "https://api.stadiamaps.com/geocoding/v1/search";
    const SEARCH_TIMEOUT_MS = 8000;
    //--------------------------------------------------
    // Shopify cart settings
    // Release 0.9.0 Unit 2
    //--------------------------------------------------
    const CART_PROPERTY_CHUNK_LENGTH = 240;
    const CART_REQUEST_TIMEOUT_MS = 12000;
    //--------------------------------------------------
    // Shared customer Design Record settings
    // Release 2.1.0 Candidate R6
    //--------------------------------------------------
    const DESIGN_RECORD_SCHEMA = "1.0";
    const DESIGN_RECORD_STATIC_STYLE = "alidade_smooth";
    const DESIGN_RECORD_STATIC_STAGE_SIZE = 512;
    const DESIGN_RECORD_STATIC_DETAIL_SIZE = 256;
    const DESIGN_RECORD_STATIC_MAX_ZOOM = 18;
    const DESIGN_RECORD_STATIC_MIN_REQUEST_SIDE = 64;
    const DESIGN_RECORD_STATIC_MAX_REQUEST_SIDE = 1024;
    const DESIGN_RECORD_WEB_MERCATOR_WORLD_METERS = 40075016.68557849;
    // Stadia Static Maps uses GL-style zoom semantics: at zoom 0 the world
    // spans 512 screen-independent points. This is intentionally 512, not the
    // 256-pixel XYZ raster-tile size. Using 256 makes an extent-derived static
    // image approximately one zoom level too tight.
    const DESIGN_RECORD_STATIC_WORLD_POINTS_AT_Z0 = 512;
    const DESIGN_RECORD_CANVAS_WIDTH = 720;
    const DESIGN_RECORD_COMPOSITION_LONG_SIDE = 540;
    const DESIGN_RECORD_BACKGROUND = "#fbfaf7";
    const DESIGN_RECORD_BLACK_FRAME = "#242323";
    const DESIGN_RECORD_BASSWOOD = "#d8c8a7";
    const DESIGN_RECORD_TEXT = "#222222";
    const DESIGN_RECORD_MUTED_TEXT = "#5f5a52";
    const DESIGN_RECORD_STATIC_ENDPOINT =
        `https://tiles.stadiamaps.com/static_cacheable/${DESIGN_RECORD_STATIC_STYLE}.png`;
    //--------------------------------------------------
    // Page mode settings
    // Release 0.8.11
    //--------------------------------------------------
    const DEFAULT_PAGE_MODE = "shopify_store";
    const SUPPORTED_PAGE_MODES = [
        "shopify_store",
        "design_capture"
    ];
    function normalisePageMode(value) {
        return SUPPORTED_PAGE_MODES.includes(value)
            ? value
            : DEFAULT_PAGE_MODE;
    }
    function getInitialPageMode() {
        if (!mapDesignerRoot) {
            return DEFAULT_PAGE_MODE;
        }
        if (isEtsyIsolatedBoundary()) {
            return "design_capture";
        }
        return normalisePageMode(mapDesignerRoot.dataset.mapDesignerPageMode);
    }
    //--------------------------------------------------
    // Current application state
    //--------------------------------------------------
    const state = {
        orientation: "portrait",
        size: "40x50",
        page: {
            mode: getInitialPageMode(),
            source: "section_setting"
        },
        map: {
            center: null,
            zoom: null,
            isProgrammaticMove: false
        },
        inspection: {
            active: false,
            scale: 1,
            translateX: 0,
            translateY: 0,
            pointerId: null,
            pointerStartX: 0,
            pointerStartY: 0,
            translateStartX: 0,
            translateStartY: 0
        },
        search: {
            query: "",
            status: "idle",
            result: null,
            error: null,
            attempts: 0,
            noResultAttempts: 0,
            selectedLocation: null,
            compositionAdjustedAfterSearch: false
        },
        mapTitle: {
            value: "",
            source: "empty",
            customerEdited: false,
            suggestedFromSearch: ""
        },
        customerGuidance: {
            note: ""
        },
        inset: {
            enabled: false,
            sourceDiameterMm: INSET_SOURCE_DEFAULT_DIAMETER_MM,
            sourceFrameMm: INSET_SOURCE_FRAME_MM,
            sourceCenterMm: null,
            detailDiameterMm: INSET_DETAIL_DEFAULT_DIAMETER_MM,
            detailFrameMm: INSET_DETAIL_FRAME_MM,
            structuralOverlapMm: INSET_FRAME_OVERLAP_MM,
            detailCenterMm: null,
            attachment: null,
            geographicBounds: null,
            qgisExtent3857: null,
            geometryValid: true,
            validationMessage: ""
        },
        production: {
            currentPayload: null,
            lastUpdatedAt: null,
            validation: null
        },
        review: {
            status: "editing",
            lockedPayload: null,
            acceptedPayload: null,
            acceptedAt: null,
            designReference: null,
            isReadyForCart: false
        },
        commerce: {
            product: initialShopifyProduct,
            binding: null,
            selectedVariant: null,
            lastCheckedAt: null
        },
        designRecord: {
            completionChannel: null,
            snapshot: null,
            status: "unavailable",
            lastError: null,
            renderedReference: null,
            renderedBlob: null,
            staticRequestUrls: [],
            imageCache: new Map()
        },
        cart: {
            readiness: null,
            lastCheckedAt: null,
            requestStatus: "idle",
            submittedAt: null,
            addedAt: null,
            addedLineItem: null,
            lastResponse: null,
            lastError: null
        }
    };
    // Screen-only precision-control target. This is deliberately kept outside
    // the production state/payload.
    let insetPrecisionTarget = "source";
    const MAP_TITLE_MAX_LENGTH = 40;
    function normaliseMapTitle(value) {
        return String(value || "")
            .replace(/\s+/g, " ")
            .trim()
            .slice(0, MAP_TITLE_MAX_LENGTH);
    }
    function normaliseCustomerGuidance(value) {
        return String(value || "")
            .replace(/\s+/g, " ")
            .trim()
            .slice(0, 300);
    }
    //--------------------------------------------------
    // Shopify variant resolution
    // Release 2.0.0 Unit 3e
    //--------------------------------------------------
    function findProductOptionIndex(product, acceptedNames) {
        if (!product || !Array.isArray(product.options)) {
            return -1;
        }
        const normalisedNames = acceptedNames.map(normaliseCommerceValue);
        return product.options.findIndex(optionName => normalisedNames.includes(normaliseCommerceValue(optionName)));
    }
    function getVariantOptionValue(variant, optionIndex) {
        if (!variant || optionIndex < 0) {
            return "";
        }
        if (Array.isArray(variant.options)) {
            return variant.options[optionIndex] || "";
        }
        return variant[`option${optionIndex + 1}`] || "";
    }
    function getExpectedVariantCombinations() {
        const combinations = [];
        ["30x40", "40x50"].forEach(sizeKey => {
            ["portrait", "landscape"].forEach(orientationKey => {
                [false, true].forEach(insetEnabled => {
                    combinations.push({
                        sizeKey,
                        sizeValue: SHOPIFY_PRODUCT_BINDING.optionValues.size[sizeKey],
                        orientationKey,
                        orientationValue: SHOPIFY_PRODUCT_BINDING.optionValues.orientation[orientationKey],
                        insetEnabled,
                        insetValue: SHOPIFY_PRODUCT_BINDING.optionValues.inset[String(insetEnabled)]
                    });
                });
            });
        });
        return combinations;
    }
    function findVariantMatches(product, sizeOptionIndex, orientationOptionIndex, insetOptionIndex, sizeValue, orientationValue, insetValue) {
        if (!product || !Array.isArray(product.variants)) {
            return [];
        }
        const expectedSize = normaliseCommerceValue(sizeValue);
        const expectedOrientation = normaliseCommerceValue(orientationValue);
        const expectedInset = normaliseCommerceValue(insetValue);
        return product.variants.filter(variant => normaliseCommerceValue(getVariantOptionValue(variant, sizeOptionIndex)) === expectedSize &&
            normaliseCommerceValue(getVariantOptionValue(variant, orientationOptionIndex)) === expectedOrientation &&
            normaliseCommerceValue(getVariantOptionValue(variant, insetOptionIndex)) === expectedInset);
    }
    function buildShopifyProductSummary(product) {
        if (!product) {
            return null;
        }
        return {
            id: product.id || null,
            title: product.title || "",
            handle: product.handle || "",
            available: product.available === true,
            options: Array.isArray(product.options)
                ? [...product.options]
                : []
        };
    }
    function buildShopifyVariantSummary(variant) {
        if (!variant) {
            return null;
        }
        return {
            id: variant.id || null,
            title: variant.title || "",
            available: variant.available === true,
            price: Number.isFinite(Number(variant.price))
                ? Number(variant.price)
                : null,
            sku: variant.sku || "",
            options: Array.isArray(variant.options)
                ? [...variant.options]
                : [
                    variant.option1,
                    variant.option2,
                    variant.option3
                ].filter(Boolean)
        };
    }
    function validateShopifyProductBinding() {
        const checkedAt = new Date().toISOString();
        const errors = [];
        const warnings = [];
        const matrix = [];
        const product = state.commerce.product;
        if (!product) {
            errors.push({
                code: "shopify_product_missing",
                message: "No Shopify product is selected in the Map Designer section settings."
            });
            return {
                isValid: false,
                status: "not_configured",
                checkedAt,
                product: null,
                variant: null,
                matrix,
                errors,
                warnings
            };
        }
        const sizeOptionIndex = findProductOptionIndex(product, SHOPIFY_PRODUCT_BINDING.optionNames.size);
        const orientationOptionIndex = findProductOptionIndex(product, SHOPIFY_PRODUCT_BINDING.optionNames.orientation);
        const insetOptionIndex = findProductOptionIndex(product, SHOPIFY_PRODUCT_BINDING.optionNames.inset);
        if (sizeOptionIndex < 0) {
            errors.push({
                code: "shopify_size_option_missing",
                message: "The selected Shopify product needs a Frame size option."
            });
        }
        if (orientationOptionIndex < 0) {
            errors.push({
                code: "shopify_orientation_option_missing",
                message: "The selected Shopify product needs an Orientation option."
            });
        }
        if (insetOptionIndex < 0) {
            errors.push({
                code: "shopify_inset_option_missing",
                message: "The selected Shopify product needs a Detail inset option."
            });
        }
        if (sizeOptionIndex >= 0 &&
            orientationOptionIndex >= 0 &&
            insetOptionIndex >= 0) {
            getExpectedVariantCombinations().forEach(combination => {
                const matches = findVariantMatches(product, sizeOptionIndex, orientationOptionIndex, insetOptionIndex, combination.sizeValue, combination.orientationValue, combination.insetValue);
                matrix.push({
                    ...combination,
                    matchCount: matches.length,
                    variant: matches.length === 1
                        ? buildShopifyVariantSummary(matches[0])
                        : null
                });
                if (matches.length === 0) {
                    errors.push({
                        code: "shopify_variant_missing",
                        message: `${combination.sizeValue} / ${combination.orientationValue} / ${combination.insetValue} is missing from the selected Shopify product.`,
                        selection: combination
                    });
                }
                if (matches.length > 1) {
                    errors.push({
                        code: "shopify_variant_duplicate",
                        message: `${combination.sizeValue} / ${combination.orientationValue} / ${combination.insetValue} is represented by more than one Shopify variant.`,
                        selection: combination
                    });
                }
            });
        }
        const selectedSizeValue = SHOPIFY_PRODUCT_BINDING.optionValues.size[state.size];
        const selectedOrientationValue = SHOPIFY_PRODUCT_BINDING.optionValues.orientation[state.orientation];
        const selectedInsetValue = SHOPIFY_PRODUCT_BINDING.optionValues.inset[String(Boolean(state.inset.enabled))];
        let selectedVariant = null;
        if (!selectedSizeValue) {
            errors.push({
                code: "shopify_selected_size_unsupported",
                message: "The selected Map Designer frame size cannot be matched to Shopify."
            });
        }
        if (!selectedOrientationValue) {
            errors.push({
                code: "shopify_selected_orientation_unsupported",
                message: "The selected Map Designer orientation cannot be matched to Shopify."
            });
        }
        if (!selectedInsetValue) {
            errors.push({
                code: "shopify_selected_inset_unsupported",
                message: "The selected Map Designer detail-inset choice cannot be matched to Shopify."
            });
        }
        if (sizeOptionIndex >= 0 &&
            orientationOptionIndex >= 0 &&
            insetOptionIndex >= 0 &&
            selectedSizeValue &&
            selectedOrientationValue &&
            selectedInsetValue) {
            const selectedMatches = findVariantMatches(product, sizeOptionIndex, orientationOptionIndex, insetOptionIndex, selectedSizeValue, selectedOrientationValue, selectedInsetValue);
            if (selectedMatches.length === 1) {
                selectedVariant = selectedMatches[0];
            }
        }
        if (selectedVariant && !selectedVariant.id) {
            errors.push({
                code: "shopify_variant_id_missing",
                message: "The matched Shopify variant does not have a usable variant ID."
            });
        }
        if (selectedVariant && selectedVariant.available !== true) {
            errors.push({
                code: "shopify_variant_unavailable",
                message: "The matched Shopify variant is not currently available for purchase."
            });
        }
        return {
            isValid: errors.length === 0,
            status: errors.length === 0 ? "ready" : "invalid",
            checkedAt,
            product: buildShopifyProductSummary(product),
            variant: buildShopifyVariantSummary(selectedVariant),
            selectedOptions: {
                size: selectedSizeValue || null,
                orientation: selectedOrientationValue || null,
                inset: selectedInsetValue || null
            },
            optionIndexes: {
                size: sizeOptionIndex,
                orientation: orientationOptionIndex,
                inset: insetOptionIndex
            },
            matrix,
            errors,
            warnings
        };
    }
    function formatShopifyPrice(price) {
        const numericPrice = Number(price);
        if (!Number.isFinite(numericPrice)) {
            return "";
        }
        const currency = mapDesignerRoot &&
            mapDesignerRoot.dataset.mapDesignerCurrency
            ? mapDesignerRoot.dataset.mapDesignerCurrency
            : "GBP";
        try {
            return new Intl.NumberFormat(undefined, {
                style: "currency",
                currency
            }).format(numericPrice / 100);
        }
        catch (error) {
            return `${(numericPrice / 100).toFixed(2)}`;
        }
    }
    function updateShopifyVariantPrice(binding) {
        if (!variantPrice) {
            return;
        }

        // Shopify pricing and delivery information are customer-facing only
        // in Shopify Store Mode. Design Capture / Etsy pages must never expose
        // Shopify commercial information, even if a product is accidentally
        // left selected in the section settings.
        if (!isShopifyStoreMode()) {
            variantPrice.textContent = "";
            variantPrice.hidden = true;

        if (deliveryNote) {
            deliveryNote.hidden = true;
        }

        return;
    }

    const price = binding &&
        binding.variant &&
        Number.isFinite(binding.variant.price)
        ? formatShopifyPrice(binding.variant.price)
        : "";

    if (!price) {
        variantPrice.textContent = "";
        variantPrice.hidden = true;

        if (deliveryNote) {
            deliveryNote.hidden = true;
        }

        return;
    }

    variantPrice.textContent = `Launch price: ${price}`;
    variantPrice.hidden = false;

    if (deliveryNote) {
        deliveryNote.hidden = false;
    }
}
    function updateShopifyProductBinding() {
        const binding = validateShopifyProductBinding();
        state.commerce.binding = binding;
        state.commerce.selectedVariant = binding.variant;
        state.commerce.lastCheckedAt = binding.checkedAt;
        if (mapDesignerRoot) {
            mapDesignerRoot.dataset.shopifyProductBindingStatus =
                binding.status;
            mapDesignerRoot.dataset.shopifyProductId =
                binding.product && binding.product.id
                    ? String(binding.product.id)
                    : "";
            mapDesignerRoot.dataset.shopifyVariantId =
                binding.variant && binding.variant.id
                    ? String(binding.variant.id)
                    : "";
        }
        updateShopifyVariantPrice(binding);
        return binding;
    }
    //--------------------------------------------------
    // Create map
    //--------------------------------------------------
    const map = new maplibregl.Map({
        container: "map-designer-map",
        style: "https://tiles.stadiamaps.com/styles/alidade_smooth.json",
        center: [0, 20],
        zoom: 1.4,
        // Release 2.0 Unit 3a — launch orientation policy.
        // The production handoff is axis-aligned, so customer composition
        // must remain north-up and flat. Pan and zoom remain available.
        bearing: 0,
        pitch: 0,
        maxPitch: 0,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
        transformCameraUpdate: cameraUpdate => ({
            ...cameraUpdate,
            bearing: 0,
            pitch: 0,
            roll: 0
        }),
        attributionControl: false
    });
    function enforceLaunchOrientationPolicy() {
        if (map.dragRotate && typeof map.dragRotate.disable === "function") {
            map.dragRotate.disable();
        }
        if (map.touchZoomRotate &&
            typeof map.touchZoomRotate.disableRotation === "function") {
            // Preserve two-finger pinch zoom while preventing twist rotation.
            map.touchZoomRotate.disableRotation();
        }
        if (map.touchPitch && typeof map.touchPitch.disable === "function") {
            map.touchPitch.disable();
        }
        const bearing = typeof map.getBearing === "function"
            ? map.getBearing()
            : 0;
        const pitch = typeof map.getPitch === "function"
            ? map.getPitch()
            : 0;
        if (Math.abs(bearing) > 0.0001 || Math.abs(pitch) > 0.0001) {
            map.jumpTo({
                bearing: 0,
                pitch: 0
            });
        }
    }
    enforceLaunchOrientationPolicy();
    let insetDetailMap = null;
    // Unit 2b: responsive stage resizing must not change the geographic
    // composition. The map canvas is a square whose pixel size follows the
    // designer stage width. If that width changes while MapLibre keeps the
    // same zoom, the geographic area beneath a fixed artwork-mm position
    // changes. Track the previous stage width so zoom can be adjusted by the
    // exact responsive scale ratio.
    let lastResponsiveStageWidthPx = null;
    let stageResizeAnimationFrame = null;
    let isResponsiveCompositionResize = false;
    function ensureInsetDetailMap() {
        if (insetDetailMap || !insetDetailMapContainer) {
            return insetDetailMap;
        }
        insetDetailMap = new maplibregl.Map({
            container: insetDetailMapContainer,
            style: "https://tiles.stadiamaps.com/styles/alidade_smooth.json",
            center: [0, 20],
            zoom: 1.4,
            attributionControl: false,
            interactive: false
        });
        insetDetailMap.on("load", () => {
            const insetCanvas = insetDetailMap.getCanvas();
            if (insetCanvas) {
                insetCanvas.setAttribute("tabindex", "-1");
                insetCanvas.setAttribute("aria-hidden", "true");
            }
            updateInsetDetailMap();
        });
        return insetDetailMap;
    }
    //--------------------------------------------------
    // Stable stage resize handling
    //--------------------------------------------------
    function resizeMapToStage(previousStageWidthPx, nextStageWidthPx) {
        const previousWidth = Number(previousStageWidthPx);
        const nextWidth = Number(nextStageWidthPx);
        const shouldPreserveGeography = Number.isFinite(previousWidth) &&
            Number.isFinite(nextWidth) &&
            previousWidth > 0 &&
            nextWidth > 0 &&
            Math.abs(nextWidth - previousWidth) > 0.5;
        const previousCenter = map.getCenter();
        const previousZoom = map.getZoom();
        // Unit 3a: responsive resizing preserves geography but never
        // preserves a rotated or pitched camera. Launch compositions are
        // intentionally north-up and flat.
        const previousBearing = 0;
        const previousPitch = 0;
        isResponsiveCompositionResize = true;
        try {
            map.resize();
            if (shouldPreserveGeography) {
                const responsiveScale = nextWidth / previousWidth;
                const zoomAdjustment = Math.log2(responsiveScale);
                if (Number.isFinite(zoomAdjustment) &&
                    Math.abs(zoomAdjustment) > 0.000001) {
                    map.jumpTo({
                        center: [previousCenter.lng, previousCenter.lat],
                        zoom: previousZoom + zoomAdjustment,
                        bearing: previousBearing,
                        pitch: previousPitch
                    });
                }
            }
            if (insetDetailMap) {
                insetDetailMap.resize();
            }
            enforceLaunchOrientationPolicy();
        }
        finally {
            isResponsiveCompositionResize = false;
        }
        updateMapState();
        if (state.inspection.active) {
            applyInspectionTransform();
        }
        updateInsetGeometry();
        updateProductionPayload();
    }
    function scheduleStageResize() {
        if (stageResizeAnimationFrame !== null) {
            window.cancelAnimationFrame(stageResizeAnimationFrame);
        }
        stageResizeAnimationFrame = window.requestAnimationFrame(() => {
            stageResizeAnimationFrame = null;
            const previousStageWidthPx = lastResponsiveStageWidthPx;
            updateProductFrame();
            const nextStageWidthPx = canvas.clientWidth;
            resizeMapToStage(previousStageWidthPx, nextStageWidthPx);
            lastResponsiveStageWidthPx = nextStageWidthPx;
        });
    }
    if (typeof ResizeObserver !== "undefined") {
        const stageResizeObserver = new ResizeObserver(() => {
            scheduleStageResize();
        });
        stageResizeObserver.observe(canvas);
    }
    else {
        window.addEventListener("resize", scheduleStageResize);
    }
    map.on("load", () => {
        const restoredConfirmedDesign =
            restoreConfirmedDesignCaptureVisual();
        if (restoredConfirmedDesign) {
            setDesignCaptureConfirmedViewLocked(true);
        }
        if (!restoredConfirmedDesign) {
            updateMapState();
            updateInsetGeometry();
            updateProductionPayload();
        }
        scheduleStageResize();
    });
    map.on("moveend", () => {
        if (isResponsiveCompositionResize) {
            return;
        }
        updateMapState();
        updateInsetGeometry();
        updateProductionPayload();
    });
    [
        "dragstart",
        "zoomstart",
        "rotatestart",
        "pitchstart"
    ].forEach(eventName => {
        map.on(eventName, () => {
            if (isResponsiveCompositionResize) {
                return;
            }
            markCompositionAdjustedAfterSearch();
        });
    });
    //--------------------------------------------------
    // Map state foundation
    //--------------------------------------------------
    function updateMapState() {
        const center = map.getCenter();
        state.map.center = {
            lng: center.lng,
            lat: center.lat
        };
        state.map.zoom = map.getZoom();
    }
    //--------------------------------------------------
    // Search anchor vs framed composition
    // Release 0.8.10
    //--------------------------------------------------
    function markCompositionAdjustedAfterSearch() {
        if (!state.search.selectedLocation) {
            return;
        }
        if (state.search.compositionAdjustedAfterSearch) {
            return;
        }
        if (state.map.isProgrammaticMove) {
            return;
        }
        if (state.review.status !== "editing") {
            return;
        }
        state.search.compositionAdjustedAfterSearch = true;
        updateProductionPayload();
    }
    function beginProgrammaticMapMove() {
        state.map.isProgrammaticMove = true;
    }
    function endProgrammaticMapMove() {
        state.map.isProgrammaticMove = false;
    }
    //--------------------------------------------------
    // Motion preference helper
    //--------------------------------------------------
    function prefersReducedMotion() {
        return window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
    function getSearchMoveDuration() {
        return prefersReducedMotion() ? 0 : 850;
    }
    //--------------------------------------------------
    // Universal workspace navigation
    // Release 2.0.1; originated in Mobile UX Stage 3a
    //--------------------------------------------------
    function updateAssistedConfigurationSummary() {
        if (!assistedConfigSummary) {
            return;
        }
        const product = PRODUCTS[state.size];
        const sizeLabel = product ? product.label : state.size;
        const orientationLabel = state.orientation === "landscape" ? "Landscape" : "Portrait";
        const insetLabel = state.inset.enabled ? "Detail inset" : "No inset";
        assistedConfigSummary.textContent =
            `${sizeLabel} · ${orientationLabel} · ${insetLabel}`;
    }
    function scrollDesignerElementIntoView(element) {
        if (!element || typeof element.scrollIntoView !== "function") {
            return;
        }
        element.scrollIntoView({
            behavior: prefersReducedMotion() ? "auto" : "smooth",
            block: "start"
        });
    }
    if (assistedChangeOptionsButton) {
        assistedChangeOptionsButton.addEventListener("click", () => {
            scrollDesignerElementIntoView(configurationControls);
        });
    }
    if (assistedReturnToMapButton) {
        assistedReturnToMapButton.addEventListener("click", () => {
            scrollDesignerElementIntoView(mapWorkspace);
        });
    }
    //--------------------------------------------------
    // Universal enlarged workspace
    // Release 2.0.1; originated in Mobile UX Stage 4a
    //
    // Availability is intentionally based on editing state, not viewport width
    // or pointer type. Phones, tablets, hybrid devices and desktop users all get
    // the same optional precision workspace; responsive CSS still controls only
    // presentation/layout.
    //--------------------------------------------------
    function getInspectionVisualScale() {
        return state.inspection.active
            ? state.inspection.scale
            : 1;
    }
    function getInspectionViewportSize() {
        if (!inspectionViewport || !canvas) {
            return null;
        }
        const width = inspectionViewport.clientWidth;
        const height = inspectionViewport.clientHeight;
        if (!width || !height) {
            return null;
        }
        return {
            width,
            height,
            canvasWidth: canvas.clientWidth,
            canvasHeight: canvas.clientHeight
        };
    }
    function constrainInspectionTranslation(x, y, scale) {
        const viewport = getInspectionViewportSize();
        if (!viewport) {
            return { x: 0, y: 0 };
        }
        const scaledWidth = viewport.canvasWidth * scale;
        const scaledHeight = viewport.canvasHeight * scale;
        const minimumX = Math.min(0, viewport.width - scaledWidth);
        const minimumY = Math.min(0, viewport.height - scaledHeight);
        return {
            x: clampNumber(x, minimumX, 0),
            y: clampNumber(y, minimumY, 0)
        };
    }
    function applyInspectionTransform() {
        if (!canvas) {
            return;
        }
        if (!state.inspection.active) {
            canvas.style.removeProperty("transform");
            return;
        }
        const constrained = constrainInspectionTranslation(state.inspection.translateX, state.inspection.translateY, state.inspection.scale);
        state.inspection.translateX = constrained.x;
        state.inspection.translateY = constrained.y;
        canvas.style.transform =
            `matrix(${state.inspection.scale}, 0, 0, ${state.inspection.scale}, ` +
                `${state.inspection.translateX}, ${state.inspection.translateY})`;
        if (inspectionScaleOutput) {
            inspectionScaleOutput.textContent =
                `${Math.round(state.inspection.scale * 100)}%`;
        }
    }
    function syncInspectionAvailability() {
        const canUseInspection = Boolean(
            (state.review.status === "editing" && state.inset.enabled) ||
            state.review.status === "previewing"
        );
        if (inspectionStartWrap) {
            inspectionStartWrap.hidden =
                !canUseInspection || state.inspection.active;
        }
        if (inspectionControls) {
            inspectionControls.hidden = !state.inspection.active;
        }
        if (inspectionStartButton) {
            inspectionStartButton.setAttribute("aria-pressed", String(state.inspection.active));
            inspectionStartButton.textContent =
                state.review.status === "previewing"
                    ? "Inspect preview"
                    : "Enlarge workspace";
        }
        if (inspectionZoomOutButton) {
            inspectionZoomOutButton.disabled =
                state.inspection.scale <= INSPECTION_MIN_SCALE;
        }
        if (inspectionZoomInButton) {
            inspectionZoomInButton.disabled =
                state.inspection.scale >= INSPECTION_MAX_SCALE;
        }
    }
    function setInspectionScale(nextScale) {
        if (!state.inspection.active) {
            return;
        }
        const viewport = getInspectionViewportSize();
        if (!viewport) {
            return;
        }
        const previousScale = state.inspection.scale;
        const scale = clampNumber(nextScale, INSPECTION_MIN_SCALE, INSPECTION_MAX_SCALE);
        if (Math.abs(scale - previousScale) < 0.001) {
            return;
        }
        const focusX = viewport.width / 2;
        const focusY = viewport.height / 2;
        const baseFocusX = (focusX - state.inspection.translateX) / previousScale;
        const baseFocusY = (focusY - state.inspection.translateY) / previousScale;
        state.inspection.scale = scale;
        state.inspection.translateX =
            focusX - baseFocusX * scale;
        state.inspection.translateY =
            focusY - baseFocusY * scale;
        applyInspectionTransform();
        syncInspectionAvailability();
    }
    function enterInspectionView() {
        const canEnterInspection = Boolean(
            (state.review.status === "editing" && state.inset.enabled) ||
            state.review.status === "previewing"
        );
        if (state.inspection.active || !canEnterInspection) {
            return;
        }
        const viewport = getInspectionViewportSize();
        if (!viewport) {
            return;
        }
        state.inspection.active = true;
        state.inspection.scale = INSPECTION_DEFAULT_SCALE;
        state.inspection.translateX =
            (viewport.width - viewport.canvasWidth * state.inspection.scale) / 2;
        state.inspection.translateY =
            (viewport.height - viewport.canvasHeight * state.inspection.scale) / 2;
        if (mapDesignerRoot) {
            mapDesignerRoot.classList.add("is-inspection-view");
        }
        setMapInteractionsEnabled(false);
        applyInspectionTransform();
        syncInspectionAvailability();
    }
    function finishInspectionPan() {
        state.inspection.pointerId = null;
        if (mapDesignerRoot) {
            mapDesignerRoot.classList.remove("is-inspection-panning");
        }
    }
    function exitInspectionView(options = {}) {
        const restoreMapInteractions = options.restoreMapInteractions !== false;
        if (!state.inspection.active) {
            syncInspectionAvailability();
            return;
        }
        finishInspectionPan();
        state.inspection.active = false;
        state.inspection.scale = 1;
        state.inspection.translateX = 0;
        state.inspection.translateY = 0;
        if (mapDesignerRoot) {
            mapDesignerRoot.classList.remove("is-inspection-view", "is-inspection-panning");
        }
        applyInspectionTransform();
        syncInspectionAvailability();
        if (restoreMapInteractions &&
            state.review.status === "editing") {
            setMapInteractionsEnabled(true);
        }
    }
    if (inspectionStartButton) {
        inspectionStartButton.addEventListener("click", () => {
            enterInspectionView();
        });
    }
    if (inspectionZoomOutButton) {
        inspectionZoomOutButton.addEventListener("click", () => {
            setInspectionScale(state.inspection.scale - INSPECTION_SCALE_STEP);
        });
    }
    if (inspectionZoomInButton) {
        inspectionZoomInButton.addEventListener("click", () => {
            setInspectionScale(state.inspection.scale + INSPECTION_SCALE_STEP);
        });
    }
    if (inspectionFullButton) {
        inspectionFullButton.addEventListener("click", () => {
            exitInspectionView();
        });
    }
    if (inspectionViewport) {
        inspectionViewport.addEventListener("pointerdown", event => {
            if (!state.inspection.active ||
                !["editing", "previewing"].includes(state.review.status)) {
                return;
            }
            if (event.button !== undefined && event.button !== 0) {
                return;
            }
            if (event.target.closest &&
                event.target.closest("[data-inset-source], [data-inset-detail], button, input, a")) {
                return;
            }
            event.preventDefault();
            state.inspection.pointerId = event.pointerId;
            state.inspection.pointerStartX = event.clientX;
            state.inspection.pointerStartY = event.clientY;
            state.inspection.translateStartX = state.inspection.translateX;
            state.inspection.translateStartY = state.inspection.translateY;
            inspectionViewport.setPointerCapture(event.pointerId);
            if (mapDesignerRoot) {
                mapDesignerRoot.classList.add("is-inspection-panning");
            }
        });
        inspectionViewport.addEventListener("pointermove", event => {
            if (!state.inspection.active ||
                state.inspection.pointerId !== event.pointerId ||
                !inspectionViewport.hasPointerCapture(event.pointerId)) {
                return;
            }
            event.preventDefault();
            const requestedX = state.inspection.translateStartX +
                (event.clientX - state.inspection.pointerStartX);
            const requestedY = state.inspection.translateStartY +
                (event.clientY - state.inspection.pointerStartY);
            const constrained = constrainInspectionTranslation(requestedX, requestedY, state.inspection.scale);
            state.inspection.translateX = constrained.x;
            state.inspection.translateY = constrained.y;
            applyInspectionTransform();
        });
        const finishPan = event => {
            if (state.inspection.pointerId !== event.pointerId) {
                return;
            }
            if (inspectionViewport.hasPointerCapture(event.pointerId)) {
                inspectionViewport.releasePointerCapture(event.pointerId);
            }
            finishInspectionPan();
        };
        inspectionViewport.addEventListener("pointerup", finishPan);
        inspectionViewport.addEventListener("pointercancel", finishPan);
    }
    //--------------------------------------------------
    // External map controls
    //--------------------------------------------------
    if (zoomInButton) {
        zoomInButton.addEventListener("click", () => {
            markCompositionAdjustedAfterSearch();
            map.zoomIn({
                duration: prefersReducedMotion() ? 0 : 250
            });
        });
    }
    if (zoomOutButton) {
        zoomOutButton.addEventListener("click", () => {
            markCompositionAdjustedAfterSearch();
            map.zoomOut({
                duration: prefersReducedMotion() ? 0 : 250
            });
        });
    }
    //--------------------------------------------------
    // Active button helper
    //--------------------------------------------------
    function getSelectionAccessibleLabel(button) {
        const configuredLabel = button.dataset.selectionLabel;
        if (configuredLabel) {
            return configuredLabel;
        }
        return button.textContent.trim();
    }
    function updateSelectionAccessibility(button, isActive) {
        const selectionState = isActive ? "selected" : "not selected";
        button.setAttribute("aria-label", `${getSelectionAccessibleLabel(button)}, ${selectionState}`);
    }
    function setActiveButton(buttons, activeButton) {
        buttons.forEach(button => {
            const isActive = button === activeButton;
            button.classList.toggle("is-active", isActive);
            button.setAttribute("aria-pressed", String(isActive));
            updateSelectionAccessibility(button, isActive);
        });
    }
    function initialiseSelectionAccessibility(buttons) {
        buttons.forEach(button => {
            updateSelectionAccessibility(button, button.getAttribute("aria-pressed") === "true");
        });
    }
    function announceConfigurationSelection(message) {
        if (!selectionStatus || !message) {
            return;
        }
        // Clear first so assistive technology reliably announces a selection
        // even if the same wording is encountered again later in the session.
        selectionStatus.textContent = "";
        window.requestAnimationFrame(() => {
            selectionStatus.textContent = message;
        });
    }
    //--------------------------------------------------
    // Product dimension helper
    //--------------------------------------------------
    function getDisplayDimensions(product) {
        if (state.orientation === "landscape") {
            return {
                widthCm: product.outerHeightCm,
                heightCm: product.outerWidthCm
            };
        }
        return {
            widthCm: product.outerWidthCm,
            heightCm: product.outerHeightCm
        };
    }
    //--------------------------------------------------
    // Frame-change composition preservation
    // Universal behaviour; originated in Mobile UX Stage 8a
    //--------------------------------------------------
    function getTargetMappedAreaProjectionMetrics() {
        const selectedProduct = PRODUCTS[state.size];
        const stageSizePx = canvas ? canvas.clientWidth : 0;
        if (!selectedProduct || !stageSizePx) {
            return null;
        }
        const oriented = getOrientedDimensions(selectedProduct);
        const referenceLongSidePx = stageSizePx * (REFERENCE_LONG_SIDE_PERCENT / 100);
        const pixelsPerCm = referenceLongSidePx / REFERENCE_OUTER_LONG_CM;
        const mappedWidthPx = oriented.mappedArtwork.widthCm * pixelsPerCm;
        const mappedHeightPx = oriented.mappedArtwork.heightCm * pixelsPerCm;
        const artworkWidthMm = oriented.mappedArtwork.widthCm * 10;
        const artworkHeightMm = oriented.mappedArtwork.heightCm * 10;
        if (!mappedWidthPx ||
            !mappedHeightPx ||
            !artworkWidthMm ||
            !artworkHeightMm) {
            return null;
        }
        return {
            leftInMapPx: (stageSizePx - mappedWidthPx) / 2,
            topInMapPx: (stageSizePx - mappedHeightPx) / 2,
            pxPerMmX: mappedWidthPx / artworkWidthMm,
            pxPerMmY: mappedHeightPx / artworkHeightMm
        };
    }
    function getInsetSourceGeographicCenterForPreservation() {
        if (!state.inset.enabled || !state.inset.sourceCenterMm) {
            return null;
        }
        const metrics = getInsetRenderMetrics();
        const geographic = metrics
            ? getSourceGeographicBounds(metrics)
            : state.inset.geographicBounds;
        // Geographic inset state is stored as:
        // geographicBounds.bounds4326.center.
        // Stage 8a originally looked for a top-level `center`, which meant
        // frame changes never captured a source geographic anchor.
        const center = geographic &&
            geographic.bounds4326 &&
            geographic.bounds4326.center
            ? geographic.bounds4326.center
            : geographic && geographic.center
                ? geographic.center
                : null;
        if (!center ||
            !Number.isFinite(center.lng) ||
            !Number.isFinite(center.lat)) {
            return null;
        }
        return {
            lng: center.lng,
            lat: center.lat
        };
    }
    function restoreInsetSourceCenterFromGeography(geographicCenter) {
        if (!state.inset.enabled ||
            !geographicCenter ||
            !Number.isFinite(geographicCenter.lng) ||
            !Number.isFinite(geographicCenter.lat)) {
            return false;
        }
        const targetMetrics = getTargetMappedAreaProjectionMetrics();
        if (!targetMetrics) {
            return false;
        }
        const projected = map.project([
            geographicCenter.lng,
            geographicCenter.lat
        ]);
        if (!projected ||
            !Number.isFinite(projected.x) ||
            !Number.isFinite(projected.y)) {
            return false;
        }
        const requestedCenter = {
            x: (projected.x - targetMetrics.leftInMapPx) / targetMetrics.pxPerMmX,
            y: (projected.y - targetMetrics.topInMapPx) / targetMetrics.pxPerMmY
        };
        state.inset.sourceCenterMm =
            constrainSourceCenterMm(requestedCenter);
        return true;
    }
    function getInsetDetailAttachmentForPreservation() {
        if (!state.inset.enabled || !state.inset.detailCenterMm) {
            return null;
        }
        const path = getInsetCenterPathSequence(state.inset.detailDiameterMm);
        if (!path || !path.perimeter) {
            return null;
        }
        const constrained = constrainDetailCenterToPerimeter(state.inset.detailCenterMm);
        const center = constrained.center;
        const attachment = constrained.attachment;
        if (!center || !attachment || !attachment.primaryEdge) {
            return null;
        }
        let corner = null;
        if (attachment.isCorner && attachment.edges.length === 2) {
            const edgeSet = new Set(attachment.edges);
            if (edgeSet.has("top") && edgeSet.has("left")) {
                corner = "top-left";
            }
            else if (edgeSet.has("top") && edgeSet.has("right")) {
                corner = "top-right";
            }
            else if (edgeSet.has("bottom") && edgeSet.has("right")) {
                corner = "bottom-right";
            }
            else if (edgeSet.has("bottom") && edgeSet.has("left")) {
                corner = "bottom-left";
            }
        }
        if (corner) {
            return {
                corner,
                edge: attachment.primaryEdge,
                ratio: null
            };
        }
        let ratio = 0.5;
        if (attachment.primaryEdge === "top" ||
            attachment.primaryEdge === "bottom") {
            ratio = path.horizontal
                ? (center.x - path.left) / path.horizontal
                : 0.5;
        }
        else {
            ratio = path.vertical
                ? (center.y - path.top) / path.vertical
                : 0.5;
        }
        return {
            corner: null,
            edge: attachment.primaryEdge,
            ratio: clampNumber(ratio, 0, 1)
        };
    }
    function getNearestValidDetailCenterOnPerimeter(requestedCenter) {
        const constrained = constrainDetailCenterToPerimeter(requestedCenter);
        const candidate = constrained.center;
        const sourceCenter = state.inset.sourceCenterMm;
        if (!candidate ||
            !sourceCenter ||
            !insetCentersOverlapMm(sourceCenter, candidate)) {
            return constrained;
        }
        const path = getInsetCenterPathSequence(state.inset.detailDiameterMm);
        if (!path || !path.perimeter) {
            return constrained;
        }
        const startDistance = detailCenterToPerimeterDistance(candidate, path);
        const forwardDistance = findDetailPerimeterExitAfterSourceCollision(startDistance, 1, path, sourceCenter);
        const backwardDistance = findDetailPerimeterExitAfterSourceCollision(startDistance, -1, path, sourceCenter);
        const choices = [];
        if (Number.isFinite(forwardDistance)) {
            choices.push({
                distance: forwardDistance,
                delta: Math.abs(getShortestInsetPerimeterDelta(startDistance, forwardDistance, path.perimeter))
            });
        }
        if (Number.isFinite(backwardDistance)) {
            choices.push({
                distance: backwardDistance,
                delta: Math.abs(getShortestInsetPerimeterDelta(startDistance, backwardDistance, path.perimeter))
            });
        }
        if (!choices.length) {
            return constrained;
        }
        choices.sort((a, b) => a.delta - b.delta);
        return constrainDetailCenterToPerimeter(perimeterDistanceToDetailCenter(choices[0].distance, path));
    }
    function restoreInsetDetailAttachment(preservedAttachment) {
        if (!state.inset.enabled ||
            !preservedAttachment) {
            return false;
        }
        const path = getInsetCenterPathSequence(state.inset.detailDiameterMm);
        if (!path || !path.perimeter) {
            return false;
        }
        let requestedCenter = null;
        if (preservedAttachment.corner) {
            const corners = {
                "top-left": { x: path.left, y: path.top },
                "top-right": { x: path.right, y: path.top },
                "bottom-right": { x: path.right, y: path.bottom },
                "bottom-left": { x: path.left, y: path.bottom }
            };
            requestedCenter = corners[preservedAttachment.corner] || null;
        }
        else {
            const ratio = clampNumber(Number.isFinite(preservedAttachment.ratio)
                ? preservedAttachment.ratio
                : 0.5, 0, 1);
            if (preservedAttachment.edge === "top") {
                requestedCenter = {
                    x: path.left + path.horizontal * ratio,
                    y: path.top
                };
            }
            else if (preservedAttachment.edge === "right") {
                requestedCenter = {
                    x: path.right,
                    y: path.top + path.vertical * ratio
                };
            }
            else if (preservedAttachment.edge === "bottom") {
                requestedCenter = {
                    x: path.left + path.horizontal * ratio,
                    y: path.bottom
                };
            }
            else if (preservedAttachment.edge === "left") {
                requestedCenter = {
                    x: path.left,
                    y: path.top + path.vertical * ratio
                };
            }
        }
        if (!requestedCenter) {
            return false;
        }
        const restored = getNearestValidDetailCenterOnPerimeter(requestedCenter);
        state.inset.detailCenterMm = restored.center;
        state.inset.attachment = restored.attachment;
        return true;
    }
    //--------------------------------------------------
    // Update visible physical product frame
    //--------------------------------------------------
    function updateProductFrame() {
        const selectedProduct = PRODUCTS[state.size];
        if (!selectedProduct) {
            return;
        }
        const stageSizePx = canvas.clientWidth;
        if (!stageSizePx) {
            return;
        }
        canvas.style.setProperty("--stage-size", `${stageSizePx}px`);
        const referenceLongSidePx = stageSizePx * (REFERENCE_LONG_SIDE_PERCENT / 100);
        const pixelsPerCm = referenceLongSidePx / REFERENCE_OUTER_LONG_CM;
        const dimensions = getDisplayDimensions(selectedProduct);
        canvas.style.setProperty("--product-width", `${dimensions.widthCm * pixelsPerCm}px`);
        canvas.style.setProperty("--product-height", `${dimensions.heightCm * pixelsPerCm}px`);
        canvas.style.setProperty("--black-frame", `${selectedProduct.blackFrameCm * pixelsPerCm}px`);
        canvas.style.setProperty("--basswood-reveal", `${selectedProduct.basswoodRevealCm * pixelsPerCm}px`);
        canvas.dataset.orientation = state.orientation;
        canvas.dataset.size = state.size;
        updateInsetGeometry();
    }
    //--------------------------------------------------
    // Customer-defined detail inset
    // Release 2.0.0 Unit 1
    //--------------------------------------------------
    function clampNumber(value, minimum, maximum) {
        return Math.min(maximum, Math.max(minimum, value));
    }
    function getInsetArtworkDimensionsMm() {
        const product = PRODUCTS[state.size];
        if (!product) {
            return null;
        }
        const oriented = getOrientedDimensions(product);
        return {
            widthMm: oriented.mappedArtwork.widthCm * 10,
            heightMm: oriented.mappedArtwork.heightCm * 10
        };
    }
    function getInsetRenderMetrics() {
        if (!mappedArea || !canvas) {
            return null;
        }
        const artwork = getInsetArtworkDimensionsMm();
        const mappedRect = mappedArea.getBoundingClientRect();
        const canvasRect = canvas.getBoundingClientRect();
        const visualScale = getInspectionVisualScale();
        if (!artwork ||
            !mappedRect.width ||
            !mappedRect.height ||
            !canvasRect.width ||
            !canvasRect.height ||
            !visualScale) {
            return null;
        }
        const pxPerMmX = mappedRect.width / visualScale / artwork.widthMm;
        const pxPerMmY = mappedRect.height / visualScale / artwork.heightMm;
        return {
            artwork,
            mappedRect,
            canvasRect,
            visualScale,
            leftPx: (mappedRect.left - canvasRect.left) / visualScale,
            topPx: (mappedRect.top - canvasRect.top) / visualScale,
            pxPerMmX,
            pxPerMmY,
            pxPerMm: (pxPerMmX + pxPerMmY) / 2,
            clientPxPerMmX: mappedRect.width / artwork.widthMm,
            clientPxPerMmY: mappedRect.height / artwork.heightMm
        };
    }
    function initialiseInsetPositions() {
        const artwork = getInsetArtworkDimensionsMm();
        if (!artwork) {
            return;
        }
        if (!state.inset.sourceCenterMm) {
            state.inset.sourceCenterMm = {
                x: artwork.widthMm * 0.5,
                y: artwork.heightMm * 0.5
            };
        }
        if (!state.inset.detailCenterMm) {
            const outerRadius = state.inset.detailDiameterMm / 2 +
                state.inset.detailFrameMm;
            const inset = outerRadius - state.inset.structuralOverlapMm;
            state.inset.detailCenterMm = {
                x: clampNumber(artwork.widthMm * 0.72, inset, artwork.widthMm - inset),
                y: inset
            };
        }
    }
    function constrainSourceCenterMmForDiameter(center, diameterMm) {
        const artwork = getInsetArtworkDimensionsMm();
        if (!artwork ||
            !center ||
            !Number.isFinite(diameterMm)) {
            return center;
        }
        const radius = diameterMm / 2 +
            state.inset.sourceFrameMm;
        return {
            x: clampNumber(center.x, radius, artwork.widthMm - radius),
            y: clampNumber(center.y, radius, artwork.heightMm - radius)
        };
    }
    function constrainSourceCenterMm(center) {
        return constrainSourceCenterMmForDiameter(center, state.inset.sourceDiameterMm);
    }
    function closestPointOnSegment(point, start, end) {
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const lengthSquared = dx * dx + dy * dy;
        if (!lengthSquared) {
            return { ...start };
        }
        const t = clampNumber(((point.x - start.x) * dx +
            (point.y - start.y) * dy) / lengthSquared, 0, 1);
        return {
            x: start.x + dx * t,
            y: start.y + dy * t
        };
    }
    function buildInsetAttachment(center, bounds) {
        const epsilon = 0.0001;
        const edges = [];
        if (Math.abs(center.y - bounds.top) <= epsilon) {
            edges.push("top");
        }
        if (Math.abs(center.x - bounds.right) <= epsilon) {
            edges.push("right");
        }
        if (Math.abs(center.y - bounds.bottom) <= epsilon) {
            edges.push("bottom");
        }
        if (Math.abs(center.x - bounds.left) <= epsilon) {
            edges.push("left");
        }
        const primaryEdge = edges[0] || null;
        let positionAlongEdgeMm = null;
        if (primaryEdge === "top" || primaryEdge === "bottom") {
            positionAlongEdgeMm = center.x;
        }
        else if (primaryEdge === "left" || primaryEdge === "right") {
            positionAlongEdgeMm = center.y;
        }
        return {
            edges,
            primaryEdge,
            isCorner: edges.length === 2,
            positionAlongEdgeMm: Number.isFinite(positionAlongEdgeMm)
                ? Number(positionAlongEdgeMm.toFixed(3))
                : null
        };
    }
    function constrainDetailCenterToPerimeter(center, detailDiameterMm = state.inset.detailDiameterMm) {
        const artwork = getInsetArtworkDimensionsMm();
        if (!artwork || !center) {
            return {
                center,
                attachment: null
            };
        }
        const outerRadius = detailDiameterMm / 2 +
            state.inset.detailFrameMm;
        const inset = outerRadius - state.inset.structuralOverlapMm;
        const bounds = {
            left: inset,
            top: inset,
            right: artwork.widthMm - inset,
            bottom: artwork.heightMm - inset
        };
        const segments = [
            {
                edge: "top",
                start: { x: bounds.left, y: bounds.top },
                end: { x: bounds.right, y: bounds.top }
            },
            {
                edge: "right",
                start: { x: bounds.right, y: bounds.top },
                end: { x: bounds.right, y: bounds.bottom }
            },
            {
                edge: "bottom",
                start: { x: bounds.right, y: bounds.bottom },
                end: { x: bounds.left, y: bounds.bottom }
            },
            {
                edge: "left",
                start: { x: bounds.left, y: bounds.bottom },
                end: { x: bounds.left, y: bounds.top }
            }
        ];
        let best = null;
        segments.forEach(segment => {
            const candidate = closestPointOnSegment(center, segment.start, segment.end);
            const dx = candidate.x - center.x;
            const dy = candidate.y - center.y;
            const distanceSquared = dx * dx + dy * dy;
            if (!best || distanceSquared < best.distanceSquared) {
                best = {
                    edge: segment.edge,
                    center: candidate,
                    distanceSquared
                };
            }
        });
        const constrained = best
            ? best.center
            : { x: bounds.left, y: bounds.top };
        return {
            center: constrained,
            attachment: buildInsetAttachment(constrained, bounds),
            centerPathBoundsMm: bounds
        };
    }
    function getInsetMinimumCenterSeparationMm(sourceDiameterMm = state.inset.sourceDiameterMm, detailDiameterMm = state.inset.detailDiameterMm) {
        return (sourceDiameterMm / 2 +
            state.inset.sourceFrameMm +
            detailDiameterMm / 2 +
            state.inset.detailFrameMm);
    }
    function getInsetCenterSeparationMm(sourceCenter, detailCenter) {
        if (!sourceCenter || !detailCenter) {
            return Infinity;
        }
        return Math.hypot(detailCenter.x - sourceCenter.x, detailCenter.y - sourceCenter.y);
    }
    function insetCentersOverlapMm(sourceCenter, detailCenter) {
        const minimumSeparation = getInsetMinimumCenterSeparationMm();
        return (getInsetCenterSeparationMm(sourceCenter, detailCenter) <
            minimumSeparation - INSET_COLLISION_EPSILON_MM);
    }
    function isInsetDiameterCandidateValid(target, diameterMm) {
        if (!state.inset.enabled ||
            !Number.isFinite(diameterMm) ||
            !state.inset.sourceCenterMm ||
            !state.inset.detailCenterMm) {
            return true;
        }
        let sourceDiameterMm = state.inset.sourceDiameterMm;
        let detailDiameterMm = state.inset.detailDiameterMm;
        let sourceCenter = state.inset.sourceCenterMm;
        let detailCenter = state.inset.detailCenterMm;
        if (target === "source") {
            sourceDiameterMm = diameterMm;
            sourceCenter =
                constrainSourceCenterMmForDiameter(sourceCenter, sourceDiameterMm);
        }
        else if (target === "detail") {
            detailDiameterMm = diameterMm;
            detailCenter =
                constrainDetailCenterToPerimeter(detailCenter, detailDiameterMm).center;
        }
        else {
            return false;
        }
        const minimumSeparation = getInsetMinimumCenterSeparationMm(sourceDiameterMm, detailDiameterMm);
        return (getInsetCenterSeparationMm(sourceCenter, detailCenter) >=
            minimumSeparation - INSET_COLLISION_EPSILON_MM);
    }
    function constrainInsetDiameterIncrease(target, requestedDiameterMm) {
        const currentDiameterMm = target === "source"
            ? state.inset.sourceDiameterMm
            : target === "detail"
                ? state.inset.detailDiameterMm
                : requestedDiameterMm;
        if (!Number.isFinite(requestedDiameterMm) ||
            !Number.isFinite(currentDiameterMm) ||
            requestedDiameterMm <= currentDiameterMm) {
            return requestedDiameterMm;
        }
        let acceptedDiameterMm = currentDiameterMm;
        // Diameter controls use 1 mm steps. Walk upward one production step at a
        // time and stop at the first value that would turn circle contact into
        // overlap. This makes resizing feel physically constrained in the same way
        // as Stage 7 movement collisions.
        for (let candidate = Math.ceil(currentDiameterMm + 1); candidate <= requestedDiameterMm; candidate += 1) {
            if (!isInsetDiameterCandidateValid(target, candidate)) {
                break;
            }
            acceptedDiameterMm = candidate;
        }
        return acceptedDiameterMm;
    }
    function interpolateInsetPoint(start, end, amount) {
        return {
            x: start.x + (end.x - start.x) * amount,
            y: start.y + (end.y - start.y) * amount
        };
    }
    function getClosestValidSourceCenterOutsideDetail(requestedCenter, fallbackCenter) {
        const candidate = constrainSourceCenterMm(requestedCenter);
        const detailCenter = state.inset.detailCenterMm;
        if (!candidate || !detailCenter) {
            return candidate;
        }
        if (!insetCentersOverlapMm(candidate, detailCenter)) {
            return candidate;
        }
        const artwork = getInsetArtworkDimensionsMm();
        if (!artwork) {
            return fallbackCenter || candidate;
        }
        const sourceRadius = state.inset.sourceDiameterMm / 2 +
            state.inset.sourceFrameMm;
        const minimumSeparation = getInsetMinimumCenterSeparationMm();
        const bounds = {
            left: sourceRadius,
            top: sourceRadius,
            right: artwork.widthMm - sourceRadius,
            bottom: artwork.heightMm - sourceRadius
        };
        const possible = [];
        const addPossible = point => {
            if (!point) {
                return;
            }
            if (point.x < bounds.left - INSET_COLLISION_EPSILON_MM ||
                point.x > bounds.right + INSET_COLLISION_EPSILON_MM ||
                point.y < bounds.top - INSET_COLLISION_EPSILON_MM ||
                point.y > bounds.bottom + INSET_COLLISION_EPSILON_MM ||
                insetCentersOverlapMm(point, detailCenter)) {
                return;
            }
            possible.push({
                x: clampNumber(point.x, bounds.left, bounds.right),
                y: clampNumber(point.y, bounds.top, bounds.bottom)
            });
        };
        // First try the natural radial projection onto the detail-circle exclusion
        // boundary. As the pointer moves sideways while still pushing into the
        // detail circle, this point travels around the circumference and produces
        // the desired gliding behaviour.
        let radialX = candidate.x - detailCenter.x;
        let radialY = candidate.y - detailCenter.y;
        let radialLength = Math.hypot(radialX, radialY);
        if (radialLength < INSET_COLLISION_EPSILON_MM && fallbackCenter) {
            radialX = fallbackCenter.x - detailCenter.x;
            radialY = fallbackCenter.y - detailCenter.y;
            radialLength = Math.hypot(radialX, radialY);
        }
        if (radialLength >= INSET_COLLISION_EPSILON_MM) {
            addPossible({
                x: detailCenter.x + radialX / radialLength * minimumSeparation,
                y: detailCenter.y + radialY / radialLength * minimumSeparation
            });
        }
        const verticalIntersections = x => {
            const dx = x - detailCenter.x;
            const remainingSquared = minimumSeparation * minimumSeparation - dx * dx;
            if (remainingSquared < 0) {
                return;
            }
            const dy = Math.sqrt(Math.max(0, remainingSquared));
            addPossible({ x, y: detailCenter.y - dy });
            addPossible({ x, y: detailCenter.y + dy });
        };
        const horizontalIntersections = y => {
            const dy = y - detailCenter.y;
            const remainingSquared = minimumSeparation * minimumSeparation - dy * dy;
            if (remainingSquared < 0) {
                return;
            }
            const dx = Math.sqrt(Math.max(0, remainingSquared));
            addPossible({ x: detailCenter.x - dx, y });
            addPossible({ x: detailCenter.x + dx, y });
        };
        // Where the exclusion circle meets the legal source-centre rectangle, the
        // source can continue sliding along the frame-side limit instead of being
        // trapped at the first contact point.
        verticalIntersections(bounds.left);
        verticalIntersections(bounds.right);
        horizontalIntersections(bounds.top);
        horizontalIntersections(bounds.bottom);
        const edgeSegments = [
            [
                { x: bounds.left, y: bounds.top },
                { x: bounds.right, y: bounds.top }
            ],
            [
                { x: bounds.right, y: bounds.top },
                { x: bounds.right, y: bounds.bottom }
            ],
            [
                { x: bounds.right, y: bounds.bottom },
                { x: bounds.left, y: bounds.bottom }
            ],
            [
                { x: bounds.left, y: bounds.bottom },
                { x: bounds.left, y: bounds.top }
            ]
        ];
        edgeSegments.forEach(([start, end]) => {
            addPossible(closestPointOnSegment(candidate, start, end));
        });
        addPossible({ x: bounds.left, y: bounds.top });
        addPossible({ x: bounds.right, y: bounds.top });
        addPossible({ x: bounds.right, y: bounds.bottom });
        addPossible({ x: bounds.left, y: bounds.bottom });
        addPossible(fallbackCenter);
        if (!possible.length) {
            return fallbackCenter || candidate;
        }
        possible.sort((a, b) => {
            const distanceA = Math.hypot(a.x - candidate.x, a.y - candidate.y);
            const distanceB = Math.hypot(b.x - candidate.x, b.y - candidate.y);
            return distanceA - distanceB;
        });
        return possible[0];
    }
    function constrainSourceMovementAgainstDetail(requestedCenter, currentCenter) {
        const candidate = constrainSourceCenterMm(requestedCenter);
        const detailCenter = state.inset.detailCenterMm;
        if (!candidate || !detailCenter || !currentCenter) {
            return candidate;
        }
        const current = constrainSourceCenterMm(currentCenter);
        // If an older state is already invalid, allow movement that escapes the
        // collision rather than trapping the customer inside it.
        if (insetCentersOverlapMm(current, detailCenter)) {
            if (!insetCentersOverlapMm(candidate, detailCenter)) {
                return candidate;
            }
            if (getInsetCenterSeparationMm(candidate, detailCenter) >
                getInsetCenterSeparationMm(current, detailCenter)) {
                return candidate;
            }
            return current;
        }
        const travelMm = Math.hypot(candidate.x - current.x, candidate.y - current.y);
        if (!travelMm) {
            return current;
        }
        const samples = Math.max(1, Math.ceil(travelMm / INSET_COLLISION_PATH_SAMPLE_MM));
        let previousAmount = 0;
        for (let index = 1; index <= samples; index += 1) {
            const amount = index / samples;
            const sample = interpolateInsetPoint(current, candidate, amount);
            if (!insetCentersOverlapMm(sample, detailCenter)) {
                previousAmount = amount;
                continue;
            }
            // Find the first exact contact point along the attempted movement.
            let low = previousAmount;
            let high = amount;
            for (let iteration = 0; iteration < INSET_COLLISION_BINARY_ITERATIONS; iteration += 1) {
                const middle = (low + high) / 2;
                const middlePoint = interpolateInsetPoint(current, candidate, middle);
                if (insetCentersOverlapMm(middlePoint, detailCenter)) {
                    high = middle;
                }
                else {
                    low = middle;
                }
            }
            const contact = interpolateInsetPoint(current, candidate, low);
            const normalX = contact.x - detailCenter.x;
            const normalY = contact.y - detailCenter.y;
            const normalLength = Math.hypot(normalX, normalY);
            if (normalLength < INSET_COLLISION_EPSILON_MM) {
                return contact;
            }
            const unitNormalX = normalX / normalLength;
            const unitNormalY = normalY / normalLength;
            const remainderX = candidate.x - contact.x;
            const remainderY = candidate.y - contact.y;
            const inwardAmount = remainderX * unitNormalX + remainderY * unitNormalY;
            // Remove only the component that pushes farther into the detail circle.
            // The tangential component remains, so continued finger/nudge movement
            // naturally carries the source around the detail-circle perimeter.
            const slideRequested = inwardAmount < 0
                ? {
                    x: candidate.x - inwardAmount * unitNormalX,
                    y: candidate.y - inwardAmount * unitNormalY
                }
                : candidate;
            return getClosestValidSourceCenterOutsideDetail(slideRequested, contact);
        }
        return candidate;
    }
    function getShortestInsetPerimeterDelta(fromDistance, toDistance, perimeter) {
        let delta = toDistance - fromDistance;
        const half = perimeter / 2;
        if (delta > half) {
            delta -= perimeter;
        }
        else if (delta < -half) {
            delta += perimeter;
        }
        return delta;
    }
    function findDetailPerimeterExitAfterSourceCollision(startDistance, direction, path, sourceCenter) {
        if (!path || !path.perimeter || !sourceCenter || !direction) {
            return null;
        }
        const step = INSET_COLLISION_PATH_SAMPLE_MM;
        const maxSteps = Math.ceil(path.perimeter / step) + 2;
        let previousDistance = startDistance;
        let previousCenter = perimeterDistanceToDetailCenter(previousDistance, path);
        // Ensure the search begins inside the blocked interval. If numerical
        // rounding placed us exactly on its entry boundary, move a tiny amount in
        // the requested direction before looking for the far-side exit.
        if (!insetCentersOverlapMm(sourceCenter, previousCenter)) {
            previousDistance +=
                Math.sign(direction) * INSET_COLLISION_EPSILON_MM * 4;
            previousCenter =
                perimeterDistanceToDetailCenter(previousDistance, path);
        }
        for (let index = 1; index <= maxSteps; index += 1) {
            const nextDistance = startDistance + Math.sign(direction) * step * index;
            const nextCenter = perimeterDistanceToDetailCenter(nextDistance, path);
            if (insetCentersOverlapMm(sourceCenter, nextCenter)) {
                previousDistance = nextDistance;
                previousCenter = nextCenter;
                continue;
            }
            let blockedDistance = previousDistance;
            let clearDistance = nextDistance;
            for (let iteration = 0; iteration < INSET_COLLISION_BINARY_ITERATIONS; iteration += 1) {
                const middleDistance = (blockedDistance + clearDistance) / 2;
                const middleCenter = perimeterDistanceToDetailCenter(middleDistance, path);
                if (insetCentersOverlapMm(sourceCenter, middleCenter)) {
                    blockedDistance = middleDistance;
                }
                else {
                    clearDistance = middleDistance;
                }
            }
            return clearDistance;
        }
        return null;
    }
    function constrainDetailMovementAgainstSource(requestedCenter, currentCenter) {
        const candidateConstraint = constrainDetailCenterToPerimeter(requestedCenter);
        const candidate = candidateConstraint.center;
        const sourceCenter = state.inset.sourceCenterMm;
        if (!candidate || !sourceCenter || !currentCenter) {
            return candidateConstraint;
        }
        const currentConstraint = constrainDetailCenterToPerimeter(currentCenter);
        const current = currentConstraint.center;
        // As above, allow an already-invalid legacy position to move outward and
        // escape rather than locking it in place.
        if (insetCentersOverlapMm(sourceCenter, current)) {
            if (!insetCentersOverlapMm(sourceCenter, candidate)) {
                return candidateConstraint;
            }
            if (getInsetCenterSeparationMm(sourceCenter, candidate) >
                getInsetCenterSeparationMm(sourceCenter, current)) {
                return candidateConstraint;
            }
            return currentConstraint;
        }
        const path = getInsetCenterPathSequence(state.inset.detailDiameterMm);
        if (!path || !path.perimeter) {
            return candidateConstraint;
        }
        const currentDistance = detailCenterToPerimeterDistance(current, path);
        const candidateDistance = detailCenterToPerimeterDistance(candidate, path);
        const delta = getShortestInsetPerimeterDelta(currentDistance, candidateDistance, path.perimeter);
        if (!delta) {
            return currentConstraint;
        }
        const direction = Math.sign(delta);
        const samples = Math.max(1, Math.ceil(Math.abs(delta) / INSET_COLLISION_PATH_SAMPLE_MM));
        let previousAmount = 0;
        for (let index = 1; index <= samples; index += 1) {
            const amount = index / samples;
            const sampleDistance = currentDistance + delta * amount;
            const sampleCenter = perimeterDistanceToDetailCenter(sampleDistance, path);
            if (!insetCentersOverlapMm(sourceCenter, sampleCenter)) {
                previousAmount = amount;
                continue;
            }
            // If the requested destination itself is already clear, the customer's
            // gesture has crossed the source-circle blocked interval. Honour that
            // intent and allow the detail circle to skip directly to the clear
            // destination on the far side.
            if (!insetCentersOverlapMm(sourceCenter, candidate)) {
                return candidateConstraint;
            }
            // Otherwise the requested point lies inside the blocked interval (the
            // usual 1 mm nudge/hold case). Jump to the first clear point on the far
            // side in the same direction of travel.
            const collisionDistance = currentDistance + delta * Math.max(previousAmount, amount);
            const exitDistance = findDetailPerimeterExitAfterSourceCollision(collisionDistance, direction, path, sourceCenter);
            if (!Number.isFinite(exitDistance)) {
                return currentConstraint;
            }
            const exitCenter = perimeterDistanceToDetailCenter(exitDistance, path);
            return constrainDetailCenterToPerimeter(exitCenter);
        }
        return candidateConstraint;
    }
    function artworkMmToCanvasPx(point, metrics) {
        return {
            x: metrics.leftPx + point.x * metrics.pxPerMmX,
            y: metrics.topPx + point.y * metrics.pxPerMmY
        };
    }
    function canvasClientPointToArtworkMm(clientX, clientY, metrics) {
        const pxPerMmX = metrics.clientPxPerMmX || metrics.pxPerMmX;
        const pxPerMmY = metrics.clientPxPerMmY || metrics.pxPerMmY;
        return {
            x: (clientX - metrics.mappedRect.left) / pxPerMmX,
            y: (clientY - metrics.mappedRect.top) / pxPerMmY
        };
    }
    function getSourceGeographicBounds(metrics) {
        if (!state.inset.enabled || !state.inset.sourceCenterMm) {
            return null;
        }
        const sourceCenterPx = artworkMmToCanvasPx(state.inset.sourceCenterMm, metrics);
        const canvasRect = metrics.canvasRect;
        const mapRect = mapContainer.getBoundingClientRect();
        const visualScale = metrics.visualScale || 1;
        // MapLibre unproject() expects coordinates in the map's unscaled internal
        // pixel space. Normalise the screen-only inspection transform away before
        // deriving production geography.
        const centerInMap = {
            x: (canvasRect.left + sourceCenterPx.x * visualScale - mapRect.left) / visualScale,
            y: (canvasRect.top + sourceCenterPx.y * visualScale - mapRect.top) / visualScale
        };
        const radiusPx = state.inset.sourceDiameterMm / 2 * metrics.pxPerMm;
        const westPoint = map.unproject([
            centerInMap.x - radiusPx,
            centerInMap.y
        ]);
        const eastPoint = map.unproject([
            centerInMap.x + radiusPx,
            centerInMap.y
        ]);
        const northPoint = map.unproject([
            centerInMap.x,
            centerInMap.y - radiusPx
        ]);
        const southPoint = map.unproject([
            centerInMap.x,
            centerInMap.y + radiusPx
        ]);
        const centerPoint = map.unproject([
            centerInMap.x,
            centerInMap.y
        ]);
        const bounds = {
            west: roundCoordinate(westPoint.lng),
            east: roundCoordinate(eastPoint.lng),
            north: roundCoordinate(northPoint.lat),
            south: roundCoordinate(southPoint.lat),
            center: {
                lng: roundCoordinate(centerPoint.lng),
                lat: roundCoordinate(centerPoint.lat)
            }
        };
        const northWest = {
            lng: bounds.west,
            lat: bounds.north
        };
        const southEast = {
            lng: bounds.east,
            lat: bounds.south
        };
        const projectedNorthWest = projectWgs84ToWebMercator(northWest);
        const projectedSouthEast = projectWgs84ToWebMercator(southEast);
        let extent3857 = null;
        if (projectedNorthWest && projectedSouthEast) {
            extent3857 = {
                crs: "EPSG:3857",
                coordinateOrder: "xmin, xmax, ymin, ymax",
                units: "metres",
                xmin: roundProjectedMeters(Math.min(projectedNorthWest.x, projectedSouthEast.x)),
                xmax: roundProjectedMeters(Math.max(projectedNorthWest.x, projectedSouthEast.x)),
                ymin: roundProjectedMeters(Math.min(projectedNorthWest.y, projectedSouthEast.y)),
                ymax: roundProjectedMeters(Math.max(projectedNorthWest.y, projectedSouthEast.y))
            };
            extent3857.qgisInput =
                formatQgisExtent(extent3857, 3);
        }
        return {
            bounds4326: bounds,
            extent3857
        };
    }
    function getExternalTangentPoints(centerA, radiusA, centerB, radiusB, side) {
        const dx = centerB.x - centerA.x;
        const dy = centerB.y - centerA.y;
        const distance = Math.hypot(dx, dy);
        if (!distance || distance <= Math.abs(radiusA - radiusB)) {
            return null;
        }
        const vx = dx / distance;
        const vy = dy / distance;
        const c = (radiusA - radiusB) / distance;
        const hSquared = 1 - c * c;
        if (hSquared < 0) {
            return null;
        }
        const h = Math.sqrt(hSquared);
        const nx = c * vx - side * h * vy;
        const ny = c * vy + side * h * vx;
        return {
            a: {
                x: centerA.x + radiusA * nx,
                y: centerA.y + radiusA * ny
            },
            b: {
                x: centerB.x + radiusB * nx,
                y: centerB.y + radiusB * ny
            }
        };
    }
    function formatSvgPoints(points) {
        return points
            .map(point => `${point.x.toFixed(2)},${point.y.toFixed(2)}`)
            .join(" ");
    }
    function updateInsetConnectorBands(sourceCenterPx, detailCenterPx, metrics) {
        if (!insetConnectorSvg || insetConnectorBands.length < 2) {
            return;
        }
        // Connector points are calculated in the canvas's unscaled internal
        // pixel coordinate system. getBoundingClientRect() includes the mobile
        // inspection transform, so using its visual dimensions here would change
        // the SVG coordinate system as soon as an inset drag triggered a redraw.
        // Keep the connector viewBox in the same unscaled coordinate space as the
        // source/detail circle centres.
        const connectorVisualScale = metrics.visualScale || 1;
        const connectorCanvasWidth = metrics.canvasRect.width / connectorVisualScale;
        const connectorCanvasHeight = metrics.canvasRect.height / connectorVisualScale;
        insetConnectorSvg.setAttribute("viewBox", `0 0 ${connectorCanvasWidth} ${connectorCanvasHeight}`);
        const sourceInnerRadius = state.inset.sourceDiameterMm / 2 * metrics.pxPerMm;
        const sourceOuterRadius = (state.inset.sourceDiameterMm / 2 +
            state.inset.sourceFrameMm) * metrics.pxPerMm;
        const detailInnerRadius = state.inset.detailDiameterMm / 2 * metrics.pxPerMm;
        const detailOuterRadius = (state.inset.detailDiameterMm / 2 +
            state.inset.detailFrameMm) * metrics.pxPerMm;
        const separation = Math.hypot(detailCenterPx.x - sourceCenterPx.x, detailCenterPx.y - sourceCenterPx.y);
        const overlap = insetCentersOverlapMm(state.inset.sourceCenterMm, state.inset.detailCenterMm);
        state.inset.geometryValid = !overlap;
        state.inset.validationMessage = overlap
            ? "Move the area of interest or detail inset so the two framed circles do not overlap."
            : "";
        if (overlap) {
            insetConnectorBands.forEach(band => {
                band.setAttribute("points", "");
            });
            return;
        }
        [-1, 1].forEach((side, index) => {
            const outer = getExternalTangentPoints(sourceCenterPx, sourceOuterRadius, detailCenterPx, detailOuterRadius, side);
            const inner = getExternalTangentPoints(sourceCenterPx, sourceInnerRadius, detailCenterPx, detailInnerRadius, side);
            if (!outer || !inner) {
                insetConnectorBands[index].setAttribute("points", "");
                return;
            }
            insetConnectorBands[index].setAttribute("points", formatSvgPoints([
                outer.a,
                outer.b,
                inner.b,
                inner.a
            ]));
        });
    }
    function setInsetStatus(message, warning = false) {
        if (!insetStatus) {
            return;
        }
        insetStatus.textContent = message || "";
        insetStatus.classList.toggle("is-warning", Boolean(warning));
    }
    function updateInsetDetailMap() {
        if (!state.inset.enabled) {
            return;
        }
        const detailMap = ensureInsetDetailMap();
        const geographic = state.inset.geographicBounds;
        if (!detailMap ||
            !geographic ||
            !geographic.bounds4326) {
            return;
        }
        const bounds = geographic.bounds4326;
        const applyBounds = () => {
            detailMap.resize();
            detailMap.fitBounds([
                [bounds.west, bounds.south],
                [bounds.east, bounds.north]
            ], {
                padding: 0,
                duration: 0,
                linear: true
            });
        };
        if (detailMap.loaded()) {
            applyBounds();
        }
        else {
            detailMap.once("load", applyBounds);
        }
    }
    function updateInsetGeometry() {
        if (!insetLayer) {
            return;
        }
        insetLayer.hidden = !state.inset.enabled;
        if (insetControls) {
            insetControls.hidden = !state.inset.enabled;
        }
        if (!state.inset.enabled) {
            setInsetStatus("");
            return;
        }
        initialiseInsetPositions();
        const metrics = getInsetRenderMetrics();
        if (!metrics) {
            return;
        }
        state.inset.sourceCenterMm =
            constrainSourceCenterMm(state.inset.sourceCenterMm);
        const detailConstraint = constrainDetailCenterToPerimeter(state.inset.detailCenterMm);
        state.inset.detailCenterMm =
            detailConstraint.center;
        state.inset.attachment =
            detailConstraint.attachment;
        const sourceCenterPx = artworkMmToCanvasPx(state.inset.sourceCenterMm, metrics);
        const detailCenterPx = artworkMmToCanvasPx(state.inset.detailCenterMm, metrics);
        const sourceFramePx = Math.max(1, state.inset.sourceFrameMm * metrics.pxPerMm);
        const detailFramePx = Math.max(1, state.inset.detailFrameMm * metrics.pxPerMm);
        const sourceOuterDiameterPx = (state.inset.sourceDiameterMm +
            state.inset.sourceFrameMm * 2) * metrics.pxPerMm;
        const detailOuterDiameterPx = (state.inset.detailDiameterMm +
            state.inset.detailFrameMm * 2) * metrics.pxPerMm;
        const interactionVisualScale = metrics.visualScale || 1;
        const minimumInternalTouchDiameterPx = INSET_TOUCH_TARGET_MIN_PX / interactionVisualScale;
        // Stage 5b: keep a fixed amount of real screen space outside the source
        // circle perimeter, rather than measuring the grab target from its centre.
        // Dividing by visualScale means the outward allowance remains 44px on
        // screen at Full view and at every inspection magnification.
        const sourceInternalTouchMarginPx = INSET_SOURCE_TOUCH_MARGIN_PX / interactionVisualScale;
        const sourceTouchDiameterPx = Math.max(sourceOuterDiameterPx + sourceInternalTouchMarginPx * 2, minimumInternalTouchDiameterPx);
        if (insetSourceHandle) {
            insetSourceHandle.style.left = `${sourceCenterPx.x}px`;
            insetSourceHandle.style.top = `${sourceCenterPx.y}px`;
            insetSourceHandle.style.width = `${sourceOuterDiameterPx}px`;
            insetSourceHandle.style.height = `${sourceOuterDiameterPx}px`;
            insetSourceHandle.style.setProperty("--inset-source-frame-px", `${sourceFramePx}px`);
            insetSourceHandle.style.setProperty("--inset-touch-target-px", `${sourceTouchDiameterPx}px`);
        }
        if (insetDetailHandle) {
            insetDetailHandle.style.left = `${detailCenterPx.x}px`;
            insetDetailHandle.style.top = `${detailCenterPx.y}px`;
            insetDetailHandle.style.width = `${detailOuterDiameterPx}px`;
            insetDetailHandle.style.height = `${detailOuterDiameterPx}px`;
            insetDetailHandle.style.setProperty("--inset-detail-frame-px", `${detailFramePx}px`);
            insetDetailHandle.style.setProperty("--inset-touch-target-px", `${Math.max(detailOuterDiameterPx, minimumInternalTouchDiameterPx)}px`);
        }
        updateInsetConnectorBands(sourceCenterPx, detailCenterPx, metrics);
        state.inset.geographicBounds =
            getSourceGeographicBounds(metrics);
        state.inset.qgisExtent3857 =
            state.inset.geographicBounds
                ? state.inset.geographicBounds.extent3857
                : null;
        insetLayer.classList.toggle("is-invalid", !state.inset.geometryValid);
        if (!state.inset.geometryValid) {
            setInsetStatus(state.inset.validationMessage, true);
        }
        else {
            const edgeText = state.inset.attachment &&
                state.inset.attachment.edges.length
                ? state.inset.attachment.edges.join(" + ")
                : "frame";
            setInsetStatus(`Inset attached to ${edgeText}. The enlarged circle shows the selected source area.`);
        }
        updateInsetDetailMap();
        // Stage 7c: circle movement changes the amount of room available for
        // diameter growth, so keep the + buttons synchronized with the current
        // collision-limited maximum after every geometry update.
        updateInsetDiameterStepButtons();
    }
    function buildInsetPayload() {
        if (!state.inset.enabled) {
            return {
                enabled: false,
                schemaVersion: "2.0-unit-2"
            };
        }
        const sourceOuterDiameterMm = state.inset.sourceDiameterMm +
            state.inset.sourceFrameMm * 2;
        const detailOuterDiameterMm = state.inset.detailDiameterMm +
            state.inset.detailFrameMm * 2;
        return {
            enabled: true,
            schemaVersion: "2.0-unit-2",
            sourceCircle: {
                mappedDiameterMm: state.inset.sourceDiameterMm,
                frameThicknessMm: state.inset.sourceFrameMm,
                outerDiameterMm: sourceOuterDiameterMm,
                centerArtworkMm: state.inset.sourceCenterMm
                    ? {
                        x: Number(state.inset.sourceCenterMm.x.toFixed(3)),
                        y: Number(state.inset.sourceCenterMm.y.toFixed(3))
                    }
                    : null,
                geographicSelection: state.inset.geographicBounds
                    ? clonePlainObject(state.inset.geographicBounds)
                    : null
            },
            detailCircle: {
                mappedDiameterMm: state.inset.detailDiameterMm,
                frameThicknessMm: state.inset.detailFrameMm,
                outerDiameterMm: detailOuterDiameterMm,
                structuralOverlapMm: state.inset.structuralOverlapMm,
                centerArtworkMm: state.inset.detailCenterMm
                    ? {
                        x: Number(state.inset.detailCenterMm.x.toFixed(3)),
                        y: Number(state.inset.detailCenterMm.y.toFixed(3))
                    }
                    : null,
                attachment: state.inset.attachment
                    ? clonePlainObject(state.inset.attachment)
                    : null,
                geographicSelection: state.inset.geographicBounds
                    ? clonePlainObject(state.inset.geographicBounds)
                    : null
            },
            connector: {
                geometry: "paired_external_tangent_bands",
                sourceFrameThicknessMm: state.inset.sourceFrameMm,
                detailFrameThicknessMm: state.inset.detailFrameMm
            },
            validation: {
                isValid: Boolean(state.inset.geometryValid),
                message: state.inset.validationMessage || ""
            }
        };
    }
    //--------------------------------------------------
    // Detail inset QGIS production handoff
    // Release 2.0.0 Unit 2
    //--------------------------------------------------
    function roundArtworkMillimetres(value) {
        return Number.isFinite(value)
            ? Number(value.toFixed(3))
            : null;
    }
    function buildInsetBoundingBoxMm(center, diameterMm) {
        if (!center ||
            !Number.isFinite(center.x) ||
            !Number.isFinite(center.y) ||
            !Number.isFinite(diameterMm) ||
            diameterMm <= 0) {
            return null;
        }
        const radius = diameterMm / 2;
        return {
            xMm: roundArtworkMillimetres(center.x - radius),
            yMm: roundArtworkMillimetres(center.y - radius),
            widthMm: roundArtworkMillimetres(diameterMm),
            heightMm: roundArtworkMillimetres(diameterMm)
        };
    }
    function buildInsetCircleLayoutGeometry(circle) {
        if (!circle ||
            !circle.centerArtworkMm ||
            !Number.isFinite(circle.mappedDiameterMm) ||
            !Number.isFinite(circle.frameThicknessMm) ||
            !Number.isFinite(circle.outerDiameterMm)) {
            return null;
        }
        const center = {
            x: roundArtworkMillimetres(circle.centerArtworkMm.x),
            y: roundArtworkMillimetres(circle.centerArtworkMm.y)
        };
        return {
            centerArtworkMm: center,
            mappedDiameterMm: roundArtworkMillimetres(circle.mappedDiameterMm),
            frameThicknessMm: roundArtworkMillimetres(circle.frameThicknessMm),
            outerDiameterMm: roundArtworkMillimetres(circle.outerDiameterMm),
            mappedRadiusMm: roundArtworkMillimetres(circle.mappedDiameterMm / 2),
            outerRadiusMm: roundArtworkMillimetres(circle.outerDiameterMm / 2),
            mappedBoundingBoxMm: buildInsetBoundingBoxMm(center, circle.mappedDiameterMm),
            outerBoundingBoxMm: buildInsetBoundingBoxMm(center, circle.outerDiameterMm)
        };
    }
    function roundInsetTangentPoint(point) {
        if (!point ||
            !Number.isFinite(point.x) ||
            !Number.isFinite(point.y)) {
            return null;
        }
        return {
            xMm: roundArtworkMillimetres(point.x),
            yMm: roundArtworkMillimetres(point.y)
        };
    }
    function buildInsetConnectorProductionGeometry(sourceCircle, detailCircle) {
        if (!sourceCircle ||
            !detailCircle ||
            !sourceCircle.centerArtworkMm ||
            !detailCircle.centerArtworkMm) {
            return null;
        }
        const sourceCenter = {
            x: sourceCircle.centerArtworkMm.x,
            y: sourceCircle.centerArtworkMm.y
        };
        const detailCenter = {
            x: detailCircle.centerArtworkMm.x,
            y: detailCircle.centerArtworkMm.y
        };
        const sourceInnerRadius = sourceCircle.mappedDiameterMm / 2;
        const sourceOuterRadius = sourceCircle.outerDiameterMm / 2;
        const detailInnerRadius = detailCircle.mappedDiameterMm / 2;
        const detailOuterRadius = detailCircle.outerDiameterMm / 2;
        const bands = [];
        [
            { side: -1, name: "band_a" },
            { side: 1, name: "band_b" }
        ].forEach(definition => {
            const outer = getExternalTangentPoints(sourceCenter, sourceOuterRadius, detailCenter, detailOuterRadius, definition.side);
            const inner = getExternalTangentPoints(sourceCenter, sourceInnerRadius, detailCenter, detailInnerRadius, definition.side);
            if (!outer || !inner) {
                return;
            }
            bands.push({
                name: definition.name,
                side: definition.side,
                pointsArtworkMm: [
                    roundInsetTangentPoint(outer.a),
                    roundInsetTangentPoint(outer.b),
                    roundInsetTangentPoint(inner.b),
                    roundInsetTangentPoint(inner.a)
                ]
            });
        });
        return {
            geometry: "paired_external_tangent_bands",
            artworkCoordinateUnits: "millimetres",
            sourceInnerRadiusMm: roundArtworkMillimetres(sourceInnerRadius),
            sourceOuterRadiusMm: roundArtworkMillimetres(sourceOuterRadius),
            detailInnerRadiusMm: roundArtworkMillimetres(detailInnerRadius),
            detailOuterRadiusMm: roundArtworkMillimetres(detailOuterRadius),
            bands
        };
    }
    function buildInsetExtent4326(geographicSelection) {
        const bounds = geographicSelection && geographicSelection.bounds4326
            ? geographicSelection.bounds4326
            : null;
        if (!bounds ||
            ![bounds.west, bounds.east, bounds.south, bounds.north].every(Number.isFinite)) {
            return null;
        }
        const extent = {
            crs: "EPSG:4326",
            coordinateOrder: "xmin, xmax, ymin, ymax",
            units: "degrees",
            xmin: roundCoordinate(bounds.west),
            xmax: roundCoordinate(bounds.east),
            ymin: roundCoordinate(bounds.south),
            ymax: roundCoordinate(bounds.north)
        };
        extent.longitudeSpanDegrees = Number((extent.xmax - extent.xmin).toFixed(7));
        extent.crossesAntimeridian = extent.longitudeSpanDegrees > 180;
        extent.qgisInput = formatQgisExtent(extent, 7);
        return extent;
    }
    function buildInsetExtent3857(geographicSelection) {
        const source = geographicSelection && geographicSelection.extent3857
            ? geographicSelection.extent3857
            : null;
        if (!source ||
            ![source.xmin, source.xmax, source.ymin, source.ymax].every(Number.isFinite)) {
            return null;
        }
        const extent = {
            crs: "EPSG:3857",
            coordinateOrder: "xmin, xmax, ymin, ymax",
            units: "metres",
            xmin: roundProjectedMeters(source.xmin),
            xmax: roundProjectedMeters(source.xmax),
            ymin: roundProjectedMeters(source.ymin),
            ymax: roundProjectedMeters(source.ymax)
        };
        extent.widthMeters = roundProjectedMeters(extent.xmax - extent.xmin);
        extent.heightMeters = roundProjectedMeters(extent.ymax - extent.ymin);
        extent.qgisInput = formatQgisExtent(extent, 3);
        return extent;
    }
    function buildInsetQgisProductionHandoff(insetPayload, mainQgisHandoff, designReference) {
        if (!insetPayload || !insetPayload.enabled) {
            return null;
        }
        const sourceCircle = insetPayload.sourceCircle || null;
        const detailCircle = insetPayload.detailCircle || null;
        if (!sourceCircle || !detailCircle) {
            return null;
        }
        const geographicSelection = sourceCircle.geographicSelection || null;
        const extent3857 = buildInsetExtent3857(geographicSelection);
        const extent4326 = buildInsetExtent4326(geographicSelection);
        if (!extent3857 || !extent4326) {
            return null;
        }
        const sourceGeometry = buildInsetCircleLayoutGeometry(sourceCircle);
        const detailGeometry = buildInsetCircleLayoutGeometry(detailCircle);
        if (!sourceGeometry || !detailGeometry) {
            return null;
        }
        const sourceCenter4326 = geographicSelection &&
            geographicSelection.bounds4326 &&
            geographicSelection.bounds4326.center
            ? {
                lng: roundCoordinate(geographicSelection.bounds4326.center.lng),
                lat: roundCoordinate(geographicSelection.bounds4326.center.lat)
            }
            : null;
        const sourceCenter3857 = sourceCenter4326
            ? projectWgs84ToWebMercator(sourceCenter4326)
            : null;
        const detailDiameterMm = detailCircle.mappedDiameterMm;
        const widthScale = calculateNominalScaleDenominator(extent3857.widthMeters, detailDiameterMm);
        const heightScale = calculateNominalScaleDenominator(extent3857.heightMeters, detailDiameterMm);
        const scaleChecks = [widthScale, heightScale].filter(Number.isFinite);
        const recommendedScale = scaleChecks.length
            ? scaleChecks.reduce((total, value) => total + value, 0) / scaleChecks.length
            : null;
        const scaleDifferencePercent = Number.isFinite(widthScale) && Number.isFinite(heightScale)
            ? Number((Math.abs(widthScale - heightScale) / Math.max(widthScale, heightScale) * 100).toFixed(4))
            : null;
        const connector = buildInsetConnectorProductionGeometry(sourceCircle, detailCircle);
        const artworkWidthMm = mainQgisHandoff && mainQgisHandoff.layoutMapItem
            ? mainQgisHandoff.layoutMapItem.widthMm
            : null;
        const artworkHeightMm = mainQgisHandoff && mainQgisHandoff.layoutMapItem
            ? mainQgisHandoff.layoutMapItem.heightMm
            : null;
        return {
            schemaVersion: "2.0-unit-2",
            workflow: "qgis_detail_inset_exact_reconstruction",
            sourceOfTruth: "detailMap.extent3857",
            generatedAt: new Date().toISOString(),
            designReference: designReference || null,
            artworkCoordinateSystem: {
                origin: "mapped_artwork_top_left",
                units: "millimetres",
                xPositiveDirection: "right",
                yPositiveDirection: "down",
                widthMm: artworkWidthMm,
                heightMm: artworkHeightMm
            },
            mainMap: mainQgisHandoff
                ? {
                    extent3857: clonePlainObject(mainQgisHandoff.extent3857),
                    layoutMapItem: clonePlainObject(mainQgisHandoff.layoutMapItem)
                }
                : null,
            sourceCircle: {
                ...sourceGeometry,
                center4326: sourceCenter4326,
                center3857: sourceCenter3857,
                detailSelectionExtent3857: extent3857,
                detailSelectionExtent4326: extent4326
            },
            detailCircle: {
                ...detailGeometry,
                structuralOverlapMm: roundArtworkMillimetres(detailCircle.structuralOverlapMm),
                attachment: detailCircle.attachment
                    ? clonePlainObject(detailCircle.attachment)
                    : null
            },
            detailMap: {
                crs: "EPSG:3857",
                shape: "circle",
                diameterMm: roundArtworkMillimetres(detailDiameterMm),
                extent3857,
                extent4326,
                recommendedScaleDenominator: roundScaleDenominator(recommendedScale),
                widthScaleDenominator: roundScaleDenominator(widthScale),
                heightScaleDenominator: roundScaleDenominator(heightScale),
                scaleDifferencePercent,
                productionInstruction: "Use this EPSG:3857 extent as the exact rectangular map extent enclosing the customer-selected circular source area. Fit it to a square map item whose width and height equal the detail mapped diameter, then retain only the circular detail area for final production."
            },
            connector,
            reconstructionOrder: [
                "Create the main mapped-artwork layout from productionHandoff.qgis.",
                "Locate the source circle from its artwork X/Y centre and mapped diameter.",
                "Use detailMap.extent3857 as the exact QGIS extent for the enlarged detail data.",
                "Locate the detail circle from its artwork X/Y centre, mapped diameter and frame dimensions.",
                "Use detailCircle.attachment to verify the required frame-edge or corner intersection.",
                "Recreate the two connector bands from connector.bands points if needed before downstream SVG work."
            ]
        };
    }
    function formatInsetQgisProductionText(handoff) {
        if (!handoff) {
            return "";
        }
        const source = handoff.sourceCircle || {};
        const detail = handoff.detailCircle || {};
        const mapItem = handoff.detailMap || {};
        const artwork = handoff.artworkCoordinateSystem || {};
        const sourceCenter = formatInsetArtworkPointMm(source.centerArtworkMm);
        const detailCenter = formatInsetArtworkPointMm(detail.centerArtworkMm);
        const sourceMappedBox = formatInsetBoundingBoxMm(source.mappedBoundingBoxMm);
        const sourceOuterBox = formatInsetBoundingBoxMm(source.outerBoundingBoxMm);
        const detailMappedBox = formatInsetBoundingBoxMm(detail.mappedBoundingBoxMm);
        const detailOuterBox = formatInsetBoundingBoxMm(detail.outerBoundingBoxMm);
        const attachment = formatInsetAttachment(detail.attachment);
        const connector = handoff.connector || {};
        const connectorBands = Array.isArray(connector.bands)
            ? connector.bands
            : [];
        const connectorBandA = connectorBands.find(band => band && band.name === "band_a") || connectorBands[0] || null;
        const connectorBandB = connectorBands.find(band => band && band.name === "band_b") || connectorBands[1] || null;
        const connectorBandALines = connectorBandA && Array.isArray(connectorBandA.pointsArtworkMm)
            ? connectorBandA.pointsArtworkMm.map((point, index) => {
                const labels = ["Source outer", "Detail outer", "Detail inner", "Source inner"];
                return `${index + 1}. ${labels[index]}: ${formatInsetConnectorPointMm(point)}`;
            })
            : [];
        const connectorBandBLines = connectorBandB && Array.isArray(connectorBandB.pointsArtworkMm)
            ? connectorBandB.pointsArtworkMm.map((point, index) => {
                const labels = ["Source outer", "Detail outer", "Detail inner", "Source inner"];
                return `${index + 1}. ${labels[index]}: ${formatInsetConnectorPointMm(point)}`;
            })
            : [];
        const extent3857 = mapItem.extent3857 &&
            mapItem.extent3857.qgisInput
            ? mapItem.extent3857.qgisInput
            : "";
        const extent4326 = mapItem.extent4326 &&
            mapItem.extent4326.qgisInput
            ? mapItem.extent4326.qgisInput
            : "";
        return [
            "DETAIL INSET — QGIS PRODUCTION HANDOFF",
            "",
            "INSET SOURCE EXTENT — EPSG:3857",
            "Extent order: xmin, xmax, ymin, ymax",
            `Copy-ready extent: ${extent3857}`,
            `QGIS map item size: ${mapItem.diameterMm || ""} × ${mapItem.diameterMm || ""} mm`,
            Number.isFinite(mapItem.recommendedScaleDenominator)
                ? `Nominal scale check: 1:${mapItem.recommendedScaleDenominator}`
                : null,
            "",
            "INSET SOURCE CIRCLE — ARTWORK POSITION",
            `Centre: ${sourceCenter}`,
            `Mapped diameter: ${source.mappedDiameterMm || ""} mm`,
            `Frame thickness: ${source.frameThicknessMm || ""} mm`,
            `Mapped box: ${sourceMappedBox}`,
            `Outer box: ${sourceOuterBox}`,
            "",
            "INSET DETAIL CIRCLE — ARTWORK POSITION",
            `Centre: ${detailCenter}`,
            `Mapped diameter: ${detail.mappedDiameterMm || ""} mm`,
            `Frame thickness: ${detail.frameThicknessMm || ""} mm`,
            `Mapped box: ${detailMappedBox}`,
            `Outer box: ${detailOuterBox}`,
            `Attachment: ${attachment}`,
            `Structural overlap: ${detail.structuralOverlapMm || ""} mm`,
            "",
            "ARTWORK COORDINATE SYSTEM",
            "Origin: mapped artwork top-left",
            "X increases to the right; Y increases downward.",
            `Mapped artwork size: ${artwork.widthMm || ""} × ${artwork.heightMm || ""} mm`,
            "",
            "CONNECTOR BANDS — ARTWORK COORDINATES",
            `Geometry: ${connector.geometry || "Not available"}`,
            "Polygon vertex order: Source outer → Detail outer → Detail inner → Source inner",
            `Radii: ${formatInsetConnectorRadii(connector) || "Not available"}`,
            "",
            "BAND A",
            ...(connectorBandALines.length ? connectorBandALines : ["Not available"]),
            "",
            "BAND B",
            ...(connectorBandBLines.length ? connectorBandBLines : ["Not available"]),
            "",
            "BACKUP INSET EXTENT — EPSG:4326",
            `Copy-ready extent: ${extent4326}`
        ]
            .filter(line => line !== null)
            .join("\n");
    }
    function setInsetEnabled(enabled) {
        if (state.review.status !== "editing") {
            return;
        }
        const previousInsetEnabled = state.inset.enabled;
        state.inset.enabled = Boolean(enabled);
        if (!state.inset.enabled) {
            setInsetInteractionFocus(null);
        }
        if (!state.inset.enabled && state.inspection.active) {
            exitInspectionView();
        }
        if (state.inset.enabled) {
            initialiseInsetPositions();
            ensureInsetDetailMap();
        }
        insetToggleButtons.forEach(button => {
            const isActive = String(state.inset.enabled) ===
                button.dataset.insetEnabled;
            button.classList.toggle("is-active", isActive);
            button.setAttribute("aria-pressed", String(isActive));
            updateSelectionAccessibility(button, isActive);
        });
        updateInsetGeometry();
        updateInsetPrecisionControls();
        updateShopifyProductBinding();
        updateProductionPayload();
        updateAssistedConfigurationSummary();
        syncInspectionAvailability();
        if (previousInsetEnabled !== state.inset.enabled) {
            announceConfigurationSelection(state.inset.enabled
                ? "Detail inset: added."
                : "Detail inset: not included.");
        }
    }
    function handleInsetSourcePointerMove(event, dragOffsetMm = null) {
        const metrics = getInsetRenderMetrics();
        if (!metrics) {
            return;
        }
        const pointerPosition = canvasClientPointToArtworkMm(event.clientX, event.clientY, metrics);
        const requested = dragOffsetMm
            ? {
                x: pointerPosition.x - dragOffsetMm.x,
                y: pointerPosition.y - dragOffsetMm.y
            }
            : pointerPosition;
        state.inset.sourceCenterMm =
            constrainSourceMovementAgainstDetail(requested, state.inset.sourceCenterMm);
        updateInsetGeometry();
    }
    function handleInsetDetailPointerMove(event, dragOffsetMm = null) {
        const metrics = getInsetRenderMetrics();
        if (!metrics) {
            return;
        }
        const pointerPosition = canvasClientPointToArtworkMm(event.clientX, event.clientY, metrics);
        const requested = dragOffsetMm
            ? {
                x: pointerPosition.x - dragOffsetMm.x,
                y: pointerPosition.y - dragOffsetMm.y
            }
            : pointerPosition;
        const constrained = constrainDetailMovementAgainstSource(requested, state.inset.detailCenterMm);
        state.inset.detailCenterMm = constrained.center;
        state.inset.attachment = constrained.attachment;
        updateInsetGeometry();
    }
    function updateInsetPrecisionControls() {
        if (insetPrecisionControls) {
            insetPrecisionControls.hidden = !state.inset.enabled;
        }
        insetPrecisionTargetButtons.forEach(button => {
            const isActive = button.dataset.insetPrecisionTarget === insetPrecisionTarget;
            button.classList.toggle("is-active", isActive);
            button.setAttribute("aria-pressed", String(isActive));
        });
        if (insetSourcePrecisionPanel) {
            insetSourcePrecisionPanel.hidden =
                insetPrecisionTarget !== "source";
        }
        if (insetDetailPrecisionPanel) {
            insetDetailPrecisionPanel.hidden =
                insetPrecisionTarget !== "detail";
        }
    }
    function setInsetPrecisionTarget(target, syncVisualFocus = true) {
        if (target !== "source" && target !== "detail") {
            return;
        }
        insetPrecisionTarget = target;
        updateInsetPrecisionControls();
        if (!syncVisualFocus) {
            return;
        }
        const handle = target === "source"
            ? insetSourceHandle
            : insetDetailHandle;
        [insetSourceHandle, insetDetailHandle].forEach(candidate => {
            if (!candidate) {
                return;
            }
            candidate.classList.toggle("is-touch-focused", candidate === handle);
        });
    }
    function setInsetInteractionFocus(handle) {
        [insetSourceHandle, insetDetailHandle].forEach(candidate => {
            if (!candidate) {
                return;
            }
            candidate.classList.toggle("is-touch-focused", candidate === handle);
        });
        if (handle === insetSourceHandle) {
            setInsetPrecisionTarget("source", false);
        }
        else if (handle === insetDetailHandle) {
            setInsetPrecisionTarget("detail", false);
        }
    }
    function getInsetDragOffsetMm(event, currentCenterMm) {
        const metrics = getInsetRenderMetrics();
        if (!metrics || !currentCenterMm) {
            return { x: 0, y: 0 };
        }
        const pointerPosition = canvasClientPointToArtworkMm(event.clientX, event.clientY, metrics);
        return {
            x: pointerPosition.x - currentCenterMm.x,
            y: pointerPosition.y - currentCenterMm.y
        };
    }
    function bindInsetDrag(handle, moveHandler, getCurrentCenterMm) {
        if (!handle) {
            return;
        }
        let dragOffsetMm = { x: 0, y: 0 };
        handle.addEventListener("pointerdown", event => {
            if (state.review.status !== "editing" ||
                !state.inset.enabled) {
                return;
            }
            if (event.button !== undefined && event.button !== 0) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            setInsetInteractionFocus(handle);
            // Unit 1a: pointer interaction must also establish keyboard focus.
            // Without this, focus can remain on a diameter slider and arrow keys
            // continue changing the slider instead of moving the selected circle.
            try {
                handle.focus({ preventScroll: true });
            }
            catch (error) {
                handle.focus();
            }
            dragOffsetMm = getInsetDragOffsetMm(event, typeof getCurrentCenterMm === "function"
                ? getCurrentCenterMm()
                : null);
            handle.classList.add("is-dragging");
            handle.setPointerCapture(event.pointerId);
            // Deliberately do not move the object on pointerdown. Keeping the
            // initial finger-to-centre offset prevents the target from snapping
            // underneath the fingertip and obscuring the feature being positioned.
        });
        handle.addEventListener("pointermove", event => {
            if (!handle.hasPointerCapture(event.pointerId)) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            moveHandler(event, dragOffsetMm);
        });
        const finish = event => {
            if (handle.hasPointerCapture(event.pointerId)) {
                handle.releasePointerCapture(event.pointerId);
            }
            handle.classList.remove("is-dragging");
            dragOffsetMm = { x: 0, y: 0 };
            updateProductionPayload();
        };
        handle.addEventListener("pointerup", finish);
        handle.addEventListener("pointercancel", finish);
    }
    function nudgeInsetSource(deltaXMm, deltaYMm) {
        if (!state.inset.enabled ||
            state.review.status !== "editing" ||
            !state.inset.sourceCenterMm) {
            return;
        }
        state.inset.sourceCenterMm =
            constrainSourceMovementAgainstDetail({
                x: state.inset.sourceCenterMm.x + deltaXMm,
                y: state.inset.sourceCenterMm.y + deltaYMm
            }, state.inset.sourceCenterMm);
        setInsetInteractionFocus(insetSourceHandle);
        updateInsetGeometry();
        updateProductionPayload();
    }
    function moveInsetSourceByKeyboard(event) {
        if (!state.inset.enabled || state.review.status !== "editing") {
            return;
        }
        const step = event.shiftKey
            ? INSET_KEYBOARD_LARGE_STEP_MM
            : INSET_KEYBOARD_STEP_MM;
        const delta = { x: 0, y: 0 };
        if (event.key === "ArrowLeft")
            delta.x = -step;
        else if (event.key === "ArrowRight")
            delta.x = step;
        else if (event.key === "ArrowUp")
            delta.y = -step;
        else if (event.key === "ArrowDown")
            delta.y = step;
        else
            return;
        event.preventDefault();
        nudgeInsetSource(delta.x, delta.y);
    }
    function getInsetCenterPathSequence(detailDiameterMm) {
        const artwork = getInsetArtworkDimensionsMm();
        if (!artwork) {
            return null;
        }
        const radius = detailDiameterMm / 2 + INSET_DETAIL_FRAME_MM;
        const inset = radius - INSET_FRAME_OVERLAP_MM;
        const left = inset;
        const top = inset;
        const right = artwork.widthMm - inset;
        const bottom = artwork.heightMm - inset;
        const horizontal = Math.max(0, right - left);
        const vertical = Math.max(0, bottom - top);
        const perimeter = 2 * (horizontal + vertical);
        return {
            left,
            top,
            right,
            bottom,
            horizontal,
            vertical,
            perimeter
        };
    }
    function detailCenterToPerimeterDistance(center, path) {
        if (!center || !path || !path.perimeter) {
            return 0;
        }
        const epsilon = 0.001;
        if (Math.abs(center.y - path.top) <= epsilon) {
            return clampNumber(center.x - path.left, 0, path.horizontal);
        }
        if (Math.abs(center.x - path.right) <= epsilon) {
            return path.horizontal +
                clampNumber(center.y - path.top, 0, path.vertical);
        }
        if (Math.abs(center.y - path.bottom) <= epsilon) {
            return path.horizontal + path.vertical +
                clampNumber(path.right - center.x, 0, path.horizontal);
        }
        return path.horizontal * 2 + path.vertical +
            clampNumber(path.bottom - center.y, 0, path.vertical);
    }
    function perimeterDistanceToDetailCenter(distance, path) {
        let value = ((distance % path.perimeter) + path.perimeter) % path.perimeter;
        if (value <= path.horizontal) {
            return { x: path.left + value, y: path.top };
        }
        value -= path.horizontal;
        if (value <= path.vertical) {
            return { x: path.right, y: path.top + value };
        }
        value -= path.vertical;
        if (value <= path.horizontal) {
            return { x: path.right - value, y: path.bottom };
        }
        value -= path.horizontal;
        return { x: path.left, y: path.bottom - value };
    }
    function nudgeInsetDetailAlongPerimeter(direction, stepMm = INSET_KEYBOARD_STEP_MM) {
        if (!state.inset.enabled ||
            state.review.status !== "editing" ||
            !state.inset.detailCenterMm ||
            !Number.isFinite(direction) ||
            direction === 0) {
            return;
        }
        const path = getInsetCenterPathSequence(state.inset.detailDiameterMm);
        if (!path || !path.perimeter) {
            return;
        }
        const currentDistance = detailCenterToPerimeterDistance(state.inset.detailCenterMm, path);
        const center = perimeterDistanceToDetailCenter(currentDistance + Math.sign(direction) * stepMm, path);
        const constrained = constrainDetailMovementAgainstSource(center, state.inset.detailCenterMm);
        state.inset.detailCenterMm = constrained.center;
        state.inset.attachment = constrained.attachment;
        setInsetInteractionFocus(insetDetailHandle);
        updateInsetGeometry();
        updateProductionPayload();
    }
    function moveInsetDetailByKeyboard(event) {
        if (!state.inset.enabled || state.review.status !== "editing") {
            return;
        }
        if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
            return;
        }
        event.preventDefault();
        const step = event.shiftKey
            ? INSET_KEYBOARD_LARGE_STEP_MM
            : INSET_KEYBOARD_STEP_MM;
        const direction = event.key === "ArrowLeft" || event.key === "ArrowUp"
            ? -1
            : 1;
        nudgeInsetDetailAlongPerimeter(direction, step);
    }
    function bindInsetNudgePressAndHold(button, action) {
        if (!button || typeof action !== "function") {
            return;
        }
        let activePointerId = null;
        let repeatDelayTimer = null;
        let repeatIntervalTimer = null;
        const clearRepeat = () => {
            if (repeatDelayTimer !== null) {
                window.clearTimeout(repeatDelayTimer);
                repeatDelayTimer = null;
            }
            if (repeatIntervalTimer !== null) {
                window.clearInterval(repeatIntervalTimer);
                repeatIntervalTimer = null;
            }
            button.classList.remove("is-holding");
        };
        const runAction = () => {
            if (button.disabled) {
                clearRepeat();
                return;
            }
            action();
        };
        const finishPointerPress = event => {
            if (activePointerId === null ||
                (event && event.pointerId !== activePointerId)) {
                return;
            }
            const pointerId = activePointerId;
            activePointerId = null;
            clearRepeat();
            if (typeof button.hasPointerCapture === "function" &&
                button.hasPointerCapture(pointerId)) {
                button.releasePointerCapture(pointerId);
            }
            button.focus({ preventScroll: true });
        };
        button.addEventListener("pointerdown", event => {
            if (activePointerId !== null ||
                (Number.isFinite(event.button) && event.button !== 0)) {
                return;
            }
            event.preventDefault();
            activePointerId = event.pointerId;
            if (typeof button.setPointerCapture === "function") {
                button.setPointerCapture(event.pointerId);
            }
            button.classList.add("is-holding");
            runAction();
            repeatDelayTimer = window.setTimeout(() => {
                runAction();
                repeatIntervalTimer = window.setInterval(runAction, INSET_NUDGE_REPEAT_INTERVAL_MS);
            }, INSET_NUDGE_REPEAT_DELAY_MS);
        });
        button.addEventListener("pointerup", finishPointerPress);
        button.addEventListener("pointercancel", finishPointerPress);
        button.addEventListener("lostpointercapture", event => {
            if (activePointerId === event.pointerId) {
                activePointerId = null;
                clearRepeat();
            }
        });
        // Prevent the browser's long-press text selection/callout behaviour.
        button.addEventListener("contextmenu", event => event.preventDefault());
        // Pointer presses are handled above. Keep synthesized/keyboard clicks
        // available so the controls retain their normal keyboard accessibility.
        button.addEventListener("click", event => {
            if (event.detail !== 0) {
                event.preventDefault();
                return;
            }
            action();
            button.focus({ preventScroll: true });
        });
    }
    //--------------------------------------------------
    // Production data model
    // Release 0.8.1
    //--------------------------------------------------
    function roundCoordinate(value) {
        return Number(value.toFixed(7));
    }
    function roundZoom(value) {
        return Number(value.toFixed(4));
    }
    //--------------------------------------------------
    // Mapped area ground dimensions
    // Release 0.8.13
    //--------------------------------------------------
    const EARTH_RADIUS_METERS = 6371008.8;
    function roundGroundMeters(value) {
        if (!Number.isFinite(value)) {
            return null;
        }
        if (Math.abs(value) >= 1000) {
            return Math.round(value * 10) / 10;
        }
        return Math.round(value * 100) / 100;
    }
    function roundGroundKilometers(value) {
        if (!Number.isFinite(value)) {
            return null;
        }
        return Math.round((value / 1000) * 1000) / 1000;
    }
    function roundGroundSquareMeters(value) {
        if (!Number.isFinite(value)) {
            return null;
        }
        if (Math.abs(value) >= 10000) {
            return Math.round(value);
        }
        return Math.round(value * 10) / 10;
    }
    function roundGroundSquareKilometers(value) {
        if (!Number.isFinite(value)) {
            return null;
        }
        return Math.round((value / 1000000) * 1000000) / 1000000;
    }
    function toRadians(value) {
        return value * Math.PI / 180;
    }
    function getPointLatitude(point) {
        if (!point) {
            return null;
        }
        const value = point.lat !== undefined
            ? Number(point.lat)
            : Number(point.latitude);
        return Number.isFinite(value)
            ? value
            : null;
    }
    function getPointLongitude(point) {
        if (!point) {
            return null;
        }
        const value = point.lng !== undefined
            ? Number(point.lng)
            : Number(point.longitude);
        return Number.isFinite(value)
            ? value
            : null;
    }
    function calculateGeodesicDistanceMeters(start, end) {
        const startLongitude = getPointLongitude(start);
        const startLatitude = getPointLatitude(start);
        const endLongitude = getPointLongitude(end);
        const endLatitude = getPointLatitude(end);
        if (![
            startLongitude,
            startLatitude,
            endLongitude,
            endLatitude
        ].every(Number.isFinite)) {
            return null;
        }
        const deltaLatitude = toRadians(endLatitude - startLatitude);
        const deltaLongitude = toRadians(endLongitude - startLongitude);
        const startLatitudeRadians = toRadians(startLatitude);
        const endLatitudeRadians = toRadians(endLatitude);
        const haversineValue = Math.sin(deltaLatitude / 2) *
            Math.sin(deltaLatitude / 2) +
            Math.cos(startLatitudeRadians) *
                Math.cos(endLatitudeRadians) *
                Math.sin(deltaLongitude / 2) *
                Math.sin(deltaLongitude / 2);
        const clampedHaversineValue = Math.min(1, Math.max(0, haversineValue));
        const centralAngle = 2 * Math.atan2(Math.sqrt(clampedHaversineValue), Math.sqrt(1 - clampedHaversineValue));
        return EARTH_RADIUS_METERS * centralAngle;
    }
    function buildMappedAreaGroundDimensions(corners) {
        if (!corners ||
            !corners.northWest ||
            !corners.northEast ||
            !corners.southEast ||
            !corners.southWest) {
            return null;
        }
        const topMeters = calculateGeodesicDistanceMeters(corners.northWest, corners.northEast);
        const bottomMeters = calculateGeodesicDistanceMeters(corners.southWest, corners.southEast);
        const leftMeters = calculateGeodesicDistanceMeters(corners.northWest, corners.southWest);
        const rightMeters = calculateGeodesicDistanceMeters(corners.northEast, corners.southEast);
        if (![
            topMeters,
            bottomMeters,
            leftMeters,
            rightMeters
        ].every(Number.isFinite)) {
            return null;
        }
        const widthMeters = (topMeters + bottomMeters) / 2;
        const heightMeters = (leftMeters + rightMeters) / 2;
        const areaSquareMeters = widthMeters * heightMeters;
        return {
            calculationMethod: "average_geodesic_edge_lengths",
            coordinateSystem: "WGS84 longitude/latitude",
            earthRadiusMeters: EARTH_RADIUS_METERS,
            widthMeters: roundGroundMeters(widthMeters),
            heightMeters: roundGroundMeters(heightMeters),
            widthKilometers: roundGroundKilometers(widthMeters),
            heightKilometers: roundGroundKilometers(heightMeters),
            areaSquareMeters: roundGroundSquareMeters(areaSquareMeters),
            areaSquareKilometers: roundGroundSquareKilometers(areaSquareMeters),
            edgeMeasurementsMeters: {
                top: roundGroundMeters(topMeters),
                bottom: roundGroundMeters(bottomMeters),
                left: roundGroundMeters(leftMeters),
                right: roundGroundMeters(rightMeters)
            },
            productionNote: "Approximate ground dimensions of the visible mapped artwork area, calculated from the captured corner coordinates. Useful for production planning, not survey-grade measurement."
        };
    }
    //--------------------------------------------------
    // Exact QGIS production handoff
    // Release 1.0.0 Unit 1 candidate
    //--------------------------------------------------
    const WEB_MERCATOR_RADIUS_METERS = 6378137;
    const WEB_MERCATOR_MAX_LATITUDE = 85.0511287798066;
    function roundProjectedMeters(value) {
        if (!Number.isFinite(value)) {
            return null;
        }
        return Number(value.toFixed(3));
    }
    function roundScaleDenominator(value) {
        if (!Number.isFinite(value) || value <= 0) {
            return null;
        }
        return Math.round(value);
    }
    function clampWebMercatorLatitude(value) {
        if (!Number.isFinite(value)) {
            return null;
        }
        return Math.min(WEB_MERCATOR_MAX_LATITUDE, Math.max(-WEB_MERCATOR_MAX_LATITUDE, value));
    }
    function projectWgs84ToWebMercator(point) {
        const longitude = getPointLongitude(point);
        const latitude = clampWebMercatorLatitude(getPointLatitude(point));
        if (![
            longitude,
            latitude
        ].every(Number.isFinite)) {
            return null;
        }
        const x = WEB_MERCATOR_RADIUS_METERS *
            toRadians(longitude);
        const y = WEB_MERCATOR_RADIUS_METERS *
            Math.log(Math.tan(Math.PI / 4 +
                toRadians(latitude) / 2));
        return {
            x: roundProjectedMeters(x),
            y: roundProjectedMeters(y)
        };
    }
    function formatQgisCoordinate(value, decimalPlaces) {
        if (!Number.isFinite(value)) {
            return "";
        }
        return Number(value).toFixed(decimalPlaces);
    }
    function formatQgisExtent(extent, decimalPlaces) {
        if (!extent) {
            return "";
        }
        return [
            extent.xmin,
            extent.xmax,
            extent.ymin,
            extent.ymax
        ]
            .map(value => formatQgisCoordinate(value, decimalPlaces))
            .join(", ");
    }
    function buildQgisWktPolygon(coordinates, decimalPlaces) {
        if (!Array.isArray(coordinates) || coordinates.length < 4) {
            return "";
        }
        const closedCoordinates = [
            ...coordinates,
            coordinates[0]
        ];
        const coordinateText = closedCoordinates
            .map(([x, y]) => `${formatQgisCoordinate(x, decimalPlaces)} ${formatQgisCoordinate(y, decimalPlaces)}`)
            .join(", ");
        return `POLYGON ((${coordinateText}))`;
    }
    function buildQgisGeoJsonFeature(coordinates, crs, designReference) {
        if (!Array.isArray(coordinates) || coordinates.length < 4) {
            return null;
        }
        return {
            type: "Feature",
            properties: {
                designReference: designReference || null,
                crs
            },
            geometry: {
                type: "Polygon",
                coordinates: [[
                        ...coordinates,
                        coordinates[0]
                    ]]
            }
        };
    }
    function calculateNominalScaleDenominator(spanMeters, physicalSizeMm) {
        if (!Number.isFinite(spanMeters) ||
            !Number.isFinite(physicalSizeMm) ||
            spanMeters <= 0 ||
            physicalSizeMm <= 0) {
            return null;
        }
        return spanMeters / (physicalSizeMm / 1000);
    }
    function buildQgisProductionHandoff(mappedArtworkArea, orientedDimensions, mapDiagnostic, designReference) {
        if (!mappedArtworkArea ||
            !mappedArtworkArea.corners ||
            !orientedDimensions ||
            !orientedDimensions.mappedArtwork) {
            return null;
        }
        const cornerOrder = [
            "northWest",
            "northEast",
            "southEast",
            "southWest"
        ];
        const corners4326 = {};
        const corners3857 = {};
        for (const key of cornerOrder) {
            const sourcePoint = mappedArtworkArea.corners[key];
            const longitude = getPointLongitude(sourcePoint);
            const latitude = getPointLatitude(sourcePoint);
            const projectedPoint = projectWgs84ToWebMercator(sourcePoint);
            if (![
                longitude,
                latitude
            ].every(Number.isFinite) ||
                !projectedPoint) {
                return null;
            }
            corners4326[key] = {
                lng: roundCoordinate(longitude),
                lat: roundCoordinate(latitude)
            };
            corners3857[key] = projectedPoint;
        }
        const polygon4326 = cornerOrder.map(key => [
            corners4326[key].lng,
            corners4326[key].lat
        ]);
        const polygon3857 = cornerOrder.map(key => [
            corners3857[key].x,
            corners3857[key].y
        ]);
        const xValues = polygon3857.map(point => point[0]);
        const yValues = polygon3857.map(point => point[1]);
        const longitudeValues = polygon4326.map(point => point[0]);
        const latitudeValues = polygon4326.map(point => point[1]);
        const extent3857 = {
            crs: "EPSG:3857",
            coordinateOrder: "xmin, xmax, ymin, ymax",
            units: "metres",
            xmin: roundProjectedMeters(Math.min(...xValues)),
            xmax: roundProjectedMeters(Math.max(...xValues)),
            ymin: roundProjectedMeters(Math.min(...yValues)),
            ymax: roundProjectedMeters(Math.max(...yValues))
        };
        extent3857.widthMeters =
            roundProjectedMeters(extent3857.xmax - extent3857.xmin);
        extent3857.heightMeters =
            roundProjectedMeters(extent3857.ymax - extent3857.ymin);
        extent3857.qgisInput =
            formatQgisExtent(extent3857, 3);
        extent3857.corners =
            corners3857;
        extent3857.geoJson =
            buildQgisGeoJsonFeature(polygon3857, extent3857.crs, designReference);
        extent3857.wkt =
            buildQgisWktPolygon(polygon3857, 3);
        const extent4326 = {
            crs: "EPSG:4326",
            coordinateOrder: "xmin, xmax, ymin, ymax",
            units: "degrees",
            xmin: roundCoordinate(Math.min(...longitudeValues)),
            xmax: roundCoordinate(Math.max(...longitudeValues)),
            ymin: roundCoordinate(Math.min(...latitudeValues)),
            ymax: roundCoordinate(Math.max(...latitudeValues))
        };
        extent4326.longitudeSpanDegrees =
            Number((extent4326.xmax -
                extent4326.xmin).toFixed(7));
        extent4326.crossesAntimeridian =
            extent4326.longitudeSpanDegrees > 180;
        extent4326.qgisInput =
            formatQgisExtent(extent4326, 7);
        extent4326.corners =
            corners4326;
        extent4326.geoJson =
            buildQgisGeoJsonFeature(polygon4326, extent4326.crs, designReference);
        extent4326.wkt =
            buildQgisWktPolygon(polygon4326, 7);
        const mappedWidthMm = orientedDimensions.mappedArtwork.widthCm * 10;
        const mappedHeightMm = orientedDimensions.mappedArtwork.heightCm * 10;
        const widthScale = calculateNominalScaleDenominator(extent3857.widthMeters, mappedWidthMm);
        const heightScale = calculateNominalScaleDenominator(extent3857.heightMeters, mappedHeightMm);
        const scaleChecks = [
            widthScale,
            heightScale
        ].filter(Number.isFinite);
        const recommendedScale = scaleChecks.length
            ? scaleChecks.reduce((total, value) => total + value, 0) / scaleChecks.length
            : null;
        const scaleDifferencePercent = Number.isFinite(widthScale) &&
            Number.isFinite(heightScale)
            ? Number((Math.abs(widthScale - heightScale) /
                Math.max(widthScale, heightScale) *
                100).toFixed(4))
            : null;
        const center4326 = mapDiagnostic &&
            mapDiagnostic.center
            ? {
                lng: roundCoordinate(mapDiagnostic.center.lng),
                lat: roundCoordinate(mapDiagnostic.center.lat)
            }
            : null;
        const center3857 = center4326
            ? projectWgs84ToWebMercator(center4326)
            : null;
        return {
            schemaVersion: "1.0",
            workflow: "qgis_exact_extent",
            sourceOfTruth: "extent3857",
            generatedAt: new Date().toISOString(),
            designReference: designReference || null,
            extent3857,
            extent4326,
            layoutMapItem: {
                crs: "EPSG:3857",
                widthMm: mappedWidthMm,
                heightMm: mappedHeightMm,
                rotationDegrees: 0,
                recommendedScaleDenominator: roundScaleDenominator(recommendedScale),
                widthScaleDenominator: roundScaleDenominator(widthScale),
                heightScaleDenominator: roundScaleDenominator(heightScale),
                scaleDifferencePercent
            },
            center4326,
            center3857,
            mapLibreDiagnostic: mapDiagnostic
                ? {
                    center: center4326,
                    zoom: Number.isFinite(mapDiagnostic.zoom)
                        ? roundZoom(mapDiagnostic.zoom)
                        : null,
                    bearingDegrees: Number.isFinite(mapDiagnostic.bearing)
                        ? Number(mapDiagnostic.bearing.toFixed(4))
                        : 0,
                    pitchDegrees: Number.isFinite(mapDiagnostic.pitch)
                        ? Number(mapDiagnostic.pitch.toFixed(4))
                        : 0
                }
                : null,
            productionInstruction: "Use the EPSG:3857 extent and mapped-artwork layout dimensions as the authoritative QGIS setup. Do not recreate the accepted composition from centre and MapLibre zoom alone."
        };
    }
    function hasValidQgisExtent(extent, expectedCrs) {
        if (!extent || extent.crs !== expectedCrs) {
            return false;
        }
        return [
            extent.xmin,
            extent.xmax,
            extent.ymin,
            extent.ymax
        ].every(Number.isFinite) &&
            extent.xmax > extent.xmin &&
            extent.ymax > extent.ymin &&
            Boolean(extent.qgisInput);
    }
    function hasValidQgisProductionHandoff(qgisHandoff) {
        if (!qgisHandoff ||
            qgisHandoff.schemaVersion !== "1.0" ||
            qgisHandoff.workflow !== "qgis_exact_extent" ||
            qgisHandoff.sourceOfTruth !== "extent3857") {
            return false;
        }
        const layout = qgisHandoff.layoutMapItem;
        return hasValidQgisExtent(qgisHandoff.extent3857, "EPSG:3857") &&
            hasValidQgisExtent(qgisHandoff.extent4326, "EPSG:4326") &&
            layout &&
            Number.isFinite(layout.widthMm) &&
            Number.isFinite(layout.heightMm) &&
            layout.widthMm > 0 &&
            layout.heightMm > 0 &&
            Number.isFinite(layout.recommendedScaleDenominator) &&
            layout.recommendedScaleDenominator > 0;
    }
    function getOrientedDimensions(product) {
        const dimensions = {
            outer: {
                widthCm: product.outerWidthCm,
                heightCm: product.outerHeightCm
            },
            exposedOpening: {
                widthCm: product.exposedWidthCm,
                heightCm: product.exposedHeightCm
            },
            artworkSheet: {
                widthCm: product.artworkWidthCm,
                heightCm: product.artworkHeightCm
            },
            mappedArtwork: {
                widthCm: product.mappedWidthCm,
                heightCm: product.mappedHeightCm
            }
        };
        if (state.orientation !== "landscape") {
            return dimensions;
        }
        return {
            outer: {
                widthCm: dimensions.outer.heightCm,
                heightCm: dimensions.outer.widthCm
            },
            exposedOpening: {
                widthCm: dimensions.exposedOpening.heightCm,
                heightCm: dimensions.exposedOpening.widthCm
            },
            artworkSheet: {
                widthCm: dimensions.artworkSheet.heightCm,
                heightCm: dimensions.artworkSheet.widthCm
            },
            mappedArtwork: {
                widthCm: dimensions.mappedArtwork.heightCm,
                heightCm: dimensions.mappedArtwork.widthCm
            }
        };
    }
    function getPointFromScreenPosition(x, y) {
        const point = map.unproject([x, y]);
        return {
            lng: roundCoordinate(point.lng),
            lat: roundCoordinate(point.lat)
        };
    }
    function getMappedAreaPixelRect() {
        if (!mappedArea) {
            return null;
        }
        const mapRect = mapContainer.getBoundingClientRect();
        const mappedRect = mappedArea.getBoundingClientRect();
        if (!mapRect.width || !mapRect.height) {
            return null;
        }
        const visualScale = getInspectionVisualScale();
        return {
            left: (mappedRect.left - mapRect.left) / visualScale,
            top: (mappedRect.top - mapRect.top) / visualScale,
            right: (mappedRect.right - mapRect.left) / visualScale,
            bottom: (mappedRect.bottom - mapRect.top) / visualScale,
            width: mappedRect.width / visualScale,
            height: mappedRect.height / visualScale
        };
    }
    function getMappedAreaGeographicBounds() {
        const pixelRect = getMappedAreaPixelRect();
        if (!pixelRect) {
            return null;
        }
        const northWest = getPointFromScreenPosition(pixelRect.left, pixelRect.top);
        const northEast = getPointFromScreenPosition(pixelRect.right, pixelRect.top);
        const southEast = getPointFromScreenPosition(pixelRect.right, pixelRect.bottom);
        const southWest = getPointFromScreenPosition(pixelRect.left, pixelRect.bottom);
        const latitudes = [
            northWest.lat,
            northEast.lat,
            southEast.lat,
            southWest.lat
        ];
        const longitudes = [
            northWest.lng,
            northEast.lng,
            southEast.lng,
            southWest.lng
        ];
        const corners = {
            northWest,
            northEast,
            southEast,
            southWest
        };
        return {
            north: Math.max(...latitudes),
            south: Math.min(...latitudes),
            east: Math.max(...longitudes),
            west: Math.min(...longitudes),
            corners,
            pixelRect,
            groundDimensions: buildMappedAreaGroundDimensions(corners)
        };
    }
    //--------------------------------------------------
    // Receipt, production summaries and page mode
    // Release 0.8.9 / 0.8.11
    //--------------------------------------------------
    function getPageModeLabel(mode) {
        if (mode === "design_capture") {
            return "Design Capture Mode";
        }
        return "Shopify Store Mode";
    }
    function buildPageModeMetadata() {
        const mode = normalisePageMode(state.page.mode);
        const channelBoundary = getChannelBoundary();
        return {
            mode,
            label: getPageModeLabel(mode),
            source: state.page.source || "section_setting",
            channelBoundary,
            modeLocked: channelBoundary === ETSY_ISOLATED_BOUNDARY,
            isShopifyStoreMode: mode === "shopify_store",
            isDesignCaptureMode: mode === "design_capture",
            plannedFinalAction: mode === "design_capture"
                ? "technical_design_record"
                : "shopify_cart"
        };
    }
    function setPageMode(mode, source = "debug_helper") {
        const normalisedMode = normalisePageMode(mode);
        if (isEtsyIsolatedBoundary() && normalisedMode !== "design_capture") {
            state.page.mode = "design_capture";
            state.page.source = "etsy_isolated_boundary";
            return {
                ...buildPageModeMetadata(),
                requestedMode: normalisedMode,
                modeChangeRejected: true
            };
        }
        state.page.mode = normalisedMode;
        state.page.source = source;
        if (mapDesignerRoot) {
            mapDesignerRoot.dataset.mapDesignerPageMode =
                normalisedMode;
        }
        updateProductionPayload();
        updateCartReadinessGate();
        updateDesignCaptureForm();
        updateCartInterface();
        return buildPageModeMetadata();
    }
    function buildSpecialPlaceMetadata() {
        const searchedLocation = state.search.selectedLocation;
        const mapTitle = normaliseMapTitle(state.mapTitle.value);
        const titleSource = mapTitle
            ? (state.mapTitle.customerEdited
                ? "customer_map_title"
                : state.mapTitle.source === "search_suggestion"
                    ? "search_suggestion"
                    : "manual_map_title")
            : "no_title";
        return {
            label: mapTitle,
            provided: Boolean(mapTitle),
            source: titleSource,
            searchedLocation,
            searchOriginLabel: searchedLocation && searchedLocation.label
                ? searchedLocation.label
                : "",
            compositionAdjustedAfterSearch: Boolean(searchedLocation &&
                state.search.compositionAdjustedAfterSearch),
            customerEdited: Boolean(state.mapTitle.customerEdited),
            suggestedFromSearch: state.mapTitle.suggestedFromSearch || ""
        };
    }
    function buildMapTitleMetadata() {
        const specialPlace = buildSpecialPlaceMetadata();
        return {
            provided: Boolean(specialPlace.label),
            text: specialPlace.label || "",
            source: specialPlace.source,
            customerEdited: specialPlace.customerEdited,
            suggestedFromSearch: specialPlace.suggestedFromSearch
        };
    }
    function getPayloadSearchOriginLabel(payload) {
        if (payload &&
            payload.searchOrigin &&
            payload.searchOrigin.label) {
            return payload.searchOrigin.label;
        }
        if (payload &&
            payload.selectedLocation &&
            payload.selectedLocation.label) {
            return payload.selectedLocation.label;
        }
        return "";
    }
    function getDesignReferenceConfigurationCodes(payload = null) {
        const sizeKey = payload && payload.artwork
            ? payload.artwork.size
            : state.size;
        const orientationKey = payload && payload.artwork
            ? payload.artwork.orientation
            : state.orientation;
        const insetEnabled = payload && payload.inset
            ? Boolean(payload.inset.enabled)
            : Boolean(state.inset.enabled);
        const sizeCode = DESIGN_REFERENCE_SIZE_CODES[sizeKey] || "X";
        const orientationCode = DESIGN_REFERENCE_ORIENTATION_CODES[orientationKey] || "X";
        const detailCode = DESIGN_REFERENCE_DETAIL_CODES[String(insetEnabled)] || "X";
        return {
            sizeKey,
            orientationKey,
            insetEnabled,
            sizeCode,
            orientationCode,
            detailCode,
            orderMatchKey: `${sizeCode}-${orientationCode}-${detailCode}`
        };
    }
    function getDesignReferenceCheckCharacter(unsignedReference) {
        const hash = parseInt(fnv1a32(String(unsignedReference || "")), 16) >>> 0;
        return DESIGN_REFERENCE_CHARACTERS[hash % DESIGN_REFERENCE_CHARACTERS.length];
    }
    function createDesignReference(payload = null) {
        const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        let suffix = "";
        if (window.crypto &&
            typeof window.crypto.getRandomValues === "function") {
            const values = new Uint8Array(4);
            window.crypto.getRandomValues(values);
            values.forEach(value => {
                suffix += DESIGN_REFERENCE_CHARACTERS[value % DESIGN_REFERENCE_CHARACTERS.length];
            });
        }
        else {
            for (let index = 0; index < 4; index += 1) {
                suffix += DESIGN_REFERENCE_CHARACTERS[
                    Math.floor(Math.random() * DESIGN_REFERENCE_CHARACTERS.length)
                ];
            }
        }
        // Preserve the frozen Shopify Store Mode reference format. The Etsy
        // configuration-bearing schema is introduced only inside the isolated
        // Design Capture boundary so Store Mode/cart production records do not
        // change as a side effect of Etsy work.
        if (!isDesignCaptureMode()) {
            return `WW-${datePart}-${suffix}`;
        }
        const configuration = getDesignReferenceConfigurationCodes(payload);
        const unsignedReference = [
            "WWS",
            DESIGN_REFERENCE_SCHEMA,
            configuration.sizeCode,
            configuration.orientationCode,
            configuration.detailCode,
            datePart,
            suffix
        ].join("-");
        return `${unsignedReference}-${getDesignReferenceCheckCharacter(unsignedReference)}`;
    }
    function parseDesignReference(value) {
        const reference = String(value || "").trim().toUpperCase();
        const match = reference.match(/^WWS-(E\d+)-([SG])-([PL])-([ND])-(\d{8})-([A-HJ-NP-Z2-9]{4})-([A-HJ-NP-Z2-9])$/);
        if (!match) {
            return {
                reference,
                isValid: false,
                error: "format_invalid"
            };
        }
        const [
            , schema, sizeCode, orientationCode, detailCode,
            datePart, uniqueId, checkCharacter
        ] = match;
        const unsignedReference = reference.slice(0, reference.lastIndexOf("-"));
        const expectedCheckCharacter = getDesignReferenceCheckCharacter(unsignedReference);
        const schemaSupported = schema === DESIGN_REFERENCE_SCHEMA;
        const checksumValid = checkCharacter === expectedCheckCharacter;
        const size = sizeCode === "S" ? "Studio" : "Gallery";
        const sizeKey = sizeCode === "S" ? "30x40" : "40x50";
        const orientation = orientationCode === "P" ? "Portrait" : "Landscape";
        const orientationKey = orientationCode === "P" ? "portrait" : "landscape";
        const insetEnabled = detailCode === "D";
        return {
            reference,
            isValid: schemaSupported && checksumValid,
            schema,
            schemaSupported,
            checksumValid,
            expectedCheckCharacter,
            checkCharacter,
            sizeCode,
            size,
            sizeKey,
            orientationCode,
            orientation,
            orientationKey,
            detailCode,
            insetEnabled,
            detailOption: insetEnabled ? "Detail inset" : "No inset",
            orderMatchKey: `${sizeCode}-${orientationCode}-${detailCode}`,
            datePart,
            uniqueId
        };
    }
    function validateDesignReferenceAgainstPayload(reference, payload) {
        const parsed = parseDesignReference(reference);
        const expected = getDesignReferenceConfigurationCodes(payload);
        const matchesConfiguration = Boolean(
            parsed.isValid &&
            parsed.sizeCode === expected.sizeCode &&
            parsed.orientationCode === expected.orientationCode &&
            parsed.detailCode === expected.detailCode
        );
        return {
            isValid: parsed.isValid,
            matchesConfiguration,
            reference: parsed,
            expected,
            expectedOrderMatchKey: expected.orderMatchKey,
            actualOrderMatchKey: parsed.orderMatchKey || ""
        };
    }
    function validateDesignReferenceAgainstEtsySelection(reference, selection = {}) {
        const parsed = parseDesignReference(reference);
        const sizeValue = normaliseCommerceValue(
            selection.frameSize || selection.size || selection.sizeName || ""
        );
        const orientationValue = normaliseCommerceValue(
            selection.orientation || ""
        );
        const detailValue = normaliseCommerceValue(
            selection.detailOption || selection.inset || selection.detail || ""
        );
        const expectedSizeCode = ["studio", "s", "30x40", "30 × 40 cm", "30 x 40 cm"].includes(sizeValue)
            ? "S"
            : ["gallery", "g", "40x50", "40 × 50 cm", "40 x 50 cm"].includes(sizeValue)
                ? "G"
                : "";
        const expectedOrientationCode = ["portrait", "p"].includes(orientationValue)
            ? "P"
            : ["landscape", "l"].includes(orientationValue)
                ? "L"
                : "";
        const expectedDetailCode = ["detail inset", "add detail inset", "with inset", "inset", "d", "yes", "true"].includes(detailValue)
            ? "D"
            : ["no inset", "no detail inset", "without inset", "none", "n", "no", "false"].includes(detailValue)
                ? "N"
                : "";
        const mismatches = [];
        if (!parsed.isValid) {
            mismatches.push("reference_invalid");
        }
        if (!expectedSizeCode) {
            mismatches.push("etsy_size_unrecognised");
        }
        else if (parsed.sizeCode !== expectedSizeCode) {
            mismatches.push("frame_size_mismatch");
        }
        if (!expectedOrientationCode) {
            mismatches.push("etsy_orientation_unrecognised");
        }
        else if (parsed.orientationCode !== expectedOrientationCode) {
            mismatches.push("orientation_mismatch");
        }
        if (!expectedDetailCode) {
            mismatches.push("etsy_detail_option_unrecognised");
        }
        else if (parsed.detailCode !== expectedDetailCode) {
            mismatches.push("detail_option_mismatch");
        }
        const expectedOrderMatchKey = expectedSizeCode && expectedOrientationCode && expectedDetailCode
            ? `${expectedSizeCode}-${expectedOrientationCode}-${expectedDetailCode}`
            : "";
        return {
            isValidReference: parsed.isValid,
            matchesEtsySelection: mismatches.length === 0,
            reference: parsed,
            expectedOrderMatchKey,
            actualOrderMatchKey: parsed.orderMatchKey || "",
            mismatches
        };
    }
    function getPayloadOrientationLabel(payload) {
        const orientation = payload &&
            payload.artwork &&
            payload.artwork.orientation
            ? payload.artwork.orientation
            : state.orientation;
        return orientation === "landscape"
            ? "Landscape"
            : "Portrait";
    }
    function getPayloadSizeName(payload) {
        return payload &&
            payload.product &&
            payload.product.customerFacing &&
            payload.product.customerFacing.sizeName
            ? payload.product.customerFacing.sizeName
            : PRODUCTS[state.size].label;
    }
    function getPayloadPhysicalSizeLabel(payload) {
        return payload &&
            payload.product &&
            payload.product.customerFacing &&
            payload.product.customerFacing.physicalSizeLabel
            ? payload.product.customerFacing.physicalSizeLabel
            : PRODUCTS[state.size].physicalSizeLabel;
    }
    function getPayloadMapTitleText(payload) {
        if (payload &&
            payload.mapTitle &&
            typeof payload.mapTitle.text === "string") {
            return normaliseMapTitle(payload.mapTitle.text);
        }
        if (payload &&
            payload.specialPlace &&
            typeof payload.specialPlace.label === "string") {
            return normaliseMapTitle(payload.specialPlace.label);
        }
        return "";
    }
    function getPayloadSpecialPlaceLabel(payload) {
        const mapTitle = getPayloadMapTitleText(payload);
        return mapTitle || "No map title";
    }
    function getPayloadSpecialPlaceSource(payload) {
        const mapTitle = getPayloadMapTitleText(payload);
        if (!mapTitle) {
            return "no_title";
        }
        if (payload &&
            payload.mapTitle &&
            payload.mapTitle.source) {
            return payload.mapTitle.source;
        }
        if (payload &&
            payload.specialPlace &&
            payload.specialPlace.source) {
            return payload.specialPlace.source;
        }
        return "manual_map_title";
    }
    function getAcceptedAtDisplay(acceptedAt) {
        if (!acceptedAt) {
            return null;
        }
        try {
            return new Date(acceptedAt).toLocaleString(undefined, {
                dateStyle: "medium",
                timeStyle: "short"
            });
        }
        catch (error) {
            return acceptedAt;
        }
    }
    function buildCustomerReceiptSummary(payload) {
        const mapTitleText = getPayloadMapTitleText(payload);
        const specialPlace = mapTitleText || "No map title";
        const orientation = getPayloadOrientationLabel(payload);
        const sizeName = getPayloadSizeName(payload);
        const physicalSizeLabel = getPayloadPhysicalSizeLabel(payload);
        const designReference = payload &&
            payload.review
            ? payload.review.designReference || null
            : null;
        const acceptedAt = payload &&
            payload.review
            ? payload.review.acceptedAt || null
            : null;
        return {
            title: "Your framed artwork",
            designReference,
            pageMode: payload && payload.pageMode
                ? payload.pageMode
                : buildPageModeMetadata(),
            mapTitle: {
                provided: Boolean(mapTitleText),
                text: mapTitleText,
                displayText: specialPlace,
                source: getPayloadSpecialPlaceSource(payload)
            },
            searchOrigin: {
                label: getPayloadSearchOriginLabel(payload)
            },
            specialPlace: {
                label: specialPlace,
                source: getPayloadSpecialPlaceSource(payload)
            },
            frame: {
                sizeName,
                physicalSizeLabel,
                orientation
            },
            composition: {
                status: payload && payload.review
                    ? payload.review.status
                    : state.review.status,
                acceptedAt,
                acceptedAtDisplay: getAcceptedAtDisplay(acceptedAt),
                isLocked: payload && payload.review
                    ? Boolean(payload.review.isLocked)
                    : false
            },
            summaryLine: `${specialPlace} · ${orientation} · ${sizeName}`,
            formalRecordLine: `${sizeName} frame (${physicalSizeLabel}) · ${orientation}`
        };
    }
    function buildInternalProductionSummary(payload) {
        return {
            designReference: payload &&
                payload.review
                ? payload.review.designReference || null
                : null,
            pageMode: payload && payload.pageMode
                ? payload.pageMode
                : buildPageModeMetadata(),
            mapTitle: payload && payload.mapTitle
                ? clonePlainObject(payload.mapTitle)
                : null,
            searchOrigin: payload && payload.searchOrigin
                ? clonePlainObject(payload.searchOrigin)
                : null,
            customerGuidance: payload && payload.customerGuidance
                ? clonePlainObject(payload.customerGuidance)
                : {
                    provided: false,
                    note: ""
                },
            product: {
                productKey: payload &&
                    payload.artwork
                    ? payload.artwork.size
                    : state.size,
                sizeName: getPayloadSizeName(payload),
                physicalSizeLabel: getPayloadPhysicalSizeLabel(payload),
                orientation: getPayloadOrientationLabel(payload),
                nominalDimensionsCm: payload &&
                    payload.product
                    ? payload.product.nominal
                    : null,
                orientedDimensionsCm: payload &&
                    payload.product
                    ? payload.product.orientedForCustomerSelection
                    : null,
                frameDetailsCm: payload &&
                    payload.product
                    ? payload.product.frameDetails
                    : null
            },
            mapRecreation: {
                coordinateSystem: payload
                    ? payload.coordinateSystem
                    : "WGS84 longitude/latitude",
                centre: payload && payload.map
                    ? payload.map.center
                    : null,
                zoom: payload && payload.map
                    ? payload.map.zoom
                    : null,
                mappedArtworkArea: payload &&
                    payload.geographicBounds
                    ? payload.geographicBounds.mappedArtworkArea
                    : null,
                groundDimensions: payload &&
                    payload.geographicBounds &&
                    payload.geographicBounds.mappedArtworkArea
                    ? payload.geographicBounds.mappedArtworkArea.groundDimensions || null
                    : null,
                specialPlace: payload
                    ? payload.specialPlace
                    : null,
                selectedLocation: payload
                    ? payload.selectedLocation
                    : null,
                qgisProductionHandoff: payload &&
                    payload.productionHandoff
                    ? payload.productionHandoff.qgis || null
                    : null,
                insetQgisProductionHandoff: payload &&
                    payload.productionHandoff
                    ? payload.productionHandoff.insetQgis || null
                    : null
            },
            source: payload
                ? payload.source
                : null,
            productionNotes: [
                "Recreate the accepted mapped artwork area in QGIS using OSM-derived data.",
                "Use productionHandoff.qgis.extent3857 and the mapped-artwork layout dimensions as the production source of truth.",
                "Use EPSG:4326 bounds, polygons, centre and MapLibre zoom only as backup or diagnostic information.",
                "Use groundDimensions as approximate width, height and area guidance for production planning.",
                "Customer-facing product language may differ from internal physical dimensions."
            ]
        };
    }
    function buildPayloadSummaries(payload) {
        return {
            customerReceipt: buildCustomerReceiptSummary(payload),
            internalProduction: buildInternalProductionSummary(payload)
        };
    }
    function buildProductionPayload() {
        const selectedProduct = PRODUCTS[state.size];
        if (!selectedProduct) {
            return null;
        }
        const center = map.getCenter();
        const orientedDimensions = getOrientedDimensions(selectedProduct);
        const specialPlace = buildSpecialPlaceMetadata();
        const mapTitle = buildMapTitleMetadata();
        const customerGuidanceNote = normaliseCustomerGuidance(state.customerGuidance.note);
        const mappedArtworkArea = getMappedAreaGeographicBounds();
        const mapDiagnostic = {
            center: {
                lng: center.lng,
                lat: center.lat
            },
            zoom: map.getZoom(),
            bearing: typeof map.getBearing === "function"
                ? map.getBearing()
                : 0,
            pitch: typeof map.getPitch === "function"
                ? map.getPitch()
                : 0
        };
        const qgisProductionHandoff = buildQgisProductionHandoff(mappedArtworkArea, orientedDimensions, mapDiagnostic, state.review.designReference);
        const insetPayload = buildInsetPayload();
        const insetQgisProductionHandoff = buildInsetQgisProductionHandoff(insetPayload, qgisProductionHandoff, state.review.designReference);
        const payload = {
            version: MAP_DESIGNER_RELEASE,
            capturedAt: new Date().toISOString(),
            coordinateSystem: "WGS84 longitude/latitude",
            pageMode: buildPageModeMetadata(),
            source: {
                previewMapEngine: "MapLibre GL JS",
                previewMapStyle: "Stadia Maps Alidade Smooth",
                searchProvider: "Stadia Maps",
                productionWorkflow: "QGIS exact extent using OSM-derived data",
                currentManufacturingMethod: "Layered laser-cut and laser-etched plywood"
            },
            selectedLocation: state.search.selectedLocation,
            searchOrigin: state.search.selectedLocation
                ? clonePlainObject(state.search.selectedLocation)
                : null,
            mapTitle,
            // Compatibility alias for existing production helpers. The label is
            // now the customer's Map title rather than an inferred map area.
            specialPlace,
            customerGuidance: {
                provided: Boolean(customerGuidanceNote),
                note: customerGuidanceNote
            },
            map: {
                center: {
                    lng: roundCoordinate(center.lng),
                    lat: roundCoordinate(center.lat)
                },
                zoom: roundZoom(map.getZoom())
            },
            artwork: {
                orientation: state.orientation,
                size: state.size,
                productLabel: selectedProduct.label,
                physicalSizeLabel: selectedProduct.physicalSizeLabel
            },
            product: {
                customerFacing: {
                    sizeName: selectedProduct.label,
                    physicalSizeLabel: selectedProduct.physicalSizeLabel
                },
                nominal: {
                    outerFrame: {
                        widthCm: selectedProduct.outerWidthCm,
                        heightCm: selectedProduct.outerHeightCm
                    },
                    exposedOpening: {
                        widthCm: selectedProduct.exposedWidthCm,
                        heightCm: selectedProduct.exposedHeightCm
                    },
                    artworkSheet: {
                        widthCm: selectedProduct.artworkWidthCm,
                        heightCm: selectedProduct.artworkHeightCm
                    },
                    mappedArtwork: {
                        widthCm: selectedProduct.mappedWidthCm,
                        heightCm: selectedProduct.mappedHeightCm
                    }
                },
                orientedForCustomerSelection: orientedDimensions,
                frameDetails: {
                    blackFrameCm: selectedProduct.blackFrameCm,
                    basswoodRevealCm: selectedProduct.basswoodRevealCm,
                    mountingBorderCm: selectedProduct.mountingBorderCm
                }
            },
            commerce: {
                shopify: state.page.mode === "shopify_store"
                    ? (() => {
                        const binding = updateShopifyProductBinding();
                        return {
                            status: binding.status,
                            isValid: binding.isValid,
                            checkedAt: binding.checkedAt,
                            product: binding.product,
                            variant: binding.variant,
                            selectedOptions: binding.selectedOptions
                        };
                    })()
                    : null
            },
            geographicBounds: {
                mappedArtworkArea
            },
            inset: insetPayload,
            productionHandoff: {
                qgis: qgisProductionHandoff,
                insetQgis: insetQgisProductionHandoff
            },
            review: {
                status: state.review.status,
                acceptedAt: state.review.acceptedAt,
                designReference: state.review.designReference,
                isLocked: state.review.status !== "editing",
                hasAcceptedPayload: Boolean(state.review.acceptedPayload),
                isReadyForCart: Boolean(state.review.isReadyForCart)
            }
        };
        payload.summaries =
            buildPayloadSummaries(payload);
        return payload;
    }
    function serialiseProductionPayload(payload) {
        if (!payload) {
            return "";
        }
        return JSON.stringify(payload, null, 2);
    }
    function updateProductionPayloadField(payload) {
        if (!productionPayloadField) {
            return;
        }
        productionPayloadField.value =
            serialiseProductionPayload(payload);
        productionPayloadField.dataset.payloadVersion =
            payload ? payload.version : "";
        productionPayloadField.dataset.updatedAt =
            payload ? payload.capturedAt : "";
        productionPayloadField.dataset.isValid =
            state.production.validation
                ? String(state.production.validation.isValid)
                : "";
    }
    function updateAcceptedProductionPayloadField(payload) {
        if (!acceptedProductionPayloadField) {
            return;
        }
        acceptedProductionPayloadField.value =
            serialiseProductionPayload(payload);
        acceptedProductionPayloadField.dataset.payloadVersion =
            payload ? payload.version : "";
        acceptedProductionPayloadField.dataset.acceptedAt =
            payload && payload.review
                ? payload.review.acceptedAt || ""
                : "";
        acceptedProductionPayloadField.dataset.isReadyForCart =
            payload && payload.review
                ? String(Boolean(payload.review.isReadyForCart))
                : "";
        acceptedProductionPayloadField.dataset.cartReady = "";
        acceptedProductionPayloadField.dataset.cartReadinessStatus = "";
        acceptedProductionPayloadField.dataset.cartReadinessCheckedAt = "";
    }
    function clearAcceptedProductionPayloadField() {
        updateAcceptedProductionPayloadField(null);
    }
    //--------------------------------------------------
    // Production payload validation
    // Release 0.8.3
    //--------------------------------------------------
    function isFiniteNumber(value) {
        return typeof value === "number" &&
            Number.isFinite(value);
    }
    function hasFiniteCoordinatePair(coordinates) {
        return coordinates &&
            isFiniteNumber(coordinates.lng) &&
            isFiniteNumber(coordinates.lat);
    }
    function hasValidBounds(bounds) {
        if (!bounds) {
            return false;
        }
        const requiredBounds = [
            "north",
            "south",
            "east",
            "west"
        ];
        return requiredBounds.every(key => isFiniteNumber(bounds[key])) &&
            bounds.north > bounds.south &&
            bounds.east > bounds.west;
    }
    function hasValidMappedAreaCorners(bounds) {
        if (!bounds || !bounds.corners) {
            return false;
        }
        return [
            "northWest",
            "northEast",
            "southEast",
            "southWest"
        ].every(cornerName => hasFiniteCoordinatePair(bounds.corners[cornerName]));
    }
    function hasValidPixelRect(bounds) {
        if (!bounds || !bounds.pixelRect) {
            return false;
        }
        return isFiniteNumber(bounds.pixelRect.width) &&
            isFiniteNumber(bounds.pixelRect.height) &&
            bounds.pixelRect.width > 0 &&
            bounds.pixelRect.height > 0;
    }
    function hasValidInsetQgisProductionHandoff(handoff) {
        if (!handoff || handoff.schemaVersion !== "2.0-unit-2") {
            return false;
        }
        if (handoff.workflow !== "qgis_detail_inset_exact_reconstruction") {
            return false;
        }
        const source = handoff.sourceCircle || {};
        const detail = handoff.detailCircle || {};
        const detailMap = handoff.detailMap || {};
        const connector = handoff.connector || {};
        const validArtworkPoint = point => point &&
            Number.isFinite(point.x) &&
            Number.isFinite(point.y);
        if (!validArtworkPoint(source.centerArtworkMm) ||
            !validArtworkPoint(detail.centerArtworkMm)) {
            return false;
        }
        if (!hasValidQgisExtent(detailMap.extent3857, "EPSG:3857") ||
            !hasValidQgisExtent(detailMap.extent4326, "EPSG:4326")) {
            return false;
        }
        if (!Number.isFinite(detailMap.diameterMm) ||
            detailMap.diameterMm < INSET_DETAIL_MIN_DIAMETER_MM ||
            detailMap.diameterMm > INSET_DETAIL_MAX_DIAMETER_MM) {
            return false;
        }
        if (!detail.attachment ||
            !Array.isArray(detail.attachment.edges) ||
            detail.attachment.edges.length < 1 ||
            detail.attachment.edges.length > 2) {
            return false;
        }
        if (connector.geometry !== "paired_external_tangent_bands" ||
            !Array.isArray(connector.bands) ||
            connector.bands.length !== 2) {
            return false;
        }
        return connector.bands.every(band => Array.isArray(band.pointsArtworkMm) &&
            band.pointsArtworkMm.length === 4 &&
            band.pointsArtworkMm.every(point => point &&
                Number.isFinite(point.xMm) &&
                Number.isFinite(point.yMm)));
    }
    function hasValidGroundDimensions(groundDimensions) {
        return Boolean(groundDimensions &&
            isFiniteNumber(groundDimensions.widthMeters) &&
            isFiniteNumber(groundDimensions.heightMeters) &&
            groundDimensions.widthMeters > 0 &&
            groundDimensions.heightMeters > 0 &&
            isFiniteNumber(groundDimensions.widthKilometers) &&
            isFiniteNumber(groundDimensions.heightKilometers) &&
            isFiniteNumber(groundDimensions.areaSquareMeters) &&
            isFiniteNumber(groundDimensions.areaSquareKilometers) &&
            groundDimensions.edgeMeasurementsMeters &&
            isFiniteNumber(groundDimensions.edgeMeasurementsMeters.top) &&
            isFiniteNumber(groundDimensions.edgeMeasurementsMeters.bottom) &&
            isFiniteNumber(groundDimensions.edgeMeasurementsMeters.left) &&
            isFiniteNumber(groundDimensions.edgeMeasurementsMeters.right));
    }
    function hasPositiveDimensionSet(dimensionSet) {
        if (!dimensionSet) {
            return false;
        }
        return isFiniteNumber(dimensionSet.widthCm) &&
            isFiniteNumber(dimensionSet.heightCm) &&
            dimensionSet.widthCm > 0 &&
            dimensionSet.heightCm > 0;
    }
    function validateProductionPayload(payload) {
        const errors = [];
        const warnings = [];
        if (!payload) {
            errors.push({
                code: "payload_missing",
                message: "Production payload is missing."
            });
            return {
                isValid: false,
                errors,
                warnings
            };
        }
        if (!payload.version) {
            errors.push({
                code: "version_missing",
                message: "Payload version is missing."
            });
        }
        if (!payload.capturedAt) {
            errors.push({
                code: "captured_at_missing",
                message: "Payload capture timestamp is missing."
            });
        }
        if (payload.coordinateSystem !== "WGS84 longitude/latitude") {
            errors.push({
                code: "coordinate_system_invalid",
                message: "Payload coordinate system is missing or unexpected."
            });
        }
        if (!payload.map) {
            errors.push({
                code: "map_missing",
                message: "Map data is missing."
            });
        }
        else {
            if (!hasFiniteCoordinatePair(payload.map.center)) {
                errors.push({
                    code: "map_center_invalid",
                    message: "Map centre coordinates are missing or invalid."
                });
            }
            if (!isFiniteNumber(payload.map.zoom)) {
                errors.push({
                    code: "map_zoom_invalid",
                    message: "Map zoom is missing or invalid."
                });
            }
        }
        if (!payload.artwork) {
            errors.push({
                code: "artwork_missing",
                message: "Artwork selection data is missing."
            });
        }
        else {
            if (!["portrait", "landscape"].includes(payload.artwork.orientation)) {
                errors.push({
                    code: "orientation_invalid",
                    message: "Artwork orientation is missing or invalid."
                });
            }
            if (!PRODUCTS[payload.artwork.size]) {
                errors.push({
                    code: "size_invalid",
                    message: "Artwork size is missing or unsupported."
                });
            }
            if (!payload.artwork.productLabel) {
                warnings.push({
                    code: "product_label_missing",
                    message: "Product label is missing."
                });
            }
        }
        const mappedArtworkArea = payload.geographicBounds &&
            payload.geographicBounds.mappedArtworkArea
            ? payload.geographicBounds.mappedArtworkArea
            : null;
        if (!mappedArtworkArea) {
            errors.push({
                code: "mapped_bounds_missing",
                message: "Mapped artwork geographic bounds are missing."
            });
        }
        else {
            if (!hasValidBounds(mappedArtworkArea)) {
                errors.push({
                    code: "mapped_bounds_invalid",
                    message: "Mapped artwork geographic bounds are incomplete or invalid."
                });
            }
            if (!hasValidMappedAreaCorners(mappedArtworkArea)) {
                errors.push({
                    code: "mapped_corners_invalid",
                    message: "Mapped artwork corner coordinates are missing or invalid."
                });
            }
            if (!hasValidPixelRect(mappedArtworkArea)) {
                errors.push({
                    code: "mapped_pixel_rect_invalid",
                    message: "Mapped artwork pixel rectangle is missing or invalid."
                });
            }
            if (!hasValidGroundDimensions(mappedArtworkArea.groundDimensions)) {
                errors.push({
                    code: "mapped_ground_dimensions_invalid",
                    message: "Mapped artwork ground dimensions are missing or invalid."
                });
            }
        }
        const qgisHandoff = payload.productionHandoff &&
            payload.productionHandoff.qgis
            ? payload.productionHandoff.qgis
            : null;
        if (!hasValidQgisProductionHandoff(qgisHandoff)) {
            errors.push({
                code: "qgis_production_handoff_invalid",
                message: "The exact QGIS production handoff is missing or invalid."
            });
        }
        else {
            if (qgisHandoff.extent4326 &&
                qgisHandoff.extent4326.crossesAntimeridian) {
                warnings.push({
                    code: "qgis_antimeridian_review_required",
                    message: "The accepted area crosses the antimeridian and requires manual production review."
                });
            }
            if (qgisHandoff.layoutMapItem &&
                Number.isFinite(qgisHandoff.layoutMapItem.scaleDifferencePercent) &&
                qgisHandoff.layoutMapItem.scaleDifferencePercent > 0.5) {
                warnings.push({
                    code: "qgis_scale_check_difference",
                    message: "The width and height scale checks differ by more than 0.5%. Verify the QGIS layout dimensions and extent."
                });
            }
        }
        const mapOrientationDiagnostic = qgisHandoff && qgisHandoff.mapLibreDiagnostic
            ? qgisHandoff.mapLibreDiagnostic
            : null;
        const orientationToleranceDegrees = 0.001;
        if (!mapOrientationDiagnostic ||
            !isFiniteNumber(mapOrientationDiagnostic.bearingDegrees) ||
            !isFiniteNumber(mapOrientationDiagnostic.pitchDegrees) ||
            Math.abs(mapOrientationDiagnostic.bearingDegrees) > orientationToleranceDegrees ||
            Math.abs(mapOrientationDiagnostic.pitchDegrees) > orientationToleranceDegrees) {
            errors.push({
                code: "map_orientation_not_north_up",
                message: "The map must remain north-up and flat before the design can be accepted."
            });
        }
        if (!payload.product) {
            errors.push({
                code: "product_missing",
                message: "Product dimension data is missing."
            });
        }
        else {
            const nominal = payload.product.nominal || {};
            const oriented = payload.product.orientedForCustomerSelection || {};
            [
                ["nominal_outer_frame_invalid", nominal.outerFrame],
                ["nominal_exposed_opening_invalid", nominal.exposedOpening],
                ["nominal_artwork_sheet_invalid", nominal.artworkSheet],
                ["nominal_mapped_artwork_invalid", nominal.mappedArtwork],
                ["oriented_outer_frame_invalid", oriented.outer],
                ["oriented_exposed_opening_invalid", oriented.exposedOpening],
                ["oriented_artwork_sheet_invalid", oriented.artworkSheet],
                ["oriented_mapped_artwork_invalid", oriented.mappedArtwork]
            ].forEach(([code, dimensionSet]) => {
                if (!hasPositiveDimensionSet(dimensionSet)) {
                    errors.push({
                        code,
                        message: "A required product dimension set is missing or invalid."
                    });
                }
            });
            const frameDetails = payload.product.frameDetails || {};
            [
                "blackFrameCm",
                "basswoodRevealCm",
                "mountingBorderCm"
            ].forEach(key => {
                if (!isFiniteNumber(frameDetails[key]) ||
                    frameDetails[key] < 0) {
                    errors.push({
                        code: `${key}_invalid`,
                        message: `Frame detail ${key} is missing or invalid.`
                    });
                }
            });
        }
        if (payload.inset && payload.inset.enabled) {
            const inset = payload.inset;
            const sourceCircle = inset.sourceCircle || {};
            const detailCircle = inset.detailCircle || {};
            if (!isFiniteNumber(sourceCircle.mappedDiameterMm) ||
                sourceCircle.mappedDiameterMm < INSET_SOURCE_MIN_DIAMETER_MM ||
                sourceCircle.mappedDiameterMm > INSET_SOURCE_MAX_DIAMETER_MM) {
                errors.push({
                    code: "inset_source_diameter_invalid",
                    message: "Detail inset source diameter is missing or outside the supported 11–50 mm range."
                });
            }
            if (sourceCircle.frameThicknessMm !== INSET_SOURCE_FRAME_MM) {
                errors.push({
                    code: "inset_source_frame_invalid",
                    message: "Detail inset source frame must be 1.0 mm."
                });
            }
            if (!isFiniteNumber(detailCircle.mappedDiameterMm) ||
                detailCircle.mappedDiameterMm < INSET_DETAIL_MIN_DIAMETER_MM ||
                detailCircle.mappedDiameterMm > INSET_DETAIL_MAX_DIAMETER_MM) {
                errors.push({
                    code: "inset_detail_diameter_invalid",
                    message: "Detail inset diameter is missing or outside the supported 90–115 mm range."
                });
            }
            if (detailCircle.frameThicknessMm !== INSET_DETAIL_FRAME_MM) {
                errors.push({
                    code: "inset_detail_frame_invalid",
                    message: "Detail inset frame must be 2.75 mm."
                });
            }
            if (detailCircle.structuralOverlapMm !== INSET_FRAME_OVERLAP_MM) {
                errors.push({
                    code: "inset_overlap_invalid",
                    message: `Detail inset structural overlap must be ${INSET_FRAME_OVERLAP_MM.toFixed(3)} mm.`
                });
            }
            if (!sourceCircle.centerArtworkMm ||
                !isFiniteNumber(sourceCircle.centerArtworkMm.x) ||
                !isFiniteNumber(sourceCircle.centerArtworkMm.y)) {
                errors.push({
                    code: "inset_source_position_invalid",
                    message: "Detail inset source position is missing or invalid."
                });
            }
            if (!detailCircle.centerArtworkMm ||
                !isFiniteNumber(detailCircle.centerArtworkMm.x) ||
                !isFiniteNumber(detailCircle.centerArtworkMm.y) ||
                !detailCircle.attachment ||
                !Array.isArray(detailCircle.attachment.edges) ||
                detailCircle.attachment.edges.length < 1 ||
                detailCircle.attachment.edges.length > 2) {
                errors.push({
                    code: "inset_detail_attachment_invalid",
                    message: "Detail inset must be attached to one frame edge or to two frame edges at a corner."
                });
            }
            if (!sourceCircle.geographicSelection ||
                !sourceCircle.geographicSelection.extent3857 ||
                !hasValidQgisExtent(sourceCircle.geographicSelection.extent3857, "EPSG:3857")) {
                errors.push({
                    code: "inset_geographic_selection_invalid",
                    message: "Detail inset geographic selection is missing or invalid."
                });
            }
            const insetQgisHandoff = payload.productionHandoff
                ? payload.productionHandoff.insetQgis || null
                : null;
            if (!hasValidInsetQgisProductionHandoff(insetQgisHandoff)) {
                errors.push({
                    code: "inset_qgis_production_handoff_invalid",
                    message: "The exact detail-inset QGIS production handoff is missing or invalid."
                });
            }
            if (inset.validation &&
                inset.validation.isValid === false) {
                errors.push({
                    code: "inset_geometry_invalid",
                    message: inset.validation.message ||
                        "Detail inset geometry is not valid."
                });
            }
        }
        if (!payload.selectedLocation) {
            warnings.push({
                code: "selected_location_missing",
                message: "No searched location is selected. Manual map positioning can still be used if bounds, centre and zoom are valid."
            });
        }
        else {
            if (!payload.selectedLocation.label) {
                warnings.push({
                    code: "selected_location_label_missing",
                    message: "Selected location label is missing."
                });
            }
            if (!hasFiniteCoordinatePair(payload.selectedLocation.coordinates)) {
                errors.push({
                    code: "selected_location_coordinates_invalid",
                    message: "Selected location coordinates are missing or invalid."
                });
            }
        }
        if (!payload.source) {
            warnings.push({
                code: "source_metadata_missing",
                message: "Source and production workflow metadata is missing."
            });
        }
        return {
            isValid: errors.length === 0,
            errors,
            warnings
        };
    }
    //--------------------------------------------------
    // Locked customer review preview
    // Release 0.8.4
    //--------------------------------------------------
    const previewLayerVisibility = new Map();
    const insetPreviewLayerVisibility = new Map();
    function clonePlainObject(value) {
        return JSON.parse(JSON.stringify(value));
    }
    function getOrientationLabel() {
        return state.orientation === "landscape"
            ? "Landscape"
            : "Portrait";
    }
    function getReviewLocationLabel(payload) {
        return getPayloadSpecialPlaceLabel(payload);
    }
    function updateReviewSummary(payload) {
        if (reviewLocation) {
            reviewLocation.textContent =
                getReviewLocationLabel(payload);
        }
        if (reviewDetails) {
            const productLabel = payload &&
                payload.artwork &&
                payload.artwork.productLabel
                ? payload.artwork.productLabel
                : PRODUCTS[state.size].label;
            const insetLabel = payload &&
                payload.inset &&
                payload.inset.enabled
                ? " · Detail inset"
                : "";
            reviewDetails.textContent =
                `${getOrientationLabel()} · ${productLabel}${insetLabel}`;
        }
    }
    function getReviewGuidanceNote(payload) {
        if (!payload ||
            !payload.customerGuidance ||
            !payload.customerGuidance.provided ||
            !payload.customerGuidance.note) {
            return "";
        }
        return normaliseCustomerGuidance(payload.customerGuidance.note);
    }
    function setReviewStatus(message) {
        if (!reviewStatus) {
            return;
        }
        reviewStatus.replaceChildren();
        if (message) {
            const statusMessage = document.createElement("span");
            statusMessage.className =
                "map-designer__review-status-message";
            statusMessage.textContent = message;
            reviewStatus.appendChild(statusMessage);
        }
        const reviewPayload = state.review.acceptedPayload ||
            state.review.lockedPayload;
        if (reviewPayload &&
            !getPayloadMapTitleText(reviewPayload)) {
            const titleNotice = document.createElement("span");
            titleNotice.className =
                "map-designer__review-title-notice";
            titleNotice.style.display = "block";
            titleNotice.style.marginTop = "6px";
            titleNotice.textContent =
                "No title will appear on the finished artwork.";
            reviewStatus.appendChild(titleNotice);
        }
        const guidanceNote = getReviewGuidanceNote(reviewPayload);
        if (!guidanceNote) {
            return;
        }
        const guidanceConfirmation = document.createElement("div");
        guidanceConfirmation.className =
            "map-designer__review-guidance-confirmation";
        const guidanceLabel = document.createElement("span");
        guidanceLabel.className =
            "map-designer__review-guidance-label";
        guidanceLabel.textContent = "Your note";
        const guidanceText = document.createElement("span");
        guidanceText.className =
            "map-designer__review-guidance-text";
        guidanceText.textContent = guidanceNote;
        guidanceConfirmation.appendChild(guidanceLabel);
        guidanceConfirmation.appendChild(guidanceText);
        reviewStatus.appendChild(guidanceConfirmation);
    }
    function getAcceptedPayloadValidation() {
        if (!state.review.acceptedPayload) {
            return {
                isValid: false,
                errors: [
                    {
                        code: "accepted_payload_missing",
                        message: "No accepted production payload has been stored."
                    }
                ],
                warnings: []
            };
        }
        return validateProductionPayload(state.review.acceptedPayload);
    }
    function updateReviewCartReadiness() {
        const validation = getAcceptedPayloadValidation();
        state.review.isReadyForCart =
            state.review.status === "accepted" &&
                validation.isValid;
        return {
            isReadyForCart: state.review.isReadyForCart,
            validation
        };
    }
    //--------------------------------------------------
    // Cart readiness gate
    // Release 0.8.7
    //--------------------------------------------------
    function getAcceptedProductionPayloadFieldValue() {
        if (!acceptedProductionPayloadField) {
            return "";
        }
        return acceptedProductionPayloadField.value.trim();
    }
    function getAcceptedProductionPayloadFieldPayload() {
        const fieldValue = getAcceptedProductionPayloadFieldValue();
        if (!fieldValue) {
            return null;
        }
        try {
            return JSON.parse(fieldValue);
        }
        catch (error) {
            return null;
        }
    }
    function payloadsMatch(leftPayload, rightPayload) {
        if (!leftPayload || !rightPayload) {
            return false;
        }
        return serialiseProductionPayload(leftPayload).trim() ===
            serialiseProductionPayload(rightPayload).trim();
    }
    function validateCartReadinessGate() {
        const checkedAt = new Date().toISOString();
        const errors = [];
        const warnings = [];
        const reviewReadiness = updateReviewCartReadiness();
        const pageMode = buildPageModeMetadata();
        const shopifyProductBinding = pageMode.isShopifyStoreMode
            ? updateShopifyProductBinding()
            : null;
        const acceptedFieldPayload = getAcceptedProductionPayloadFieldPayload();
        if (state.review.status !== "accepted") {
            errors.push({
                code: "review_not_accepted",
                message: "The customer has not accepted the frame preview."
            });
        }
        if (!state.review.acceptedAt) {
            errors.push({
                code: "accepted_at_missing",
                message: "Accepted timestamp is missing."
            });
        }
        if (!state.review.acceptedPayload) {
            errors.push({
                code: "accepted_payload_missing",
                message: "Accepted production payload is missing."
            });
        }
        else {
            const acceptedValidation = validateProductionPayload(state.review.acceptedPayload);
            if (!acceptedValidation.isValid) {
                errors.push({
                    code: "accepted_payload_invalid",
                    message: "Accepted production payload is not valid.",
                    details: acceptedValidation.errors
                });
            }
            if (acceptedValidation.warnings.length) {
                warnings.push({
                    code: "accepted_payload_warnings",
                    message: "Accepted production payload has non-blocking warnings.",
                    details: acceptedValidation.warnings
                });
            }
        }
        if (!acceptedProductionPayloadField) {
            errors.push({
                code: "accepted_payload_field_missing",
                message: "Accepted production payload field is missing."
            });
        }
        else if (!getAcceptedProductionPayloadFieldValue()) {
            errors.push({
                code: "accepted_payload_field_empty",
                message: "Accepted production payload field is empty."
            });
        }
        else if (!acceptedFieldPayload) {
            errors.push({
                code: "accepted_payload_field_invalid_json",
                message: "Accepted production payload field does not contain valid JSON."
            });
        }
        if (state.review.acceptedPayload &&
            acceptedFieldPayload &&
            !payloadsMatch(state.review.acceptedPayload, acceptedFieldPayload)) {
            errors.push({
                code: "accepted_payload_field_mismatch",
                message: "Accepted production payload field does not match the accepted payload in state."
            });
        }
        if (!reviewReadiness.isReadyForCart) {
            errors.push({
                code: "review_readiness_false",
                message: "Review readiness is false."
            });
        }
        if (pageMode.isShopifyStoreMode &&
            (!shopifyProductBinding || !shopifyProductBinding.isValid)) {
            errors.push({
                code: "shopify_product_binding_invalid",
                message: "The Shopify product or selected variant is not ready for cart submission.",
                details: shopifyProductBinding
                    ? shopifyProductBinding.errors
                    : []
            });
        }
        const acceptedShopifyVariantId = state.review.acceptedPayload &&
            state.review.acceptedPayload.commerce &&
            state.review.acceptedPayload.commerce.shopify &&
            state.review.acceptedPayload.commerce.shopify.variant
            ? state.review.acceptedPayload.commerce.shopify.variant.id || null
            : null;
        const resolvedShopifyVariantId = shopifyProductBinding && shopifyProductBinding.variant
            ? shopifyProductBinding.variant.id || null
            : null;
        if (pageMode.isShopifyStoreMode &&
            acceptedShopifyVariantId &&
            resolvedShopifyVariantId &&
            String(acceptedShopifyVariantId) !== String(resolvedShopifyVariantId)) {
            errors.push({
                code: "shopify_variant_changed_since_acceptance",
                message: "The Shopify variant no longer matches the accepted design."
            });
        }
        return {
            isReady: errors.length === 0,
            status: errors.length === 0 ? "ready" : "not_ready",
            checkedAt,
            pageMode,
            shopifyProductBinding,
            errors,
            warnings
        };
    }
    function updateCartReadinessGate() {
        const readiness = validateCartReadinessGate();
        state.cart.readiness = readiness;
        state.cart.lastCheckedAt = readiness.checkedAt;
        if (acceptedProductionPayloadField) {
            acceptedProductionPayloadField.dataset.cartReady =
                String(readiness.isReady);
            acceptedProductionPayloadField.dataset.cartReadinessStatus =
                readiness.status;
            acceptedProductionPayloadField.dataset.cartReadinessCheckedAt =
                readiness.checkedAt;
        }
        return readiness;
    }
    function getCartPayloadForFutureSubmission() {
        const readiness = updateCartReadinessGate();
        if (!readiness.isReady || !state.review.acceptedPayload) {
            return null;
        }
        return clonePlainObject(state.review.acceptedPayload);
    }
    //--------------------------------------------------
    // Shopify Store cart completion
    // Release 0.9.0 Unit 2
    //--------------------------------------------------
    function isShopifyStoreMode() {
        return buildPageModeMetadata().isShopifyStoreMode;
    }
    function compactJson(value) {
        if (value === undefined || value === null) {
            return "";
        }
        return JSON.stringify(value);
    }
    function fnv1a32(value) {
        let hash = 0x811c9dc5;
        for (let index = 0; index < value.length; index += 1) {
            hash ^= value.charCodeAt(index);
            hash = Math.imul(hash, 0x01000193);
        }
        return (hash >>> 0)
            .toString(16)
            .padStart(8, "0");
    }
    function splitCartPropertyValue(value, chunkLength = CART_PROPERTY_CHUNK_LENGTH) {
        const source = String(value || "");
        const chunks = [];
        for (let index = 0; index < source.length; index += chunkLength) {
            chunks.push(source.slice(index, index + chunkLength));
        }
        return chunks;
    }
    function buildCartProductionRecord(payload, binding) {
        if (!payload || !binding || !binding.variant) {
            return null;
        }
        const receipt = payload.summaries && payload.summaries.customerReceipt
            ? payload.summaries.customerReceipt
            : buildCustomerReceiptSummary(payload);
        const qgis = payload.productionHandoff &&
            payload.productionHandoff.qgis
            ? payload.productionHandoff.qgis
            : null;
        if (!hasValidQgisProductionHandoff(qgis)) {
            return null;
        }
        const insetEnabled = Boolean(payload.inset &&
            payload.inset.enabled);
        const insetQgis = insetEnabled &&
            payload.productionHandoff
            ? payload.productionHandoff.insetQgis || null
            : null;
        if (insetEnabled &&
            !hasValidInsetQgisProductionHandoff(insetQgis)) {
            return null;
        }
        const layout = qgis.layoutMapItem;
        const record = {
            schemaVersion: MAP_DESIGNER_PRODUCTION_SCHEMA,
            recordType: MAP_DESIGNER_PRODUCTION_RECORD_TYPE,
            designReference: receipt.designReference || "",
            acceptedAt: payload.review
                ? payload.review.acceptedAt || ""
                : "",
            mapTitle: {
                provided: Boolean(getPayloadMapTitleText(payload)),
                text: getPayloadMapTitleText(payload),
                source: getPayloadSpecialPlaceSource(payload)
            },
            searchOrigin: {
                label: getPayloadSearchOriginLabel(payload)
            },
            specialPlace: {
                provided: Boolean(getPayloadMapTitleText(payload)),
                label: getPayloadMapTitleText(payload),
                source: getPayloadSpecialPlaceSource(payload)
            },
            customerGuidance: payload.customerGuidance
                ? clonePlainObject(payload.customerGuidance)
                : {
                    provided: false,
                    note: ""
                },
            product: {
                productKey: payload.artwork
                    ? payload.artwork.size || ""
                    : "",
                label: receipt.frame
                    ? receipt.frame.sizeName || ""
                    : "",
                physicalSize: receipt.frame
                    ? receipt.frame.physicalSizeLabel || ""
                    : "",
                orientation: receipt.frame
                    ? receipt.frame.orientation || ""
                    : "",
                mappedWidthMm: layout.widthMm,
                mappedHeightMm: layout.heightMm
            },
            qgis: {
                sourceOfTruth: qgis.sourceOfTruth,
                crs: qgis.extent3857.crs,
                extent: qgis.extent3857.qgisInput,
                backupCrs: qgis.extent4326.crs,
                backupExtent: qgis.extent4326.qgisInput,
                rotationDegrees: layout.rotationDegrees,
                recommendedScaleDenominator: layout.recommendedScaleDenominator
            },
            source: {
                productionWorkflow: payload.source
                    ? payload.source.productionWorkflow || ""
                    : "",
                previewMapStyle: payload.source
                    ? payload.source.previewMapStyle || ""
                    : "",
                manufacturingMethod: payload.source
                    ? payload.source.currentManufacturingMethod || ""
                    : ""
            }
        };
        record.commerce = {
            pricingSource: "shopify_variant",
            productId: binding.product && binding.product.id
                ? binding.product.id
                : null,
            variantId: binding.variant.id || null,
            variantTitle: binding.variant.title || "",
            variantPrice: Number.isFinite(binding.variant.price)
                ? binding.variant.price
                : null,
            selectedOptions: binding.selectedOptions
                ? clonePlainObject(binding.selectedOptions)
                : null
        };
        if (insetEnabled) {
            record.inset = {
                schemaVersion: insetQgis.schemaVersion,
                sourceOfTruth: insetQgis.sourceOfTruth,
                artworkCoordinateSystem: clonePlainObject(insetQgis.artworkCoordinateSystem),
                sourceCircle: {
                    centerArtworkMm: clonePlainObject(insetQgis.sourceCircle.centerArtworkMm),
                    mappedDiameterMm: insetQgis.sourceCircle.mappedDiameterMm,
                    frameThicknessMm: insetQgis.sourceCircle.frameThicknessMm,
                    outerDiameterMm: insetQgis.sourceCircle.outerDiameterMm,
                    mappedBoundingBoxMm: clonePlainObject(insetQgis.sourceCircle.mappedBoundingBoxMm),
                    outerBoundingBoxMm: clonePlainObject(insetQgis.sourceCircle.outerBoundingBoxMm)
                },
                detailCircle: {
                    centerArtworkMm: clonePlainObject(insetQgis.detailCircle.centerArtworkMm),
                    mappedDiameterMm: insetQgis.detailCircle.mappedDiameterMm,
                    frameThicknessMm: insetQgis.detailCircle.frameThicknessMm,
                    outerDiameterMm: insetQgis.detailCircle.outerDiameterMm,
                    structuralOverlapMm: insetQgis.detailCircle.structuralOverlapMm,
                    attachment: clonePlainObject(insetQgis.detailCircle.attachment),
                    mappedBoundingBoxMm: clonePlainObject(insetQgis.detailCircle.mappedBoundingBoxMm),
                    outerBoundingBoxMm: clonePlainObject(insetQgis.detailCircle.outerBoundingBoxMm)
                },
                detailMap: {
                    crs: insetQgis.detailMap.extent3857.crs,
                    extent: insetQgis.detailMap.extent3857.qgisInput,
                    backupCrs: insetQgis.detailMap.extent4326.crs,
                    backupExtent: insetQgis.detailMap.extent4326.qgisInput,
                    diameterMm: insetQgis.detailMap.diameterMm,
                    recommendedScaleDenominator: insetQgis.detailMap.recommendedScaleDenominator
                },
                connector: {
                    geometry: insetQgis.connector.geometry,
                    bands: insetQgis.connector.bands.map(band => ({
                        name: band.name,
                        pointsArtworkMm: clonePlainObject(band.pointsArtworkMm)
                    }))
                }
            };
        }
        return record;
    }
    function buildProductionRecordChunkProperties(record) {
        const compactRecord = compactJson(record);
        const chunks = splitCartPropertyValue(compactRecord);
        const properties = {
            "_WW Production record format": "json-chunks-v1",
            "_WW Production record chunks": String(chunks.length),
            "_WW Production record length": String(compactRecord.length),
            "_WW Production record hash": fnv1a32(compactRecord)
        };
        chunks.forEach((chunk, index) => {
            const number = String(index + 1).padStart(2, "0");
            properties[`_WW Production record ${number}`] = chunk;
        });
        return properties;
    }
    function formatInsetArtworkPointMm(point) {
        if (!point ||
            !Number.isFinite(point.x) ||
            !Number.isFinite(point.y)) {
            return "";
        }
        return `X ${point.x.toFixed(3)} mm, Y ${point.y.toFixed(3)} mm`;
    }
    function formatInsetBoundingBoxMm(box) {
        if (!box ||
            !Number.isFinite(box.xMm) ||
            !Number.isFinite(box.yMm) ||
            !Number.isFinite(box.widthMm) ||
            !Number.isFinite(box.heightMm)) {
            return "";
        }
        return `X ${box.xMm.toFixed(3)}, Y ${box.yMm.toFixed(3)}, W ${box.widthMm.toFixed(3)}, H ${box.heightMm.toFixed(3)} mm`;
    }
    function formatInsetAttachment(attachment) {
        if (!attachment ||
            !Array.isArray(attachment.edges) ||
            !attachment.edges.length) {
            return "";
        }
        return attachment.edges.join(" + ");
    }
    function formatInsetConnectorPointMm(point) {
        if (!point ||
            !Number.isFinite(point.xMm) ||
            !Number.isFinite(point.yMm)) {
            return "";
        }
        return `X ${point.xMm.toFixed(3)} mm, Y ${point.yMm.toFixed(3)} mm`;
    }
    function formatInsetConnectorBand(band) {
        if (!band ||
            !Array.isArray(band.pointsArtworkMm) ||
            band.pointsArtworkMm.length !== 4) {
            return "";
        }
        const labels = [
            "Source outer",
            "Detail outer",
            "Detail inner",
            "Source inner"
        ];
        return band.pointsArtworkMm
            .map((point, index) => `${labels[index]}: ${formatInsetConnectorPointMm(point)}`)
            .join(" | ");
    }
    function formatInsetConnectorRadii(connector) {
        if (!connector) {
            return "";
        }
        const values = [
            ["Source inner", connector.sourceInnerRadiusMm],
            ["Source outer", connector.sourceOuterRadiusMm],
            ["Detail inner", connector.detailInnerRadiusMm],
            ["Detail outer", connector.detailOuterRadiusMm]
        ];
        if (!values.every(([, value]) => Number.isFinite(value))) {
            return "";
        }
        return values
            .map(([label, value]) => `${label}: ${Number(value).toFixed(3)} mm`)
            .join(" | ");
    }
    function buildCartLineItemProperties(payload, binding) {
        if (!payload || !binding || !binding.variant) {
            return null;
        }
        const receipt = payload.summaries && payload.summaries.customerReceipt
            ? payload.summaries.customerReceipt
            : buildCustomerReceiptSummary(payload);
        const qgis = payload.productionHandoff &&
            payload.productionHandoff.qgis
            ? payload.productionHandoff.qgis
            : null;
        const insetEnabled = Boolean(payload.inset &&
            payload.inset.enabled);
        const insetQgis = insetEnabled &&
            payload.productionHandoff
            ? payload.productionHandoff.insetQgis || null
            : null;
        const productionRecord = buildCartProductionRecord(payload, binding);
        if (!qgis ||
            !hasValidQgisProductionHandoff(qgis) ||
            !productionRecord ||
            (insetEnabled &&
                !hasValidInsetQgisProductionHandoff(insetQgis))) {
            return null;
        }
        const layout = qgis.layoutMapItem;
        const properties = {
            "Design reference": receipt.designReference || "",
            "Map title": getPayloadMapTitleText(payload) || "No title",
            "_WW Search origin": getPayloadSearchOriginLabel(payload),
            "_WW Map title source": getPayloadSpecialPlaceSource(payload),
            "_WW Physical size": receipt.frame
                ? receipt.frame.physicalSizeLabel || ""
                : "",
            "_WW Accepted at": payload.review
                ? payload.review.acceptedAt || ""
                : "",
            "_WW Production record version": productionRecord.schemaVersion,
            "_WW QGIS CRS": qgis.extent3857.crs,
            "_WW QGIS extent": qgis.extent3857.qgisInput,
            "_WW QGIS mapped size": `${layout.widthMm} × ${layout.heightMm} mm`,
            "_WW QGIS rotation": `${layout.rotationDegrees}°`,
            "_WW QGIS scale check": Number.isFinite(layout.recommendedScaleDenominator)
                ? `1:${layout.recommendedScaleDenominator}`
                : "",
            "_WW QGIS backup extent": qgis.extent4326.qgisInput
        };
        const customerGuidance = payload.customerGuidance &&
            payload.customerGuidance.provided
            ? payload.customerGuidance.note || ""
            : "";
        if (customerGuidance) {
            properties["_WW Customer guidance"] =
                customerGuidance;
        }
        if (insetEnabled) {
            const source = insetQgis.sourceCircle;
            const detail = insetQgis.detailCircle;
            const detailMap = insetQgis.detailMap;
            properties["Detail inset"] =
                "Included";
            properties["_WW Inset commerce mode"] =
                "shopify_variant_price";
            properties["_WW Inset source centre"] =
                formatInsetArtworkPointMm(source.centerArtworkMm);
            properties["_WW Inset source diameter"] =
                `${source.mappedDiameterMm} mm`;
            properties["_WW Inset source frame"] =
                `${source.frameThicknessMm} mm`;
            properties["_WW Inset source box"] =
                formatInsetBoundingBoxMm(source.mappedBoundingBoxMm);
            properties["_WW Inset source outer box"] =
                formatInsetBoundingBoxMm(source.outerBoundingBoxMm);
            properties["_WW Inset detail centre"] =
                formatInsetArtworkPointMm(detail.centerArtworkMm);
            properties["_WW Inset detail diameter"] =
                `${detail.mappedDiameterMm} mm`;
            properties["_WW Inset detail frame"] =
                `${detail.frameThicknessMm} mm`;
            properties["_WW Inset detail box"] =
                formatInsetBoundingBoxMm(detail.mappedBoundingBoxMm);
            properties["_WW Inset detail outer box"] =
                formatInsetBoundingBoxMm(detail.outerBoundingBoxMm);
            properties["_WW Inset attachment"] =
                formatInsetAttachment(detail.attachment);
            properties["_WW Inset overlap"] =
                `${Number(detail.structuralOverlapMm).toFixed(3)} mm`;
            properties["_WW Inset QGIS CRS"] =
                detailMap.extent3857.crs;
            properties["_WW Inset QGIS extent"] =
                detailMap.extent3857.qgisInput;
            properties["_WW Inset QGIS mapped size"] =
                `${detailMap.diameterMm} × ${detailMap.diameterMm} mm`;
            properties["_WW Inset QGIS scale check"] =
                Number.isFinite(detailMap.recommendedScaleDenominator)
                    ? `1:${detailMap.recommendedScaleDenominator}`
                    : "";
            properties["_WW Inset QGIS backup extent"] =
                detailMap.extent4326.qgisInput;
            const connector = insetQgis.connector || {};
            const connectorBands = Array.isArray(connector.bands)
                ? connector.bands
                : [];
            const connectorBandA = connectorBands.find(band => band && band.name === "band_a") || connectorBands[0] || null;
            const connectorBandB = connectorBands.find(band => band && band.name === "band_b") || connectorBands[1] || null;
            properties["_WW Inset connector geometry"] =
                connector.geometry || "";
            properties["_WW Inset connector radii"] =
                formatInsetConnectorRadii(connector);
            properties["_WW Inset connector band A"] =
                formatInsetConnectorBand(connectorBandA);
            properties["_WW Inset connector band B"] =
                formatInsetConnectorBand(connectorBandB);
        }
        return {
            ...properties,
            ...buildProductionRecordChunkProperties(productionRecord)
        };
    }
    function buildCartSubmission() {
        if (!isShopifyStoreMode() || isEtsyIsolatedBoundary()) {
            return null;
        }
        const readiness = updateCartReadinessGate();
        if (!readiness.isReady || !state.review.acceptedPayload) {
            return null;
        }
        const binding = readiness.shopifyProductBinding;
        const properties = buildCartLineItemProperties(state.review.acceptedPayload, binding);
        if (!binding || !binding.variant || !properties) {
            return null;
        }
        return {
            items: [
                {
                    id: Number(binding.variant.id),
                    quantity: 1,
                    properties
                }
            ]
        };
    }
    function reconstructChunkedJsonFromProperties(properties, prefix) {
        if (!properties ||
            typeof properties !== "object" ||
            !prefix) {
            return null;
        }
        const chunkCount = Number(properties[`${prefix} chunks`] || 0);
        if (!Number.isInteger(chunkCount) || chunkCount < 1) {
            return null;
        }
        let compactJsonValue = "";
        for (let index = 1; index <= chunkCount; index += 1) {
            const number = String(index).padStart(2, "0");
            const chunk = properties[`${prefix} ${number}`];
            if (typeof chunk !== "string") {
                return null;
            }
            compactJsonValue += chunk;
        }
        const expectedLength = Number(properties[`${prefix} length`] ||
            compactJsonValue.length);
        const expectedHash = String(properties[`${prefix} hash`] || "");
        if (compactJsonValue.length !== expectedLength) {
            return null;
        }
        if (expectedHash &&
            fnv1a32(compactJsonValue) !== expectedHash) {
            return null;
        }
        try {
            return JSON.parse(compactJsonValue);
        }
        catch (error) {
            return null;
        }
    }
    // Release 2.0 cart persistence stores the compact production record only.
    // The complete accepted payload remains available in browser state and is not
    // duplicated into Shopify line-item properties. This keeps cart submissions
    // compact while preserving every field required to reproduce the artwork.
    function reconstructProductionRecordFromCartProperties(properties) {
        return reconstructChunkedJsonFromProperties(properties, "_WW Production record");
    }
    function verifyCartProductionRecordIntegrity(properties = null) {
        let sourceProperties = properties;
        if (!sourceProperties || typeof sourceProperties !== "object") {
            const submission = buildCartSubmission();
            sourceProperties = submission &&
                submission.items &&
                submission.items[0]
                ? submission.items[0].properties
                : null;
        }
        if (!sourceProperties) {
            return {
                isValid: false,
                reconstructed: false,
                exactMatch: false,
                reason: "No cart line-item properties are available."
            };
        }
        const reconstructed =
            reconstructProductionRecordFromCartProperties(sourceProperties);
        const readiness = updateCartReadinessGate();
        const expectedRecord = readiness.isReady &&
            state.review.acceptedPayload
            ? buildCartProductionRecord(
                state.review.acceptedPayload,
                readiness.shopifyProductBinding
            )
            : null;
        const exactMatch = Boolean(
            reconstructed &&
            expectedRecord &&
            JSON.stringify(reconstructed) === JSON.stringify(expectedRecord)
        );
        const reconstructedJson = reconstructed
            ? compactJson(reconstructed)
            : "";
        return {
            isValid: exactMatch,
            reconstructed: Boolean(reconstructed),
            exactMatch,
            designReference: sourceProperties["Design reference"] ||
                (reconstructed ? reconstructed.designReference : "") || "",
            mapTitle: sourceProperties["Map title"] ||
                (reconstructed && reconstructed.mapTitle
                    ? reconstructed.mapTitle.text || ""
                    : ""),
            product: reconstructed && reconstructed.product
                ? reconstructed.product.label || ""
                : "",
            orientation: reconstructed && reconstructed.product
                ? reconstructed.product.orientation || ""
                : "",
            variantId: reconstructed && reconstructed.commerce
                ? reconstructed.commerce.variantId || null
                : null,
            variantTitle: reconstructed && reconstructed.commerce
                ? reconstructed.commerce.variantTitle || ""
                : "",
            productionRecordChunks: Number(
                sourceProperties["_WW Production record chunks"] || 0
            ),
            recordedLength: Number(
                sourceProperties["_WW Production record length"] || 0
            ),
            reconstructedLength: reconstructedJson.length,
            recordedHash: String(
                sourceProperties["_WW Production record hash"] || ""
            ),
            reconstructedHash: reconstructedJson
                ? fnv1a32(reconstructedJson)
                : ""
        };
    }
    function getCartAddUrl() {
        const root = window.Shopify &&
            window.Shopify.routes &&
            typeof window.Shopify.routes.root === "string"
            ? window.Shopify.routes.root
            : "/";
        return `${root}cart/add.js`;
    }
    function setCartStatus(message, type = "") {
        if (!cartStatus) {
            return;
        }
        cartStatus.textContent = message || "";
        cartStatus.classList.toggle("is-error", type === "error");
        cartStatus.classList.toggle("is-success", type === "success");
    }
    function resetCartSubmissionState() {
        state.cart.requestStatus = "idle";
        state.cart.submittedAt = null;
        state.cart.addedAt = null;
        state.cart.addedLineItem = null;
        state.cart.lastResponse = null;
        state.cart.lastError = null;
        if (mapDesignerRoot) {
            mapDesignerRoot.classList.remove("is-cart-added");
        }
    }
    function hideCartPanel() {
        if (cartPanel) {
            cartPanel.hidden = true;
        }
        if (cartLink) {
            cartLink.hidden = true;
        }
        if (cartSubmitButton) {
            cartSubmitButton.disabled = true;
            cartSubmitButton.classList.remove("is-busy");
            cartSubmitButton.textContent = "Add your frame to cart";
        }
        setCartStatus("");
    }
    function updateCartInterface() {
        const readiness = updateCartReadinessGate();
        const shouldShow = isShopifyStoreMode() &&
            state.review.status === "accepted" &&
            Boolean(state.review.acceptedPayload);
        if (!shouldShow) {
            hideCartPanel();
            return readiness;
        }
        if (cartPanel) {
            cartPanel.hidden = false;
        }
        const receipt = state.review.acceptedPayload.summaries &&
            state.review.acceptedPayload.summaries.customerReceipt
            ? state.review.acceptedPayload.summaries.customerReceipt
            : buildCustomerReceiptSummary(state.review.acceptedPayload);
        if (cartSummary) {
            cartSummary.textContent =
                receipt.summaryLine ||
                    `${getPayloadSpecialPlaceLabel(state.review.acceptedPayload)} · ${getPayloadOrientationLabel(state.review.acceptedPayload)} · ${getPayloadSizeName(state.review.acceptedPayload)}`;
        }
        if (cartReference) {
            cartReference.textContent = receipt.designReference
                ? `Design reference ${receipt.designReference}`
                : "";
        }
        if (state.cart.requestStatus === "submitting") {
            if (cartSubmitButton) {
                cartSubmitButton.disabled = true;
                cartSubmitButton.classList.add("is-busy");
                cartSubmitButton.textContent = "Adding your frame…";
            }
            if (cartLink) {
                cartLink.hidden = true;
            }
            setCartStatus("Keeping your accepted design safe while we add it to your cart.");
            return readiness;
        }
        if (state.cart.requestStatus === "added") {
            if (cartSubmitButton) {
                cartSubmitButton.disabled = true;
                cartSubmitButton.classList.remove("is-busy");
                cartSubmitButton.textContent = "Added to cart";
            }
            if (cartLink) {
                cartLink.hidden = false;
            }
            if (mapDesignerRoot) {
                mapDesignerRoot.classList.add("is-cart-added");
            }
            setCartStatus("Your framed artwork is safely in your cart.", "success");
            return readiness;
        }
        if (cartSubmitButton) {
            cartSubmitButton.classList.remove("is-busy");
            cartSubmitButton.disabled = !readiness.isReady;
            cartSubmitButton.textContent = state.cart.requestStatus === "error"
                ? "Try adding again"
                : "Add your frame to cart";
        }
        if (cartLink) {
            cartLink.hidden = true;
        }
        if (state.cart.requestStatus === "error") {
            setCartStatus(state.cart.lastError && state.cart.lastError.customerMessage
                ? state.cart.lastError.customerMessage
                : "We could not add your frame just now. Your accepted design is still here — please try again.", "error");
        }
        else if (readiness.isReady) {
            const acceptedInsetEnabled = Boolean(state.review.acceptedPayload &&
                state.review.acceptedPayload.inset &&
                state.review.acceptedPayload.inset.enabled);
            setCartStatus(acceptedInsetEnabled
                ? "Your accepted design, including the detail inset, will be added as one framed artwork."
                : "Your accepted design will be added as one framed artwork.");
        }
        else {
            setCartStatus("The accepted design needs one more check before it can be added to your cart.", "error");
        }
        return readiness;
    }
    function classifyCartError(error, statusCode) {
        if (error && error.name === "AbortError") {
            return "timeout";
        }
        if (typeof navigator !== "undefined" && !navigator.onLine) {
            return "network";
        }
        if (statusCode === 422) {
            return "unavailable";
        }
        if (statusCode === 429) {
            return "busy";
        }
        if (statusCode && statusCode >= 500) {
            return "service";
        }
        if (error instanceof TypeError) {
            return "network";
        }
        return "unknown";
    }
    function getCartErrorMessage(type) {
        if (type === "unavailable") {
            return "That frame is not available to add just now. Your accepted design is still here — please try again shortly.";
        }
        if (type === "network") {
            return "We could not reach the cart. Your accepted design is still here — reconnect and try again.";
        }
        if (type === "timeout") {
            return "The cart is taking longer than expected. Your accepted design is still here — please try again.";
        }
        if (type === "busy") {
            return "The cart is busy just now. Your accepted design is still here — please try again in a moment.";
        }
        if (type === "service") {
            return "The cart is temporarily unavailable. Your accepted design is still here — please try again shortly.";
        }
        return "We could not add your frame just now. Your accepted design is still here — please try again.";
    }
    async function parseCartResponse(response) {
        const text = await response.text();
        if (!text) {
            return null;
        }
        try {
            return JSON.parse(text);
        }
        catch (error) {
            return { description: text };
        }
    }
    async function addAcceptedDesignToCart() {
        if (!isShopifyStoreMode() || isEtsyIsolatedBoundary()) {
            return {
                requestStatus: "blocked",
                lastError: {
                    type: "channel_boundary",
                    customerMessage: "Shopify cart submission is disabled on this page."
                }
            };
        }
        if (state.cart.requestStatus === "submitting" ||
            state.cart.requestStatus === "added") {
            return clonePlainObject(state.cart);
        }
        const submission = buildCartSubmission();
        if (!submission) {
            state.cart.requestStatus = "error";
            state.cart.lastError = {
                type: "not_ready",
                customerMessage: "The accepted design needs one more check before it can be added to your cart."
            };
            updateCartInterface();
            return clonePlainObject(state.cart);
        }
        state.cart.requestStatus = "submitting";
        state.cart.submittedAt = new Date().toISOString();
        state.cart.lastError = null;
        updateCartInterface();
        const controller = typeof AbortController !== "undefined"
            ? new AbortController()
            : null;
        const timeout = window.setTimeout(() => {
            if (controller) {
                controller.abort();
            }
        }, CART_REQUEST_TIMEOUT_MS);
        let statusCode = null;
        try {
            const response = await fetch(getCartAddUrl(), {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(submission),
                signal: controller ? controller.signal : undefined
            });
            statusCode = response.status;
            const data = await parseCartResponse(response);
            if (!response.ok) {
                const requestError = new Error("Cart request failed");
                requestError.responseData = data;
                throw requestError;
            }
            const addedLineItem = data && Array.isArray(data.items)
                ? data.items[0] || null
                : data;
            state.cart.requestStatus = "added";
            state.cart.addedAt = new Date().toISOString();
            state.cart.addedLineItem = addedLineItem;
            state.cart.lastResponse = data;
            state.cart.lastError = null;
            setCompletedDesignRecord(
                buildDesignRecordSnapshot(state.review.acceptedPayload),
                "shopify"
            );
            updateCartInterface();
            document.dispatchEvent(new CustomEvent("wonderfullywood:map-designer-cart-added", {
                detail: {
                    designReference: state.review.designReference,
                    variantId: submission.items[0].id,
                    lineItem: clonePlainObject(addedLineItem),
                    response: clonePlainObject(data)
                }
            }));
            if (cartLink) {
                cartLink.focus();
            }
            return clonePlainObject(state.cart);
        }
        catch (error) {
            const errorType = classifyCartError(error, statusCode);
            state.cart.requestStatus = "error";
            state.cart.lastResponse = error && error.responseData
                ? error.responseData
                : null;
            state.cart.lastError = {
                type: errorType,
                statusCode,
                customerMessage: getCartErrorMessage(errorType),
                technicalMessage: error && error.message
                    ? error.message
                    : "Cart request failed"
            };
            updateCartInterface();
            return clonePlainObject(state.cart);
        }
        finally {
            window.clearTimeout(timeout);
        }
    }
    //--------------------------------------------------
    // Shared customer Design Record
    // Release 2.1.0 Candidate R6
    //--------------------------------------------------
    //
    // The Design Record is intentionally channel-neutral. It becomes available
    // only after the accepted composition has been persisted into the relevant
    // completion path: successful Shopify cart addition or confirmed Etsy
    // Design Capture submission. It is a record of composition/configuration,
    // never an invoice, order receipt, payment proof or finished-art proof.
    //
    // Map imagery is requested lazily from Stadia Maps' static_cacheable
    // endpoint only when the customer asks to view the record. This avoids
    // consuming static-map credits for every exploratory accepted composition.
    //--------------------------------------------------
    function buildDesignRecordSnapshot(payload) {
        if (!payload || !payload.review || !payload.review.designReference) {
            return null;
        }
        const oriented = payload.product && payload.product.orientedForCustomerSelection
            ? payload.product.orientedForCustomerSelection
            : null;
        const frameDetails = payload.product && payload.product.frameDetails
            ? payload.product.frameDetails
            : null;
        const mainQgis = payload.productionHandoff && payload.productionHandoff.qgis
            ? payload.productionHandoff.qgis
            : null;
        const insetQgis = payload.productionHandoff && payload.productionHandoff.insetQgis
            ? payload.productionHandoff.insetQgis
            : null;
        const mappedArtworkArea = payload.geographicBounds && payload.geographicBounds.mappedArtworkArea
            ? payload.geographicBounds.mappedArtworkArea
            : null;
        const livePreviewCenter = map && typeof map.getCenter === "function"
            ? map.getCenter()
            : null;
        const livePreviewZoom = map && typeof map.getZoom === "function"
            ? map.getZoom()
            : null;
        if (!payload.map || !payload.map.center || !oriented || !frameDetails) {
            return null;
        }
        return {
            schema: DESIGN_RECORD_SCHEMA,
            sourceRelease: payload.version || MAP_DESIGNER_RELEASE,
            designReference: payload.review.designReference,
            acceptedAt: payload.review.acceptedAt || payload.capturedAt || null,
            mapTitle: getPayloadMapTitleText(payload),
            frameSize: getPayloadSizeName(payload),
            physicalSize: getPayloadPhysicalSizeLabel(payload),
            orientation: getPayloadOrientationLabel(payload),
            artwork: {
                orientationKey: payload.artwork ? payload.artwork.orientation || null : null,
                sizeKey: payload.artwork ? payload.artwork.size || null : null
            },
            map: {
                center: clonePlainObject(payload.map.center),
                zoom: payload.map.zoom
            },
            previewCamera: livePreviewCenter &&
                Number.isFinite(livePreviewCenter.lng) &&
                Number.isFinite(livePreviewCenter.lat) &&
                Number.isFinite(livePreviewZoom)
                ? {
                    center: {
                        lng: livePreviewCenter.lng,
                        lat: livePreviewCenter.lat
                    },
                    zoom: livePreviewZoom
                }
                : null,
            geographicBounds: {
                mappedArtworkArea: mappedArtworkArea
                    ? clonePlainObject(mappedArtworkArea)
                    : null
            },
            productionGeometry: {
                mainExtent3857: mainQgis && mainQgis.extent3857
                    ? clonePlainObject(mainQgis.extent3857)
                    : null,
                detailExtent3857: insetQgis && insetQgis.detailMap && insetQgis.detailMap.extent3857
                    ? clonePlainObject(insetQgis.detailMap.extent3857)
                    : null
            },
            product: {
                outer: clonePlainObject(oriented.outer),
                mappedArtwork: clonePlainObject(oriented.mappedArtwork),
                frameDetails: clonePlainObject(frameDetails)
            },
            inset: payload.inset
                ? clonePlainObject(payload.inset)
                : { enabled: false },
            source: {
                previewMapEngine: payload.source ? payload.source.previewMapEngine || "MapLibre GL JS" : "MapLibre GL JS",
                previewMapStyle: payload.source ? payload.source.previewMapStyle || "Stadia Maps Alidade Smooth" : "Stadia Maps Alidade Smooth"
            }
        };
    }
    function getDesignRecordConfigurationLine(snapshot) {
        if (!snapshot) {
            return "";
        }
        return [
            snapshot.frameSize,
            snapshot.orientation,
            snapshot.inset && snapshot.inset.enabled ? "Detail inset" : "No inset"
        ].filter(Boolean).join(" · ");
    }
    function getDesignRecordCompletionCopy(channel) {
        if (channel === "etsy") {
            return "This confirms the composition recorded for matching to an Etsy order. It is not an Etsy order receipt or proof of payment; the Etsy order is the authoritative purchase record.";
        }
        return "This confirms the composition added to your Shopify cart. It is not an order receipt or proof of payment; the Shopify order confirmation becomes the authoritative purchase record after checkout.";
    }
    function getDesignRecordArtisticCopy() {
        return "This Design Record captures the map area, framing and layout you composed. Map labels, road numbers, symbols and other cartographic details are included only as reference aids to help identify the location and may not appear in the finished wooden artwork. Your finished piece is individually recreated for production, so roads, buildings, landscape features and other artistic detail may vary from this record.";
    }
    function setDesignRecordStatus(message, type = "status") {
        if (!designRecordStatus) {
            return;
        }
        designRecordStatus.textContent = message || "";
        designRecordStatus.classList.toggle("is-error", type === "error");
        designRecordStatus.classList.toggle("is-success", type === "success");
    }
    function setCompletedDesignRecord(snapshot, channel) {
        if (!snapshot || !snapshot.designReference) {
            clearCompletedDesignRecord();
            return;
        }
        state.designRecord.completionChannel = channel === "etsy" ? "etsy" : "shopify";
        state.designRecord.snapshot = clonePlainObject(snapshot);
        state.designRecord.status = "available";
        state.designRecord.lastError = null;
        state.designRecord.renderedReference = null;
        state.designRecord.renderedBlob = null;
        state.designRecord.staticRequestUrls = [];
        updateDesignRecordInterface();
    }
    function clearCompletedDesignRecord() {
        state.designRecord.completionChannel = null;
        state.designRecord.snapshot = null;
        state.designRecord.status = "unavailable";
        state.designRecord.lastError = null;
        state.designRecord.renderedReference = null;
        state.designRecord.renderedBlob = null;
        state.designRecord.staticRequestUrls = [];
        if (designRecordPanel) {
            designRecordPanel.hidden = true;
        }
        if (designRecordSheet) {
            designRecordSheet.hidden = true;
        }
        if (designRecordCanvas) {
            const context = designRecordCanvas.getContext("2d");
            if (context) {
                context.clearRect(0, 0, designRecordCanvas.width, designRecordCanvas.height);
            }
        }
        if (designRecordPrintButton) {
            designRecordPrintButton.disabled = true;
        }
        if (designRecordSaveButton) {
            designRecordSaveButton.disabled = true;
        }
        setDesignRecordStatus("");
    }
    function updateDesignRecordInterface() {
        const snapshot = state.designRecord.snapshot;
        const available = Boolean(snapshot && snapshot.designReference);
        if (designRecordPanel) {
            designRecordPanel.hidden = !available;
        }
        if (!available) {
            return;
        }
        if (designRecordIntro) {
            designRecordIntro.textContent = state.designRecord.completionChannel === "etsy"
                ? "Keep a visual record of the map area, framing and configuration you recorded before returning to Etsy."
                : "Keep a visual record of the map area, framing and configuration you added to your cart.";
        }
        if (designRecordAccessibleSummary) {
            const title = snapshot.mapTitle || "No map title";
            designRecordAccessibleSummary.textContent =
                `Design reference ${snapshot.designReference}. ${getDesignRecordConfigurationLine(snapshot)}. Map title: ${title}. ${getDesignRecordArtisticCopy()} ${getDesignRecordCompletionCopy(state.designRecord.completionChannel)}`;
        }
        if (designRecordOpenButton) {
            designRecordOpenButton.disabled = state.designRecord.status === "rendering";
            designRecordOpenButton.textContent = state.designRecord.renderedReference === snapshot.designReference
                ? "Refresh design record"
                : "View my design record";
        }
        if (designRecordPrintButton) {
            designRecordPrintButton.disabled = state.designRecord.renderedReference !== snapshot.designReference;
        }
        if (designRecordSaveButton) {
            designRecordSaveButton.disabled = state.designRecord.renderedReference !== snapshot.designReference;
        }
    }
    function inverseProjectWebMercatorPoint(x, y) {
        if (![x, y].every(Number.isFinite)) {
            return null;
        }
        const lng = x / 6378137 * 180 / Math.PI;
        const lat = (2 * Math.atan(Math.exp(y / 6378137)) - Math.PI / 2) * 180 / Math.PI;
        return {
            lng,
            lat
        };
    }
    function normaliseDesignRecordExtent3857(extent) {
        if (!extent ||
            ![extent.xmin, extent.xmax, extent.ymin, extent.ymax].every(Number.isFinite)) {
            return null;
        }
        const widthMeters = extent.xmax - extent.xmin;
        const heightMeters = extent.ymax - extent.ymin;
        if (!Number.isFinite(widthMeters) || !Number.isFinite(heightMeters) ||
            widthMeters <= 0 || heightMeters <= 0) {
            return null;
        }
        return {
            xmin: extent.xmin,
            xmax: extent.xmax,
            ymin: extent.ymin,
            ymax: extent.ymax,
            widthMeters,
            heightMeters,
            center3857: {
                x: (extent.xmin + extent.xmax) / 2,
                y: (extent.ymin + extent.ymax) / 2
            }
        };
    }
    function getDesignRecordExtentViewport(extent, targetLongSide) {
        const normalised = normaliseDesignRecordExtent3857(extent);
        if (!normalised || !Number.isFinite(targetLongSide) || targetLongSide <= 0) {
            return null;
        }
        const longSpanMeters = Math.max(normalised.widthMeters, normalised.heightMeters);
        const idealMetersPerPixel = longSpanMeters / targetLongSide;
        const idealZoom = Math.log2(
            DESIGN_RECORD_WEB_MERCATOR_WORLD_METERS /
            (DESIGN_RECORD_STATIC_WORLD_POINTS_AT_Z0 * idealMetersPerPixel)
        );
        let staticZoom = Math.max(
            0,
            Math.min(DESIGN_RECORD_STATIC_MAX_ZOOM, Math.round(idealZoom))
        );
        const cropPaddingPixels = 12;
        function calculateAtZoom(zoom) {
            const metersPerPixel =
                DESIGN_RECORD_WEB_MERCATOR_WORLD_METERS /
                (DESIGN_RECORD_STATIC_WORLD_POINTS_AT_Z0 * Math.pow(2, zoom));
            return {
                metersPerPixel,
                exactWidth: normalised.widthMeters / metersPerPixel,
                exactHeight: normalised.heightMeters / metersPerPixel
            };
        }
        let dimensions = calculateAtZoom(staticZoom);
        while (
            Math.max(
                dimensions.exactWidth + cropPaddingPixels * 2,
                dimensions.exactHeight + cropPaddingPixels * 2
            ) > DESIGN_RECORD_STATIC_MAX_REQUEST_SIDE &&
            staticZoom > 0
        ) {
            staticZoom -= 1;
            dimensions = calculateAtZoom(staticZoom);
        }
        while (
            Math.min(dimensions.exactWidth, dimensions.exactHeight) <
                DESIGN_RECORD_STATIC_MIN_REQUEST_SIDE &&
            staticZoom < DESIGN_RECORD_STATIC_MAX_ZOOM
        ) {
            const next = calculateAtZoom(staticZoom + 1);
            if (Math.max(
                next.exactWidth + cropPaddingPixels * 2,
                next.exactHeight + cropPaddingPixels * 2
            ) > DESIGN_RECORD_STATIC_MAX_REQUEST_SIDE) {
                break;
            }
            staticZoom += 1;
            dimensions = next;
        }
        const requestWidth = Math.max(
            DESIGN_RECORD_STATIC_MIN_REQUEST_SIDE,
            Math.ceil(dimensions.exactWidth + cropPaddingPixels * 2)
        );
        const requestHeight = Math.max(
            DESIGN_RECORD_STATIC_MIN_REQUEST_SIDE,
            Math.ceil(dimensions.exactHeight + cropPaddingPixels * 2)
        );
        if (requestWidth > DESIGN_RECORD_STATIC_MAX_REQUEST_SIDE ||
            requestHeight > DESIGN_RECORD_STATIC_MAX_REQUEST_SIDE) {
            return null;
        }
        const center = inverseProjectWebMercatorPoint(
            normalised.center3857.x,
            normalised.center3857.y
        );
        if (!center) {
            return null;
        }
        const cropWidth = dimensions.exactWidth;
        const cropHeight = dimensions.exactHeight;
        const cropX = (requestWidth - cropWidth) / 2;
        const cropY = (requestHeight - cropHeight) / 2;
        return {
            staticZoom,
            requestWidth,
            requestHeight,
            center,
            metersPerPixel: dimensions.metersPerPixel,
            crop: {
                x: cropX,
                y: cropY,
                width: cropWidth,
                height: cropHeight
            },
            sourceExtent3857: {
                xmin: normalised.xmin,
                xmax: normalised.xmax,
                ymin: normalised.ymin,
                ymax: normalised.ymax
            }
        };
    }
    function buildStaticMapRequestFromExtent(extent3857, targetLongSide) {
        const viewport = getDesignRecordExtentViewport(extent3857, targetLongSide);
        if (!viewport) {
            return null;
        }
        const url = new URL(DESIGN_RECORD_STATIC_ENDPOINT);
        url.searchParams.set(
            "center",
            `${viewport.center.lat},${viewport.center.lng}`
        );
        url.searchParams.set("zoom", String(viewport.staticZoom));
        url.searchParams.set(
            "size",
            `${viewport.requestWidth}x${viewport.requestHeight}`
        );
        url.searchParams.set("manual_attribution", "true");
        return {
            url: url.toString(),
            ...viewport,
            sourceOfTruth: "accepted_qgis_extent3857"
        };
    }
    function getStaticMapIntegerViewport(zoom, targetSize) {
        if (!Number.isFinite(zoom) || !Number.isFinite(targetSize) || zoom < 0 || targetSize <= 0) {
            return null;
        }
        const staticZoom = Math.ceil(zoom);
        if (staticZoom > DESIGN_RECORD_STATIC_MAX_ZOOM) {
            return null;
        }
        const zoomDelta = staticZoom - zoom;
        const compensatedSize = targetSize * Math.pow(2, zoomDelta);
        const requestSize = Math.max(64, Math.min(1024, Math.round(compensatedSize)));
        return {
            staticZoom,
            requestSize,
            targetSize,
            scaleCompensation: requestSize / targetSize
        };
    }
    function buildStaticMapRequest(center, zoom, size) {
        if (!center || !Number.isFinite(center.lat) || !Number.isFinite(center.lng)) {
            return null;
        }
        const viewport = getStaticMapIntegerViewport(zoom, size);
        if (!viewport) {
            return null;
        }
        const url = new URL(DESIGN_RECORD_STATIC_ENDPOINT);
        url.searchParams.set("center", `${center.lat},${center.lng}`);
        url.searchParams.set("zoom", String(viewport.staticZoom));
        url.searchParams.set("size", `${viewport.requestSize}x${viewport.requestSize}`);
        url.searchParams.set("manual_attribution", "true");
        return {
            url: url.toString(),
            ...viewport,
            acceptedZoom: zoom,
            sourceOfTruth: "legacy_center_zoom_fallback"
        };
    }
    function getDesignRecordStaticRequests(snapshot) {
        if (!snapshot) {
            return { eligible: false, reason: "missing_snapshot", requests: [] };
        }
        const exactMainExtent =
            snapshot.productionGeometry &&
            snapshot.productionGeometry.mainExtent3857
                ? snapshot.productionGeometry.mainExtent3857
                : null;
        let mainRequest = exactMainExtent
            ? buildStaticMapRequestFromExtent(
                exactMainExtent,
                DESIGN_RECORD_STATIC_STAGE_SIZE
            )
            : null;
        if (!mainRequest) {
            if (!snapshot.map || !Number.isFinite(snapshot.map.zoom) ||
                snapshot.map.zoom > DESIGN_RECORD_STATIC_MAX_ZOOM) {
                return {
                    eligible: false,
                    reason: "main_geometry",
                    requests: [],
                    message: "The accepted production extent needed for the saved Design Record is unavailable."
                };
            }
            mainRequest = buildStaticMapRequest(
                snapshot.map.center,
                snapshot.map.zoom,
                DESIGN_RECORD_STATIC_STAGE_SIZE
            );
        }
        if (!mainRequest) {
            return { eligible: false, reason: "main_request", requests: [] };
        }
        const requests = [{ key: "main", ...mainRequest }];
        if (snapshot.inset && snapshot.inset.enabled) {
            const exactDetailExtent =
                snapshot.productionGeometry &&
                snapshot.productionGeometry.detailExtent3857
                    ? snapshot.productionGeometry.detailExtent3857
                    : null;
            let detailRequest = exactDetailExtent
                ? buildStaticMapRequestFromExtent(
                    exactDetailExtent,
                    DESIGN_RECORD_STATIC_DETAIL_SIZE
                )
                : null;
            if (!detailRequest) {
                const source = snapshot.inset.sourceCircle;
                const detail = snapshot.inset.detailCircle;
                const geographic = source &&
                    source.geographicSelection &&
                    source.geographicSelection.bounds4326
                    ? source.geographicSelection.bounds4326
                    : null;
                if (!snapshot.map || !source || !detail || !geographic || !geographic.center ||
                    !Number.isFinite(snapshot.map.zoom) ||
                    !Number.isFinite(source.mappedDiameterMm) ||
                    !Number.isFinite(detail.mappedDiameterMm) ||
                    source.mappedDiameterMm <= 0 || detail.mappedDiameterMm <= 0) {
                    return { eligible: false, reason: "inset_geometry", requests: [] };
                }
                const detailZoom =
                    snapshot.map.zoom +
                    Math.log2(detail.mappedDiameterMm / source.mappedDiameterMm);
                if (!Number.isFinite(detailZoom) ||
                    detailZoom > DESIGN_RECORD_STATIC_MAX_ZOOM) {
                    return {
                        eligible: false,
                        reason: "detail_zoom",
                        requests: [],
                        message: `The detail inset requires saved-map zoom ${Number(detailZoom.toFixed(2))}, while the saved static-map service supports zoom up to ${DESIGN_RECORD_STATIC_MAX_ZOOM}.`
                    };
                }
                detailRequest = buildStaticMapRequest(
                    geographic.center,
                    detailZoom,
                    DESIGN_RECORD_STATIC_DETAIL_SIZE
                );
            }
            if (!detailRequest) {
                return { eligible: false, reason: "detail_request", requests: [] };
            }
            requests.push({ key: "detail", ...detailRequest });
        }
        return { eligible: true, reason: null, requests };
    }
    async function fetchDesignRecordImage(url) {
        if (state.designRecord.imageCache.has(url)) {
            return state.designRecord.imageCache.get(url);
        }
        const promise = (async () => {
            const response = await fetch(url, {
                method: "GET",
                mode: "cors",
                credentials: "omit"
            });
            if (!response.ok) {
                const error = new Error(`Static map request failed with HTTP ${response.status}`);
                error.statusCode = response.status;
                throw error;
            }
            const blob = await response.blob();
            if (typeof createImageBitmap === "function") {
                return await createImageBitmap(blob);
            }
            const objectUrl = URL.createObjectURL(blob);
            try {
                return await new Promise((resolve, reject) => {
                    const image = new Image();
                    image.onload = () => resolve(image);
                    image.onerror = () => reject(new Error("Static map image could not be decoded."));
                    image.src = objectUrl;
                });
            }
            finally {
                URL.revokeObjectURL(objectUrl);
            }
        })();
        state.designRecord.imageCache.set(url, promise);
        try {
            return await promise;
        }
        catch (error) {
            state.designRecord.imageCache.delete(url);
            throw error;
        }
    }
    function getDesignRecordProductGeometry(snapshot, maxLongSide = 500) {
        const outer = snapshot && snapshot.product ? snapshot.product.outer : null;
        const mapped = snapshot && snapshot.product ? snapshot.product.mappedArtwork : null;
        const frameDetails = snapshot && snapshot.product ? snapshot.product.frameDetails : null;
        if (!outer || !mapped || !frameDetails) {
            return null;
        }
        const longSideCm = Math.max(outer.widthCm, outer.heightCm);
        if (!Number.isFinite(longSideCm) || longSideCm <= 0) {
            return null;
        }
        const pixelsPerCm = maxLongSide / longSideCm;
        return {
            pixelsPerCm,
            pixelsPerMm: pixelsPerCm / 10,
            outerWidth: outer.widthCm * pixelsPerCm,
            outerHeight: outer.heightCm * pixelsPerCm,
            mappedWidth: mapped.widthCm * pixelsPerCm,
            mappedHeight: mapped.heightCm * pixelsPerCm,
            blackFrame: frameDetails.blackFrameCm * pixelsPerCm,
            basswoodReveal: frameDetails.basswoodRevealCm * pixelsPerCm
        };
    }
    function canvasRoundedRect(context, x, y, width, height, radius) {
        const r = Math.min(radius, width / 2, height / 2);
        context.beginPath();
        context.moveTo(x + r, y);
        context.arcTo(x + width, y, x + width, y + height, r);
        context.arcTo(x + width, y + height, x, y + height, r);
        context.arcTo(x, y + height, x, y, r);
        context.arcTo(x, y, x + width, y, r);
        context.closePath();
    }
    function wrapCanvasText(context, text, x, y, maxWidth, lineHeight, maxLines = 20) {
        const words = String(text || "").split(/\s+/).filter(Boolean);
        const lines = [];
        let line = "";
        words.forEach(word => {
            const test = line ? `${line} ${word}` : word;
            if (line && context.measureText(test).width > maxWidth) {
                lines.push(line);
                line = word;
            }
            else {
                line = test;
            }
        });
        if (line) {
            lines.push(line);
        }
        const visible = lines.slice(0, maxLines);
        visible.forEach((value, index) => context.fillText(value, x, y + index * lineHeight));
        return {
            lines: visible,
            nextY: y + visible.length * lineHeight
        };
    }
    function drawDesignRecordMapImage(context, asset, destinationX, destinationY, destinationWidth, destinationHeight) {
        if (!asset) {
            return false;
        }
        const image = asset.image || asset;
        const crop = asset.request && asset.request.crop
            ? asset.request.crop
            : null;
        if (!image) {
            return false;
        }
        if (crop &&
            [crop.x, crop.y, crop.width, crop.height].every(Number.isFinite) &&
            crop.width > 0 && crop.height > 0) {
            context.drawImage(
                image,
                crop.x,
                crop.y,
                crop.width,
                crop.height,
                destinationX,
                destinationY,
                destinationWidth,
                destinationHeight
            );
            return true;
        }
        context.drawImage(
            image,
            destinationX,
            destinationY,
            destinationWidth,
            destinationHeight
        );
        return true;
    }
    function drawDesignRecordConnectorBand(context, sourceCenter, sourceInnerRadius, sourceOuterRadius, detailCenter, detailInnerRadius, detailOuterRadius, side) {
        const outer = getExternalTangentPoints(sourceCenter, sourceOuterRadius, detailCenter, detailOuterRadius, side);
        const inner = getExternalTangentPoints(sourceCenter, sourceInnerRadius, detailCenter, detailInnerRadius, side);
        if (!outer || !inner) {
            return;
        }
        context.beginPath();
        context.moveTo(outer.a.x, outer.a.y);
        context.lineTo(outer.b.x, outer.b.y);
        context.lineTo(inner.b.x, inner.b.y);
        context.lineTo(inner.a.x, inner.a.y);
        context.closePath();
        context.fillStyle = DESIGN_RECORD_BASSWOOD;
        context.fill();
        context.strokeStyle = "rgba(70,60,42,.28)";
        context.lineWidth = 1;
        context.stroke();
    }
    function drawDesignRecordComposition(context, snapshot, images, x, y) {
        const geometry = getDesignRecordProductGeometry(snapshot, 500);
        if (!geometry || !images.main) {
            return null;
        }
        const outerX = x + (500 - geometry.outerWidth) / 2;
        const outerY = y;
        const mappedX = outerX + geometry.blackFrame + geometry.basswoodReveal;
        const mappedY = outerY + geometry.blackFrame + geometry.basswoodReveal;
        context.save();
        context.shadowColor = "rgba(0,0,0,.10)";
        context.shadowBlur = 18;
        context.shadowOffsetY = 7;
        context.fillStyle = DESIGN_RECORD_BLACK_FRAME;
        context.fillRect(outerX, outerY, geometry.outerWidth, geometry.outerHeight);
        context.restore();

        context.fillStyle = DESIGN_RECORD_BASSWOOD;
        context.fillRect(
            outerX + geometry.blackFrame,
            outerY + geometry.blackFrame,
            geometry.outerWidth - geometry.blackFrame * 2,
            geometry.outerHeight - geometry.blackFrame * 2
        );
        drawDesignRecordMapImage(
            context,
            images.main,
            mappedX,
            mappedY,
            geometry.mappedWidth,
            geometry.mappedHeight
        );

        if (snapshot.inset && snapshot.inset.enabled && images.detail) {
            const source = snapshot.inset.sourceCircle;
            const detail = snapshot.inset.detailCircle;
            const sourceCenter = {
                x: mappedX + source.centerArtworkMm.x * geometry.pixelsPerMm,
                y: mappedY + source.centerArtworkMm.y * geometry.pixelsPerMm
            };
            const detailCenter = {
                x: mappedX + detail.centerArtworkMm.x * geometry.pixelsPerMm,
                y: mappedY + detail.centerArtworkMm.y * geometry.pixelsPerMm
            };
            const sourceInnerRadius = source.mappedDiameterMm / 2 * geometry.pixelsPerMm;
            const sourceOuterRadius = (source.mappedDiameterMm / 2 + source.frameThicknessMm) * geometry.pixelsPerMm;
            const detailInnerRadius = detail.mappedDiameterMm / 2 * geometry.pixelsPerMm;
            const detailOuterRadius = (detail.mappedDiameterMm / 2 + detail.frameThicknessMm) * geometry.pixelsPerMm;

            drawDesignRecordConnectorBand(context, sourceCenter, sourceInnerRadius, sourceOuterRadius, detailCenter, detailInnerRadius, detailOuterRadius, -1);
            drawDesignRecordConnectorBand(context, sourceCenter, sourceInnerRadius, sourceOuterRadius, detailCenter, detailInnerRadius, detailOuterRadius, 1);

            context.save();
            context.beginPath();
            context.arc(sourceCenter.x, sourceCenter.y, sourceOuterRadius, 0, Math.PI * 2);
            context.strokeStyle = DESIGN_RECORD_BASSWOOD;
            context.lineWidth = Math.max(2, source.frameThicknessMm * geometry.pixelsPerMm * 2);
            context.stroke();
            context.beginPath();
            context.arc(sourceCenter.x, sourceCenter.y, sourceInnerRadius - 1, 0, Math.PI * 2);
            context.setLineDash([5, 4]);
            context.strokeStyle = "rgba(34,34,34,.72)";
            context.lineWidth = 1;
            context.stroke();
            context.restore();

            context.save();
            context.beginPath();
            context.arc(detailCenter.x, detailCenter.y, detailInnerRadius, 0, Math.PI * 2);
            context.clip();
            drawDesignRecordMapImage(
                context,
                images.detail,
                detailCenter.x - detailInnerRadius,
                detailCenter.y - detailInnerRadius,
                detailInnerRadius * 2,
                detailInnerRadius * 2
            );
            context.restore();
            context.save();
            context.beginPath();
            context.arc(detailCenter.x, detailCenter.y, detailOuterRadius, 0, Math.PI * 2);
            context.strokeStyle = DESIGN_RECORD_BASSWOOD;
            context.lineWidth = Math.max(2, detail.frameThicknessMm * geometry.pixelsPerMm * 2);
            context.stroke();
            context.strokeStyle = "rgba(60,49,30,.45)";
            context.lineWidth = 1;
            context.stroke();
            context.restore();
        }

        return {
            x: outerX,
            y: outerY,
            width: geometry.outerWidth,
            height: geometry.outerHeight,
            bottom: outerY + geometry.outerHeight
        };
    }
    function formatDesignRecordDate(value) {
        if (!value) {
            return "";
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return String(value);
        }
        try {
            return new Intl.DateTimeFormat(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            }).format(date);
        }
        catch (error) {
            return date.toISOString();
        }
    }
    function drawDesignRecordCanvas(snapshot, images) {
        if (!designRecordCanvas) {
            return null;
        }
        const context = designRecordCanvas.getContext("2d");
        if (!context) {
            return null;
        }
        const compositionGeometry = getDesignRecordProductGeometry(snapshot, DESIGN_RECORD_COMPOSITION_LONG_SIDE);
        if (!compositionGeometry) {
            return null;
        }
        const compositionHeight = compositionGeometry.outerHeight;
        const canvasHeight = Math.ceil(230 + compositionHeight + 445);
        designRecordCanvas.width = DESIGN_RECORD_CANVAS_WIDTH;
        designRecordCanvas.height = canvasHeight;
        context.fillStyle = DESIGN_RECORD_BACKGROUND;
        context.fillRect(0, 0, designRecordCanvas.width, designRecordCanvas.height);
        context.textBaseline = "top";
        context.fillStyle = DESIGN_RECORD_TEXT;
        context.font = "600 30px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
        context.fillText("Your Map Design Record", 48, 42);
        context.font = "500 15px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
        context.fillStyle = DESIGN_RECORD_MUTED_TEXT;
        context.fillText("Captured composition — a reference to the map area, framing and configuration you submitted", 48, 86);
        context.strokeStyle = "rgba(34,34,34,.14)";
        context.beginPath();
        context.moveTo(48, 120);
        context.lineTo(designRecordCanvas.width - 48, 120);
        context.stroke();

        const compositionX = (designRecordCanvas.width - DESIGN_RECORD_COMPOSITION_LONG_SIDE) / 2;
        const composition = drawDesignRecordComposition(context, snapshot, images, compositionX, 154);
        if (!composition) {
            return null;
        }
        let cursorY = composition.bottom + 42;
        context.fillStyle = DESIGN_RECORD_TEXT;
        context.font = "600 19px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
        context.fillText(snapshot.mapTitle || "Your map composition", 48, cursorY);
        cursorY += 34;
        context.font = "500 16px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
        context.fillText(getDesignRecordConfigurationLine(snapshot), 48, cursorY);
        cursorY += 30;
        context.font = "500 14px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
        context.font = "600 13px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
        context.fillStyle = DESIGN_RECORD_MUTED_TEXT;
        context.fillText("Design reference", 48, cursorY);
        cursorY += 23;
        context.font = "600 17px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
        context.fillStyle = DESIGN_RECORD_TEXT;
        context.fillText(snapshot.designReference, 48, cursorY);
        cursorY += 30;
        context.font = "400 14px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
        context.fillStyle = DESIGN_RECORD_MUTED_TEXT;
        context.fillText(`Recorded: ${formatDesignRecordDate(snapshot.acceptedAt)}`, 48, cursorY);
        cursorY += 42;

        context.fillStyle = DESIGN_RECORD_TEXT;
        context.font = "600 14px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
        context.fillText("What this record shows", 48, cursorY);
        cursorY += 25;
        context.font = "400 14px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
        context.fillStyle = DESIGN_RECORD_MUTED_TEXT;
        cursorY = wrapCanvasText(
            context,
            getDesignRecordArtisticCopy(),
            48,
            cursorY,
            designRecordCanvas.width - 96,
            21,
            5
        ).nextY + 14;
        cursorY = wrapCanvasText(
            context,
            getDesignRecordCompletionCopy(state.designRecord.completionChannel),
            48,
            cursorY,
            designRecordCanvas.width - 96,
            21,
            5
        ).nextY + 28;

        context.strokeStyle = "rgba(34,34,34,.14)";
        context.beginPath();
        context.moveTo(48, cursorY);
        context.lineTo(designRecordCanvas.width - 48, cursorY);
        context.stroke();
        cursorY += 18;
        context.fillStyle = DESIGN_RECORD_MUTED_TEXT;
        context.font = "400 10px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
        wrapCanvasText(
            context,
            "Map preview © Stadia Maps — stadiamaps.com/attribution/ · © OpenMapTiles — openmaptiles.org · © OpenStreetMap — openstreetmap.org/copyright",
            48,
            cursorY,
            designRecordCanvas.width - 96,
            15,
            3
        );
        return designRecordCanvas;
    }
    function designRecordCanvasToBlob() {
        if (!designRecordCanvas) {
            return Promise.resolve(null);
        }
        return new Promise(resolve => {
            if (typeof designRecordCanvas.toBlob === "function") {
                designRecordCanvas.toBlob(blob => resolve(blob), "image/png");
                return;
            }
            try {
                const dataUrl = designRecordCanvas.toDataURL("image/png");
                const parts = dataUrl.split(",");
                const bytes = atob(parts[1]);
                const array = new Uint8Array(bytes.length);
                for (let index = 0; index < bytes.length; index += 1) {
                    array[index] = bytes.charCodeAt(index);
                }
                resolve(new Blob([array], { type: "image/png" }));
            }
            catch (error) {
                resolve(null);
            }
        });
    }
    async function renderDesignRecord() {
        const snapshot = state.designRecord.snapshot;
        if (!snapshot || state.designRecord.status === "rendering") {
            return null;
        }
        const requestPlan = getDesignRecordStaticRequests(snapshot);
        if (!requestPlan.eligible) {
            state.designRecord.status = "error";
            state.designRecord.lastError = requestPlan;
            setDesignRecordStatus(
                requestPlan.message || "This composition cannot currently be reproduced as an exact saved Design Record.",
                "error"
            );
            updateDesignRecordInterface();
            return null;
        }
        state.designRecord.status = "rendering";
        state.designRecord.lastError = null;
        state.designRecord.staticRequestUrls = requestPlan.requests.map(item => item.url);
        updateDesignRecordInterface();
        setDesignRecordStatus("Creating your saved composition…");
        try {
            const imageEntries = await Promise.all(
                requestPlan.requests.map(async request => [
                    request.key,
                    {
                        image: await fetchDesignRecordImage(request.url),
                        request
                    }
                ])
            );
            const images = Object.fromEntries(imageEntries);
            const rendered = drawDesignRecordCanvas(snapshot, images);
            if (!rendered) {
                throw new Error("The Design Record could not be drawn.");
            }
            const blob = await designRecordCanvasToBlob();
            if (!blob) {
                throw new Error("The Design Record image could not be created.");
            }
            state.designRecord.renderedBlob = blob;
            state.designRecord.renderedReference = snapshot.designReference;
            state.designRecord.status = "ready";
            if (designRecordSheet) {
                designRecordSheet.hidden = false;
            }
            setDesignRecordStatus("Your Design Record is ready. You can print it, save it as a PDF through your print dialog, or save/share the PNG copy.", "success");
            updateDesignRecordInterface();
            return blob;
        }
        catch (error) {
            state.designRecord.status = "error";
            state.designRecord.lastError = {
                message: error && error.message ? error.message : "Design Record rendering failed."
            };
            setDesignRecordStatus("We could not create the saved Design Record just now. Your accepted design and reference are still safe; you can try again.", "error");
            updateDesignRecordInterface();
            return null;
        }
    }
    function getDesignRecordFilename(snapshot) {
        const reference = snapshot && snapshot.designReference
            ? snapshot.designReference.replace(/[^A-Za-z0-9_-]+/g, "-")
            : "WWS-Map-Design";
        return `${reference}-Map-Design-Record.png`;
    }
    async function saveOrShareDesignRecord() {
        const snapshot = state.designRecord.snapshot;
        if (!snapshot) {
            return;
        }
        let blob = state.designRecord.renderedBlob;
        if (!blob || state.designRecord.renderedReference !== snapshot.designReference) {
            blob = await renderDesignRecord();
        }
        if (!blob) {
            return;
        }
        const filename = getDesignRecordFilename(snapshot);
        const file = typeof File === "function"
            ? new File([blob], filename, { type: "image/png" })
            : null;
        if (file && navigator.share && navigator.canShare) {
            try {
                if (navigator.canShare({ files: [file] })) {
                    await navigator.share({
                        files: [file],
                        title: "Wonderful Walls Studio Map Design Record",
                        text: `Design reference ${snapshot.designReference}`
                    });
                    return;
                }
            }
            catch (error) {
                if (error && error.name === "AbortError") {
                    return;
                }
            }
        }
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        anchor.rel = "noopener";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
    async function printDesignRecord() {
        const snapshot = state.designRecord.snapshot;
        if (!snapshot || !designRecordCanvas) {
            return;
        }
        if (state.designRecord.renderedReference !== snapshot.designReference) {
            const blob = await renderDesignRecord();
            if (!blob) {
                return;
            }
        }
        let dataUrl = "";
        try {
            dataUrl = designRecordCanvas.toDataURL("image/png");
        }
        catch (error) {
            setDesignRecordStatus("The print view could not be prepared. Try saving the PNG copy instead.", "error");
            return;
        }
        const printWindow = window.open("", "_blank");
        if (!printWindow) {
            setDesignRecordStatus("Your browser blocked the print window. Allow pop-ups for this page or save the PNG copy instead.", "error");
            return;
        }
        try {
            printWindow.opener = null;
        }
        catch (error) {
            // Non-critical browser hardening only.
        }
        const filename = getDesignRecordFilename(snapshot);
        printWindow.document.open();
        printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${snapshot.designReference} — Map Design Record</title><style>
            html,body{margin:0;background:#f6f4ef;color:#222;font-family:Arial,sans-serif}
            .record-toolbar{position:sticky;top:0;z-index:2;display:flex;gap:10px;align-items:center;justify-content:center;flex-wrap:wrap;padding:12px;background:#fff;border-bottom:1px solid #ddd}
            .record-toolbar button,.record-toolbar a{appearance:none;border:1px solid #222;border-radius:999px;background:#fff;color:#222;padding:9px 16px;font:600 14px/1 Arial,sans-serif;text-decoration:none;cursor:pointer}
            .record-toolbar .primary{background:#222;color:#fff}
            .record-note{font-size:12px;color:#666}
            .record-body{display:flex;justify-content:center;padding:20px}
            img{display:block;max-width:100%;height:auto;background:#fff;box-shadow:0 8px 28px rgba(0,0,0,.08)}
            @media print{
                .record-toolbar{display:none!important}
                html,body{background:#fff}
                .record-body{padding:0}
                img{width:100%;max-width:190mm;margin:auto;box-shadow:none}
            }
        </style></head><body>
            <div class="record-toolbar">
                <button class="primary" type="button" onclick="window.print()">Print / Save as PDF</button>
                <a href="${dataUrl}" download="${filename}">Save PNG</a>
                <button type="button" onclick="window.close()">Close design record</button>
                <span class="record-note">Your Map Designer remains open in the previous tab.</span>
            </div>
            <div class="record-body"><img alt="Wonderful Walls Studio Map Design Record" src="${dataUrl}"></div>
        </body></html>`);
        printWindow.document.close();
        const image = printWindow.document.querySelector("img");
        const triggerPrint = () => {
            try {
                printWindow.focus();
                printWindow.print();
            }
            catch (error) {
                // The customer can still use the visible print control in the opened record window.
            }
        };
        if (image && !image.complete) {
            image.addEventListener("load", triggerPrint, { once: true });
        }
        else {
            window.setTimeout(triggerPrint, 100);
        }
    }
    //--------------------------------------------------
    // Design Capture contact form
    // Release 0.8.12 / 0.8.14 / 0.8.15b / 0.8.18
    //--------------------------------------------------
    //
    // 0.8.12 introduced the Shopify contact-form proof of concept.
    // 0.8.14 added a plain-text production summary for email readability.
    // 0.8.15b added the guarded submission confirmation flow.
    // 0.8.18 adds a guarded Etsy return action after confirmed submission.
    //
    // Important:
    // Shopify may leave form.posted_successfully? rendered in the DOM
    // after a contact-form submission. The Liquid success message is
    // hidden by default, and JavaScript reveals it only when sessionStorage
    // contains a pending Design Capture submission confirmation.
    //
    // The Etsy return action is shown only after confirmed submission,
    // and only uses safe HTTPS etsy.com or *.etsy.com URLs. If no safe
    // return URL exists, the customer receives manual-return instructions.
    //--------------------------------------------------
    /*
    The submission storage key is used only to bridge Shopify's
    contact-form page reload. It is not the production source of truth.

    Before submission:
    - storeDesignCaptureSubmission() writes a pending confirmation.

    After Shopify reload success:
    - hasDesignCaptureSubmissionSuccess() checks for the pending confirmation.
    - showDesignCaptureSubmissionSuccess() displays the Thank you message.
    - markDesignCaptureSubmissionConfirmationShown() consumes the pending state.

    Release 0.8.18 also stores a safe Etsy return URL with the pending
    confirmation when one is available.
    */
    const DESIGN_CAPTURE_SUBMISSION_STORAGE_KEY = "wonderfullyWoodMapDesigner.designCaptureSubmission";
    let designCaptureSubmissionConfirmationShown = false;
    let designCaptureConfirmedViewLocked = false;
    const reviewPreviewDefaultLabel = reviewPreviewButton
        ? reviewPreviewButton.textContent.trim() || "Review your composition"
        : "Review your composition";
    function isDesignCaptureMode() {
        return buildPageModeMetadata().isDesignCaptureMode;
    }
    function hasDesignCaptureSubmissionSuccess() {
        if (!designCaptureSuccessMessage) {
            return false;
        }
        if (designCaptureSubmissionConfirmationShown) {
            return true;
        }
        const storedSubmission = getStoredDesignCaptureSubmission();
        return Boolean(storedSubmission &&
            storedSubmission.pendingConfirmation === true &&
            storedSubmission.designReference);
    }
    function getDesignCapturePayloadDesignReference(payload) {
        return payload &&
            payload.review &&
            payload.review.designReference
            ? payload.review.designReference
            : "";
    }
    function setDesignCaptureReferencePanel(designReference) {
        if (!designCaptureReferencePanel) {
            return;
        }
        if (!designReference) {
            designCaptureReferencePanel.hidden = true;
            if (designCaptureVisibleReference) {
                designCaptureVisibleReference.textContent = "";
            }
            return;
        }
        designCaptureReferencePanel.hidden = false;
        if (designCaptureVisibleReference) {
            designCaptureVisibleReference.textContent = designReference;
        }
    }
    function setDesignCaptureSuccessMessage(message, isVisible = false) {
        if (!designCaptureSuccessMessage) {
            return;
        }
        designCaptureSuccessMessage.hidden = !isVisible;
        if (message !== undefined) {
            designCaptureSuccessMessage.textContent = message || "";
        }
    }
    const DESIGN_CAPTURE_RETURN_URL_PARAM = "ww_return_url";
    function isSafeEtsyReturnUrl(value) {
        if (!value) {
            return false;
        }
        try {
            const url = new URL(value, window.location.href);
            return url.protocol === "https:" &&
                (url.hostname === "etsy.com" ||
                    url.hostname.endsWith(".etsy.com"));
        }
        catch (error) {
            return false;
        }
    }
    function getSafeEtsyReturnUrl(value) {
        if (!isSafeEtsyReturnUrl(value)) {
            return "";
        }
        return new URL(value, window.location.href).href;
    }
    function getDesignCaptureReturnUrlFromQuery() {
        try {
            const params = new URLSearchParams(window.location.search);
            return getSafeEtsyReturnUrl(params.get(DESIGN_CAPTURE_RETURN_URL_PARAM));
        }
        catch (error) {
            return "";
        }
    }
    function getDesignCaptureReturnUrlFromReferrer() {
        return getSafeEtsyReturnUrl(document.referrer);
    }
    function getDesignCaptureFallbackReturnUrl() {
        if (!mapDesignerRoot) {
            return "";
        }
        return getSafeEtsyReturnUrl(mapDesignerRoot.dataset.designCaptureReturnUrl);
    }
    function getBestDesignCaptureReturnUrl() {
        return getDesignCaptureReturnUrlFromQuery() ||
            getDesignCaptureReturnUrlFromReferrer() ||
            getDesignCaptureFallbackReturnUrl();
    }
    function setDesignCaptureReturnAction(returnUrl, isVisible = false, designReference = "") {
        const safeReturnUrl = getSafeEtsyReturnUrl(returnUrl);
        const shouldShowLink = Boolean(isVisible && safeReturnUrl);
        const shouldShowStatus = Boolean(isVisible);
        if (designCaptureReturnLink) {
            designCaptureReturnLink.hidden =
                !shouldShowLink;
            if (shouldShowLink) {
                designCaptureReturnLink.href =
                    safeReturnUrl;
            }
            else {
                designCaptureReturnLink.removeAttribute("href");
            }
        }
        if (designCaptureReturnStatus) {
            designCaptureReturnStatus.hidden =
                !shouldShowStatus;
            if (!shouldShowStatus) {
                designCaptureReturnStatus.textContent = "";
            }
            else if (shouldShowLink) {
                designCaptureReturnStatus.textContent =
                    "Your design has been recorded. Return to Etsy to continue browsing or place your order when you are ready.";
            }
            else {
                designCaptureReturnStatus.textContent =
                    designReference
                        ? `Your design reference ${designReference} has been recorded. Return to Etsy and enter this reference in the Design reference field when you order.`
                        : "Your design reference has been recorded. Return to Etsy and enter it in the Design reference field when you order.";
            }
        }
    }
    function getStoredDesignCaptureSubmission() {
        if (!window.sessionStorage ||
            typeof window.sessionStorage.getItem !== "function") {
            return null;
        }
        try {
            const storedValue = window.sessionStorage.getItem(DESIGN_CAPTURE_SUBMISSION_STORAGE_KEY);
            return storedValue
                ? JSON.parse(storedValue)
                : null;
        }
        catch (error) {
            return null;
        }
    }
    function storeDesignCaptureSubmission(payload) {
        if (!payload ||
            !window.sessionStorage ||
            typeof window.sessionStorage.setItem !== "function") {
            return;
        }
        const submissionRecord = {
            designReference: getDesignCapturePayloadDesignReference(payload),
            submittedAt: new Date().toISOString(),
            pageMode: payload.pageMode || null,
            etsyConfiguration: buildDesignCaptureEtsyConfiguration(payload),
            designRecordSnapshot: buildDesignRecordSnapshot(payload),
            pendingConfirmation: true,
            returnUrl: getBestDesignCaptureReturnUrl()
        };
        try {
            window.sessionStorage.setItem(DESIGN_CAPTURE_SUBMISSION_STORAGE_KEY, JSON.stringify(submissionRecord));
        }
        catch (error) {
            // Non-critical. The form can still submit without browser storage.
        }
    }
    function resetDesignCaptureSubmissionConfirmationForNewDesign() {
        if (!isDesignCaptureMode() || !hasDesignCaptureSubmissionSuccess()) {
            return false;
        }

        designCaptureSubmissionConfirmationShown = false;

        if (
            window.sessionStorage &&
            typeof window.sessionStorage.removeItem === "function"
        ) {
            try {
                window.sessionStorage.removeItem(
                    DESIGN_CAPTURE_SUBMISSION_STORAGE_KEY
                );
            }
            catch (error) {
                // Non-critical. The active page state is still reset below.
            }
        }

        hideDesignCapturePanel();
        clearCompletedDesignRecord();
        return true;
    }

    function markDesignCaptureSubmissionConfirmationShown(storedSubmission) {
        designCaptureSubmissionConfirmationShown = true;
        if (!storedSubmission ||
            !window.sessionStorage ||
            typeof window.sessionStorage.setItem !== "function") {
            return;
        }
        const updatedSubmission = {
            ...storedSubmission,
            pendingConfirmation: false,
            confirmedAt: new Date().toISOString()
        };
        try {
            window.sessionStorage.setItem(DESIGN_CAPTURE_SUBMISSION_STORAGE_KEY, JSON.stringify(updatedSubmission));
        }
        catch (error) {
            // Non-critical. Confirmation display has already happened.
        }
    }
    function findSelectionButton(buttons, dataKey, value) {
        return Array.from(buttons || []).find(button =>
            String(button.dataset[dataKey] || "") === String(value || "")
        ) || null;
    }
    function setDisabledForDesignCaptureCollection(collection, isDisabled) {
        Array.from(collection || []).forEach(element => {
            if (element && "disabled" in element) {
                element.disabled = Boolean(isDisabled);
            }
        });
    }
    function setDesignCaptureConfirmedViewLocked(isLocked) {
        if (!isDesignCaptureMode()) {
            return;
        }
        designCaptureConfirmedViewLocked = Boolean(isLocked);
        setDisabledForDesignCaptureCollection(orientationButtons, isLocked);
        setDisabledForDesignCaptureCollection(sizeButtons, isLocked);
        setDisabledForDesignCaptureCollection(insetToggleButtons, isLocked);
        setDisabledForDesignCaptureCollection(insetDiameterStepButtons, isLocked);
        setDisabledForDesignCaptureCollection(insetPrecisionTargetButtons, isLocked);
        setDisabledForDesignCaptureCollection(insetSourceNudgeButtons, isLocked);
        setDisabledForDesignCaptureCollection(insetDetailNudgeButtons, isLocked);
        [
            insetSourceDiameterInput,
            insetDetailDiameterInput,
            mapTitleInput,
            searchInput,
            searchButton,
            zoomInButton,
            zoomOutButton,
            assistedChangeOptionsButton,
            assistedReturnToMapButton,
            inspectionStartButton,
            inspectionZoomOutButton,
            inspectionZoomInButton,
            inspectionFullButton
        ].forEach(element => {
            if (element && "disabled" in element) {
                element.disabled = Boolean(isLocked);
            }
        });
        if (insetLayer) {
            insetLayer.style.pointerEvents = isLocked ? "none" : "";
        }
        setMapInteractionsEnabled(!isLocked);
        if (reviewPreviewButton) {
            reviewPreviewButton.disabled = false;
            reviewPreviewButton.textContent = isLocked
                ? "Start another design"
                : reviewPreviewDefaultLabel;
        }
    }
    function startNewDesignCaptureFromConfirmedView() {
        if (!designCaptureConfirmedViewLocked || !isDesignCaptureMode()) {
            return false;
        }
        resetDesignCaptureSubmissionConfirmationForNewDesign();
        setDesignCaptureConfirmedViewLocked(false);
        state.review.status = "editing";
        state.review.lockedPayload = null;
        state.review.acceptedPayload = null;
        state.review.acceptedAt = null;
        state.review.designReference = null;
        state.review.isReadyForCart = false;
        restoreMapLabelLayers();
        if (mapDesignerRoot) {
            mapDesignerRoot.classList.remove("is-previewing");
            mapDesignerRoot.classList.remove("is-review-accepted");
        }
        updateProductionPayload();
        updateDesignCaptureForm();
        return true;
    }
    function restoreDesignCaptureVisualFromSnapshot(snapshot) {
        if (!snapshot || !isDesignCaptureMode()) {
            return false;
        }
        const orientationKey =
            snapshot.artwork && snapshot.artwork.orientationKey
                ? snapshot.artwork.orientationKey
                : String(snapshot.orientation || "").toLowerCase();
        const sizeKey =
            snapshot.artwork && snapshot.artwork.sizeKey
                ? snapshot.artwork.sizeKey
                : Object.keys(PRODUCTS).find(key =>
                    PRODUCTS[key] &&
                    PRODUCTS[key].label === snapshot.frameSize
                ) || null;
        if (["portrait", "landscape"].includes(orientationKey)) {
            state.orientation = orientationKey;
            const button = findSelectionButton(
                orientationButtons,
                "orientation",
                orientationKey
            );
            if (button) {
                setActiveButton(orientationButtons, button);
            }
        }
        if (sizeKey && PRODUCTS[sizeKey]) {
            state.size = sizeKey;
            const button = findSelectionButton(
                sizeButtons,
                "size",
                sizeKey
            );
            if (button) {
                setActiveButton(sizeButtons, button);
            }
        }
        if (mapTitleInput) {
            state.mapTitle.value = String(snapshot.mapTitle || "").slice(
                0,
                MAP_TITLE_MAX_LENGTH
            );
            mapTitleInput.value = state.mapTitle.value;
            state.mapTitle.source = state.mapTitle.value
                ? "restored_accepted_design"
                : null;
            state.mapTitle.customerEdited = Boolean(state.mapTitle.value);
        }
        orientationButtons.forEach(button => {
            const isActive = button.dataset.orientation === state.orientation;
            button.classList.toggle("is-active", isActive);
            button.setAttribute("aria-pressed", String(isActive));
            updateSelectionAccessibility(button, isActive);
        });
        sizeButtons.forEach(button => {
            const isActive = button.dataset.size === state.size;
            button.classList.toggle("is-active", isActive);
            button.setAttribute("aria-pressed", String(isActive));
            updateSelectionAccessibility(button, isActive);
        });
        updateProductFrame();
        const restoredCamera = snapshot.previewCamera &&
            snapshot.previewCamera.center &&
            Number.isFinite(snapshot.previewCamera.center.lng) &&
            Number.isFinite(snapshot.previewCamera.center.lat) &&
            Number.isFinite(snapshot.previewCamera.zoom)
            ? snapshot.previewCamera
            : snapshot.map;
        if (
            restoredCamera &&
            restoredCamera.center &&
            Number.isFinite(restoredCamera.center.lng) &&
            Number.isFinite(restoredCamera.center.lat) &&
            Number.isFinite(restoredCamera.zoom)
        ) {
            map.jumpTo({
                center: [
                    restoredCamera.center.lng,
                    restoredCamera.center.lat
                ],
                zoom: restoredCamera.zoom,
                bearing: 0,
                pitch: 0
            });
        }
        if (snapshot.inset && snapshot.inset.enabled) {
            state.inset.enabled = true;
            const source = snapshot.inset.sourceCircle || null;
            const detail = snapshot.inset.detailCircle || null;
            if (source) {
                if (Number.isFinite(source.mappedDiameterMm)) {
                    state.inset.sourceDiameterMm = source.mappedDiameterMm;
                }
                if (Number.isFinite(source.frameThicknessMm)) {
                    state.inset.sourceFrameMm = source.frameThicknessMm;
                }
                if (source.centerArtworkMm) {
                    state.inset.sourceCenterMm =
                        clonePlainObject(source.centerArtworkMm);
                }
            }
            if (detail) {
                if (Number.isFinite(detail.mappedDiameterMm)) {
                    state.inset.detailDiameterMm = detail.mappedDiameterMm;
                }
                if (Number.isFinite(detail.frameThicknessMm)) {
                    state.inset.detailFrameMm = detail.frameThicknessMm;
                }
                if (Number.isFinite(detail.structuralOverlapMm)) {
                    state.inset.structuralOverlapMm =
                        detail.structuralOverlapMm;
                }
                if (detail.centerArtworkMm) {
                    state.inset.detailCenterMm =
                        clonePlainObject(detail.centerArtworkMm);
                }
                if (detail.attachment) {
                    state.inset.attachment =
                        clonePlainObject(detail.attachment);
                }
            }
            ensureInsetDetailMap();
        }
        else {
            state.inset.enabled = false;
        }
        insetToggleButtons.forEach(button => {
            const isActive =
                String(state.inset.enabled) ===
                button.dataset.insetEnabled;
            button.classList.toggle("is-active", isActive);
            button.setAttribute("aria-pressed", String(isActive));
            updateSelectionAccessibility(button, isActive);
        });
        updateInsetGeometry();
        updateAssistedConfigurationSummary();
        updateShopifyProductBinding();
        updateProductionPayload();
        return true;
    }
    function restoreConfirmedDesignCaptureVisual() {
        const storedSubmission = getStoredDesignCaptureSubmission();
        if (!storedSubmission || !storedSubmission.designRecordSnapshot) {
            return false;
        }
        return restoreDesignCaptureVisualFromSnapshot(
            storedSubmission.designRecordSnapshot
        );
    }
    function showDesignCaptureSubmissionSuccess() {
        const storedSubmission = getStoredDesignCaptureSubmission();
        const designReference = storedSubmission && storedSubmission.designReference
            ? storedSubmission.designReference
            : "";
        if (!designReference) {
            hideDesignCapturePanel();
            return;
        }
        if (designCapturePanel) {
            designCapturePanel.hidden = false;
        }
        clearDesignCaptureFormFields();
        setDesignCaptureReferencePanel(designReference);
        setDesignCaptureEtsyConfiguration(storedSubmission
            ? storedSubmission.etsyConfiguration || null
            : null);
        setDesignCaptureSuccessMessage(`Thank you — your accepted design ${designReference} has been sent to ${MAP_DESIGNER_BRAND}.`, true);
        setDesignCaptureStatus("Keep this design reference. If you order through Etsy, enter it in the Design reference field and choose the matching product options.");
        setDesignCaptureReturnAction(storedSubmission ? storedSubmission.returnUrl : "", true, designReference);
        if (storedSubmission && storedSubmission.designRecordSnapshot) {
            setCompletedDesignRecord(storedSubmission.designRecordSnapshot, "etsy");
            setDesignCaptureConfirmedViewLocked(true);
        }
        markDesignCaptureSubmissionConfirmationShown(storedSubmission);
    }
    function getPayloadMappedArtworkArea(payload) {
        return payload &&
            payload.geographicBounds &&
            payload.geographicBounds.mappedArtworkArea
            ? payload.geographicBounds.mappedArtworkArea
            : null;
    }
    function getPayloadGroundDimensions(payload) {
        const mappedArtworkArea = getPayloadMappedArtworkArea(payload);
        return mappedArtworkArea &&
            mappedArtworkArea.groundDimensions
            ? mappedArtworkArea.groundDimensions
            : null;
    }
    function formatPlainSummaryValue(value, fallback = "Not available") {
        if (value === null ||
            value === undefined ||
            value === "") {
            return fallback;
        }
        return String(value);
    }
    function formatPlainSummaryNumber(value, maximumFractionDigits = 3) {
        if (!Number.isFinite(value)) {
            return "Not available";
        }
        return Number(value).toLocaleString(undefined, {
            maximumFractionDigits
        });
    }
    function formatPlainSummaryDistance(meters, kilometres) {
        if (!Number.isFinite(meters)) {
            return "Not available";
        }
        const metreText = `${formatPlainSummaryNumber(meters, 2)} m`;
        if (Number.isFinite(kilometres) &&
            kilometres >= 1) {
            return `${metreText} (${formatPlainSummaryNumber(kilometres, 3)} km)`;
        }
        return metreText;
    }
    function buildDesignCaptureProductionRecord(payload) {
        if (!payload) {
            return null;
        }
        const receipt = payload.summaries &&
            payload.summaries.customerReceipt
            ? payload.summaries.customerReceipt
            : buildCustomerReceiptSummary(payload);
        const qgis = payload.productionHandoff &&
            payload.productionHandoff.qgis
            ? payload.productionHandoff.qgis
            : null;
        if (!qgis || !hasValidQgisProductionHandoff(qgis)) {
            return null;
        }
        const insetEnabled = Boolean(payload.inset &&
            payload.inset.enabled);
        const insetQgis = insetEnabled &&
            payload.productionHandoff
            ? payload.productionHandoff.insetQgis || null
            : null;
        if (insetEnabled &&
            !hasValidInsetQgisProductionHandoff(insetQgis)) {
            return null;
        }
        const layout = qgis.layoutMapItem;
        const record = {
            schemaVersion: "design-capture-1",
            recordType: MAP_DESIGNER_PRODUCTION_RECORD_TYPE,
            designReference: receipt.designReference || "",
            acceptedAt: payload.review
                ? payload.review.acceptedAt || ""
                : "",
            pageMode: payload.pageMode
                ? payload.pageMode.mode || ""
                : state.page.mode,
            etsyOrderMatch: (() => {
                const referenceValidation = validateDesignReferenceAgainstPayload(
                    receipt.designReference || "",
                    payload
                );
                return {
                    referenceSchema: DESIGN_REFERENCE_SCHEMA,
                    orderMatchKey: referenceValidation.expectedOrderMatchKey,
                    referenceMatchesConfiguration: referenceValidation.matchesConfiguration,
                    frameSize: receipt.frame ? receipt.frame.sizeName || "" : "",
                    orientation: receipt.frame ? receipt.frame.orientation || "" : "",
                    detailOption: payload.inset && payload.inset.enabled
                        ? "Detail inset"
                        : "No inset"
                };
            })(),
            specialPlace: {
                label: receipt.specialPlace
                    ? receipt.specialPlace.label || "Custom map area"
                    : "Custom map area",
                source: payload.specialPlace
                    ? payload.specialPlace.source || ""
                    : ""
            },
            customerGuidance: payload.customerGuidance
                ? clonePlainObject(payload.customerGuidance)
                : {
                    provided: false,
                    note: ""
                },
            product: {
                productKey: payload.artwork
                    ? payload.artwork.size || ""
                    : "",
                label: receipt.frame
                    ? receipt.frame.sizeName || ""
                    : "",
                physicalSize: receipt.frame
                    ? receipt.frame.physicalSizeLabel || ""
                    : "",
                orientation: receipt.frame
                    ? receipt.frame.orientation || ""
                    : "",
                mappedWidthMm: layout.widthMm,
                mappedHeightMm: layout.heightMm
            },
            qgis: {
                sourceOfTruth: qgis.sourceOfTruth,
                crs: qgis.extent3857.crs,
                extent: qgis.extent3857.qgisInput,
                backupCrs: qgis.extent4326.crs,
                backupExtent: qgis.extent4326.qgisInput,
                rotationDegrees: layout.rotationDegrees,
                recommendedScaleDenominator: layout.recommendedScaleDenominator
            }
        };
        if (insetEnabled) {
            record.inset = {
                schemaVersion: insetQgis.schemaVersion,
                sourceOfTruth: insetQgis.sourceOfTruth,
                artworkCoordinateSystem: clonePlainObject(insetQgis.artworkCoordinateSystem),
                sourceCircle: {
                    centerArtworkMm: clonePlainObject(insetQgis.sourceCircle.centerArtworkMm),
                    mappedDiameterMm: insetQgis.sourceCircle.mappedDiameterMm,
                    frameThicknessMm: insetQgis.sourceCircle.frameThicknessMm,
                    outerDiameterMm: insetQgis.sourceCircle.outerDiameterMm
                },
                detailCircle: {
                    centerArtworkMm: clonePlainObject(insetQgis.detailCircle.centerArtworkMm),
                    mappedDiameterMm: insetQgis.detailCircle.mappedDiameterMm,
                    frameThicknessMm: insetQgis.detailCircle.frameThicknessMm,
                    outerDiameterMm: insetQgis.detailCircle.outerDiameterMm,
                    structuralOverlapMm: insetQgis.detailCircle.structuralOverlapMm,
                    attachment: clonePlainObject(insetQgis.detailCircle.attachment)
                },
                detailMap: {
                    crs: insetQgis.detailMap.extent3857.crs,
                    extent: insetQgis.detailMap.extent3857.qgisInput,
                    backupCrs: insetQgis.detailMap.extent4326.crs,
                    backupExtent: insetQgis.detailMap.extent4326.qgisInput,
                    diameterMm: insetQgis.detailMap.diameterMm,
                    recommendedScaleDenominator: insetQgis.detailMap.recommendedScaleDenominator
                },
                connector: {
                    geometry: insetQgis.connector.geometry,
                    bands: insetQgis.connector.bands.map(band => ({
                        name: band.name,
                        pointsArtworkMm: clonePlainObject(band.pointsArtworkMm)
                    }))
                }
            };
        }
        return record;
    }
    function buildDesignCaptureEtsyConfiguration(payload) {
        if (!payload) {
            return null;
        }
        const sizeName = getPayloadSizeName(payload);
        const orientation = getPayloadOrientationLabel(payload);
        if (!sizeName || !orientation) {
            return null;
        }
        const insetEnabled = Boolean(payload.inset && payload.inset.enabled);
        const detailOption = insetEnabled ? "Detail inset" : "No inset";
        const designReference = getDesignCapturePayloadDesignReference(payload);
        const referenceValidation = validateDesignReferenceAgainstPayload(
            designReference,
            payload
        );
        return {
            size: sizeName,
            insetEnabled,
            detailOption,
            optionLabel: `${sizeName} · ${detailOption}`,
            etsyVariantLabel: insetEnabled
                ? `${sizeName} + Detail inset`
                : sizeName,
            orientation,
            orientationLabel: `Artwork orientation: ${orientation}`,
            orderMatchKey: referenceValidation.expectedOrderMatchKey,
            referenceMatchesConfiguration: referenceValidation.matchesConfiguration,
            referenceSchema: DESIGN_REFERENCE_SCHEMA,
            emailValue: [
                `Frame size: ${sizeName}`,
                `Orientation: ${orientation}`,
                `Detail option: ${detailOption}`,
                `Order match key: ${referenceValidation.expectedOrderMatchKey}`,
                `Reference configuration match: ${referenceValidation.matchesConfiguration ? "PASS" : "FAIL"}`
            ].join("\n")
        };
    }
    function setDesignCaptureEtsyConfiguration(configuration) {
        const isVisible = Boolean(configuration &&
            configuration.optionLabel);
        if (designCaptureConfigurationPanel) {
            designCaptureConfigurationPanel.hidden = !isVisible;
        }
        if (designCaptureConfigurationSummary) {
            designCaptureConfigurationSummary.textContent =
                isVisible
                    ? configuration.optionLabel
                    : "";
        }
        if (designCaptureConfigurationOrientation) {
            designCaptureConfigurationOrientation.textContent =
                isVisible
                    ? configuration.orientationLabel || ""
                    : "";
        }
    }
    function buildDesignCapturePlainTextSummary(payload) {
        if (!payload) {
            return "";
        }
        const pageMode = payload.pageMode || buildPageModeMetadata();
        const mappedArtworkArea = getPayloadMappedArtworkArea(payload);
        const groundDimensions = getPayloadGroundDimensions(payload);
        const center = payload.map && payload.map.center
            ? payload.map.center
            : null;
        const acceptedAt = payload.review
            ? payload.review.acceptedAt || null
            : null;
        const designReference = payload.review
            ? payload.review.designReference || null
            : null;
        const customerGuidance = payload.customerGuidance &&
            payload.customerGuidance.provided &&
            payload.customerGuidance.note
            ? payload.customerGuidance.note
            : "Not provided";
        const qgisHandoff = payload.productionHandoff &&
            payload.productionHandoff.qgis
            ? payload.productionHandoff.qgis
            : null;
        const boundsLine = mappedArtworkArea
            ? `N ${formatPlainSummaryNumber(mappedArtworkArea.north, 7)}, S ${formatPlainSummaryNumber(mappedArtworkArea.south, 7)}, E ${formatPlainSummaryNumber(mappedArtworkArea.east, 7)}, W ${formatPlainSummaryNumber(mappedArtworkArea.west, 7)}`
            : "Not available";
        const centerLine = center
            ? `${formatPlainSummaryNumber(center.lat, 7)}, ${formatPlainSummaryNumber(center.lng, 7)}`
            : "Not available";
        const groundWidthLine = groundDimensions
            ? formatPlainSummaryDistance(groundDimensions.widthMeters, groundDimensions.widthKilometers)
            : "Not available";
        const groundHeightLine = groundDimensions
            ? formatPlainSummaryDistance(groundDimensions.heightMeters, groundDimensions.heightKilometers)
            : "Not available";
        const groundAreaLine = groundDimensions
            ? `${formatPlainSummaryNumber(groundDimensions.areaSquareMeters, 0)} m² (${formatPlainSummaryNumber(groundDimensions.areaSquareKilometers, 6)} km²)`
            : "Not available";
        const extent3857 = qgisHandoff
            ? qgisHandoff.extent3857
            : null;
        const extent4326 = qgisHandoff
            ? qgisHandoff.extent4326
            : null;
        const layoutMapItem = qgisHandoff
            ? qgisHandoff.layoutMapItem
            : null;
        const insetQgisHandoff = payload.productionHandoff &&
            payload.productionHandoff.insetQgis
            ? payload.productionHandoff.insetQgis
            : null;
        const insetProductionText = insetQgisHandoff
            ? formatInsetQgisProductionText(insetQgisHandoff)
            : "";
        return [
            "Wonderful Walls Studio — QGIS Production Handoff",
            "",
            `Design reference: ${formatPlainSummaryValue(designReference)}`,
            `Page mode: ${formatPlainSummaryValue(pageMode.label || pageMode.mode)}`,
            `Map title: ${formatPlainSummaryValue(getPayloadSpecialPlaceLabel(payload))}`,
            `Search origin: ${formatPlainSummaryValue(getPayloadSearchOriginLabel(payload))}`,
            `Map title source: ${formatPlainSummaryValue(getPayloadSpecialPlaceSource(payload))}`,
            `Frame: ${formatPlainSummaryValue(getPayloadSizeName(payload))} (${formatPlainSummaryValue(getPayloadPhysicalSizeLabel(payload))})`,
            `Orientation: ${formatPlainSummaryValue(getPayloadOrientationLabel(payload))}`,
            `Accepted: ${formatPlainSummaryValue(getAcceptedAtDisplay(acceptedAt) || acceptedAt)}`,
            "",
            "CUSTOMER GUIDANCE",
            customerGuidance,
            "",
            "PRIMARY QGIS SETUP — EPSG:3857",
            `Copy-ready extent (xmin, xmax, ymin, ymax): ${extent3857 ? extent3857.qgisInput : "Not available"}`,
            `X minimum: ${extent3857 ? formatPlainSummaryNumber(extent3857.xmin, 3) : "Not available"}`,
            `X maximum: ${extent3857 ? formatPlainSummaryNumber(extent3857.xmax, 3) : "Not available"}`,
            `Y minimum: ${extent3857 ? formatPlainSummaryNumber(extent3857.ymin, 3) : "Not available"}`,
            `Y maximum: ${extent3857 ? formatPlainSummaryNumber(extent3857.ymax, 3) : "Not available"}`,
            "",
            "LAYOUT MAP ITEM",
            `Width: ${layoutMapItem ? formatPlainSummaryNumber(layoutMapItem.widthMm, 1) : "Not available"} mm`,
            `Height: ${layoutMapItem ? formatPlainSummaryNumber(layoutMapItem.heightMm, 1) : "Not available"} mm`,
            `Rotation: ${layoutMapItem ? formatPlainSummaryNumber(layoutMapItem.rotationDegrees, 1) : "Not available"}°`,
            `Nominal scale check: 1:${layoutMapItem ? formatPlainSummaryNumber(layoutMapItem.recommendedScaleDenominator, 0) : "Not available"}`,
            "",
            "BACKUP GEOGRAPHIC EXTENT — EPSG:4326",
            `Copy-ready extent (xmin, xmax, ymin, ymax): ${extent4326 ? extent4326.qgisInput : "Not available"}`,
            `Bounds: ${boundsLine}`,
            `Approximate ground width: ${groundWidthLine}`,
            `Approximate ground height: ${groundHeightLine}`,
            `Approximate ground area: ${groundAreaLine}`,
            "",
            "DIAGNOSTICS ONLY",
            `Centre: ${centerLine}`,
            `MapLibre zoom: ${payload.map ? formatPlainSummaryValue(payload.map.zoom) : "Not available"}`,
            "",
            "Production instruction:",
            "Use the exact EPSG:3857 extent and stated layout map-item dimensions. Do not recreate the composition from centre and MapLibre zoom alone.",
            "",
            insetProductionText,
            "",
            "PRODUCTION RECORD",
            "The compact Production Record below retains the exact machine-readable production geometry, including connector geometry."
        ].join("\n");
    }
    function setDesignCaptureStatus(message) {
        if (!designCaptureStatus) {
            return;
        }
        designCaptureStatus.textContent = message || "";
    }
    function setFieldValue(field, value) {
        if (!field) {
            return;
        }
        field.value = value || "";
    }
    function clearDesignCaptureFormFields() {
        setFieldValue(designCapturePageModeField, "");
        setFieldValue(designCaptureReferenceField, "");
        setFieldValue(designCaptureReceiptSummaryField, "");
        setFieldValue(designCaptureProductionSummaryField, "");
        setFieldValue(designCapturePlainSummaryField, "");
        setFieldValue(designCaptureEtsyConfigurationField, "");
        setDesignCaptureEtsyConfiguration(null);
        setDesignCaptureReferencePanel("");
        setDesignCaptureSuccessMessage("", false);
        setDesignCaptureReturnAction("", false);
        if (designCaptureSubmitButton) {
            designCaptureSubmitButton.disabled = true;
        }
    }
    function hideDesignCapturePanel() {
        if (designCapturePanel) {
            designCapturePanel.hidden = true;
        }
        clearDesignCaptureFormFields();
        setDesignCaptureStatus("");
    }
    function populateDesignCaptureForm(payload) {
        if (!payload || !payload.summaries) {
            hideDesignCapturePanel();
            return;
        }
        if (!isDesignCaptureMode()) {
            hideDesignCapturePanel();
            return;
        }
        if (designCapturePanel) {
            designCapturePanel.hidden = false;
        }
        /*
        A Shopify contact form success message may still be
        present in the DOM after a previous submission. Hide it
        during the normal Looks good flow. The Etsy return action
        is also hidden until a confirmed submission state exists.
        */
        setDesignCaptureSuccessMessage("", false);
        setDesignCaptureReturnAction("", false);
        setFieldValue(designCapturePageModeField, payload.pageMode ? payload.pageMode.mode : state.page.mode);
        const designReference = getDesignCapturePayloadDesignReference(payload);
        setFieldValue(designCaptureReferenceField, designReference);
        setDesignCaptureReferencePanel(designReference);
        const etsyConfiguration = buildDesignCaptureEtsyConfiguration(payload);
        setDesignCaptureEtsyConfiguration(etsyConfiguration);
        setFieldValue(designCaptureEtsyConfigurationField, etsyConfiguration
            ? etsyConfiguration.emailValue
            : "");
        setFieldValue(designCaptureReceiptSummaryField, serialiseProductionPayload(payload.summaries.customerReceipt));
        const designCaptureProductionRecord = buildDesignCaptureProductionRecord(payload);
        setFieldValue(designCaptureProductionSummaryField, designCaptureProductionRecord
            ? compactJson(designCaptureProductionRecord)
            : "");
        setFieldValue(designCapturePlainSummaryField, buildDesignCapturePlainTextSummary(payload));
        /*
         * The complete accepted browser-state payload is intentionally
         * not submitted through Shopify's contact endpoint. The compact
         * production record above contains the exact production/QGIS
         * reconstruction data required for Design Capture submissions.
         */
        if (designCaptureSubmitButton) {
            designCaptureSubmitButton.disabled = false;
        }
        const referenceValidation = validateDesignReferenceAgainstPayload(
            designReference,
            payload
        );
        if (!referenceValidation.isValid || !referenceValidation.matchesConfiguration) {
            if (designCaptureSubmitButton) {
                designCaptureSubmitButton.disabled = true;
            }
            setDesignCaptureStatus("The design reference did not validate against the accepted configuration. Keep editing and review the composition again before recording it.");
            return;
        }
        setDesignCaptureStatus(`Ready to record. Etsy match key: ${referenceValidation.expectedOrderMatchKey}.`);
    }
    function updateDesignCaptureForm() {
        if (hasDesignCaptureSubmissionSuccess() &&
            isDesignCaptureMode()) {
            showDesignCaptureSubmissionSuccess();
            return;
        }
        if (state.review.status === "accepted" &&
            state.review.acceptedPayload &&
            isDesignCaptureMode()) {
            populateDesignCaptureForm(state.review.acceptedPayload);
            return;
        }
        hideDesignCapturePanel();
    }
    function setMapInteractionsEnabled(isEnabled) {
        const shouldEnable = Boolean(isEnabled && !state.inspection.active);
        // Unit 3a: only pan/zoom-style interactions are restored when returning
        // to editing. Rotation and pitch remain disabled for the launch workflow.
        const handlers = [
            map.dragPan,
            map.scrollZoom,
            map.boxZoom,
            map.keyboard,
            map.doubleClickZoom,
            map.touchZoomRotate
        ];
        handlers.forEach(handler => {
            if (!handler) {
                return;
            }
            if (shouldEnable && typeof handler.enable === "function") {
                handler.enable();
            }
            if (!shouldEnable && typeof handler.disable === "function") {
                handler.disable();
            }
        });
        if (shouldEnable) {
            enforceLaunchOrientationPolicy();
        }
        else {
            if (map.dragRotate && typeof map.dragRotate.disable === "function") {
                map.dragRotate.disable();
            }
            if (map.touchPitch && typeof map.touchPitch.disable === "function") {
                map.touchPitch.disable();
            }
        }
    }
    function hideSymbolLayersForMap(targetMap, visibilityStore) {
        if (!targetMap ||
            typeof targetMap.isStyleLoaded !== "function" ||
            !targetMap.isStyleLoaded()) {
            return false;
        }
        const style = targetMap.getStyle();
        if (!style || !Array.isArray(style.layers)) {
            return false;
        }
        visibilityStore.clear();
        style.layers.forEach(layer => {
            if (layer.type !== "symbol") {
                return;
            }
            const currentVisibility = targetMap.getLayoutProperty(layer.id, "visibility") || "visible";
            visibilityStore.set(layer.id, currentVisibility);
            targetMap.setLayoutProperty(layer.id, "visibility", "none");
        });
        return true;
    }
    function restoreSymbolLayersForMap(targetMap, visibilityStore) {
        if (!targetMap) {
            visibilityStore.clear();
            return;
        }
        visibilityStore.forEach((visibility, layerId) => {
            if (!targetMap.getLayer(layerId)) {
                return;
            }
            targetMap.setLayoutProperty(layerId, "visibility", visibility);
        });
        visibilityStore.clear();
    }
    function hideMapLabelLayers() {
        hideSymbolLayersForMap(map, previewLayerVisibility);
        if (!state.inset.enabled) {
            return;
        }
        const detailMap = ensureInsetDetailMap();
        if (!detailMap) {
            return;
        }
        if (hideSymbolLayersForMap(detailMap, insetPreviewLayerVisibility)) {
            return;
        }
        detailMap.once("load", () => {
            if (state.review.status === "editing") {
                return;
            }
            hideSymbolLayersForMap(detailMap, insetPreviewLayerVisibility);
        });
    }
    function restoreMapLabelLayers() {
        restoreSymbolLayersForMap(map, previewLayerVisibility);
        restoreSymbolLayersForMap(insetDetailMap, insetPreviewLayerVisibility);
    }
    function enterReviewPreview() {
        // A confirmed Etsy Design Capture submission stays visible after Shopify
        // reloads the contact form. Entering Review again is the deliberate
        // boundary that starts a new recording cycle: retire the previous
        // on-page confirmation so the next accepted composition receives its
        // own reference and can be submitted once.
        resetDesignCaptureSubmissionConfirmationForNewDesign();
        if (state.designRecord.snapshot && state.review.status === "editing") {
            clearCompletedDesignRecord();
        }

        // Review always returns to the true full-frame composition. The enlarged
        // workspace is a screen-only editing aid and never becomes part of the
        // locked production state.
        if (state.inspection.active) {
            exitInspectionView({ restoreMapInteractions: false });
        }
        const payload = updateProductionPayload();
        const validation = state.production.validation;
        if (!payload || !validation || !validation.isValid) {
            setReviewStatus("The composition is not quite ready. Adjust the map, then review your composition again.");
            return;
        }
        state.review.status = "previewing";
        syncInspectionAvailability();
        state.review.lockedPayload = clonePlainObject(payload);
        state.review.acceptedAt = null;
        if (mapTitleInput) {
            mapTitleInput.disabled = true;
        }
        if (customerGuidanceInput) {
            customerGuidanceInput.disabled = true;
        }
        resetCartSubmissionState();
        hideCartPanel();
        if (mapDesignerRoot) {
            mapDesignerRoot.classList.add("is-previewing");
            mapDesignerRoot.classList.remove("is-review-accepted");
        }
        if (reviewPanel) {
            reviewPanel.hidden = false;
        }
        if (reviewAcceptButton) {
            reviewAcceptButton.disabled = false;
            reviewAcceptButton.textContent = "Looks good";
        }
        updateReviewSummary(state.review.lockedPayload);
        setReviewStatus("Your composition is locked while you review it.");
        setMapInteractionsEnabled(false);
        hideMapLabelLayers();
        updateProductionPayload();
        if (reviewAcceptButton) {
            // Keep keyboard/screen-reader focus on the primary review action without
            // allowing the browser to drag the viewport down to the review card.
            // The viewport is positioned deliberately on the preview below.
            try {
                reviewAcceptButton.focus({ preventScroll: true });
            }
            catch (error) {
                reviewAcceptButton.focus();
            }
        }
        // Release 2.0.1 universalises the Stage 9b review landing behaviour:
        // entering Review should land on the composition being reviewed on every
        // device, not on the controls below it. Wait for layout to settle and use
        // the existing helper so reduced-motion preferences remain respected.
        if (mapWorkspace) {
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    scrollDesignerElementIntoView(mapWorkspace);
                });
            });
        }
    }
    function exitReviewPreview() {
        // Preview magnification is screen-only. Always return to the true full
        // composition before reopening editing controls.
        if (state.inspection.active) {
            exitInspectionView({ restoreMapInteractions: false });
        }
        state.review.status = "editing";
        state.review.lockedPayload = null;
        state.review.acceptedPayload = null;
        state.review.acceptedAt = null;
        state.review.designReference = null;
        state.review.isReadyForCart = false;
        if (mapTitleInput) {
            mapTitleInput.disabled = false;
        }
        if (customerGuidanceInput) {
            customerGuidanceInput.disabled = false;
        }
        resetCartSubmissionState();
        hideCartPanel();
        if (reviewEditButton) {
            reviewEditButton.textContent = "Keep editing";
        }
        clearAcceptedProductionPayloadField();
        updateCartReadinessGate();
        updateDesignCaptureForm();
        if (mapDesignerRoot) {
            mapDesignerRoot.classList.remove("is-previewing", "is-review-accepted");
        }
        if (reviewPanel) {
            reviewPanel.hidden = true;
        }
        restoreMapLabelLayers();
        setMapInteractionsEnabled(true);
        syncInspectionAvailability();
        updateProductionPayload();
        if (reviewPreviewButton) {
            reviewPreviewButton.focus();
        }
    }
    function acceptReviewPreview() {
        if (state.review.status !== "previewing") {
            return;
        }
        // Inspection zoom/pan is never part of the accepted production state.
        // Return to Full view before locking acceptance.
        if (state.inspection.active) {
            exitInspectionView({ restoreMapInteractions: false });
        }
        if (!state.review.lockedPayload) {
            setReviewStatus("The composition is not quite ready. Keep editing, then review your composition again.");
            return;
        }
        const lockedPayloadValidation = validateProductionPayload(state.review.lockedPayload);
        if (!lockedPayloadValidation.isValid) {
            setReviewStatus("The composition needs one more check. Keep editing, then review your composition again.");
            return;
        }
        state.review.status = "accepted";
        syncInspectionAvailability();
        state.review.acceptedAt = new Date().toISOString();
        state.review.designReference =
            state.review.designReference || createDesignReference(state.review.lockedPayload);
        /*
        First store the locked payload so cart readiness can
        be evaluated against an accepted source of truth.
        */
        state.review.acceptedPayload =
            clonePlainObject(state.review.lockedPayload);
        updateReviewCartReadiness();
        /*
        Rebuild the accepted payload after readiness is known,
        so the hidden accepted field includes the final review
        state: accepted, locked and ready for cart.
        */
        const acceptedPayload = updateProductionPayload();
        state.review.acceptedPayload =
            clonePlainObject(acceptedPayload);
        const readiness = updateReviewCartReadiness();
        updateAcceptedProductionPayloadField(state.review.acceptedPayload);
        updateCartReadinessGate();
        updateDesignCaptureForm();
        updateCartInterface();
        updateProductionPayload();
        if (mapDesignerRoot) {
            mapDesignerRoot.classList.add("is-review-accepted");
        }
        if (reviewAcceptButton) {
            reviewAcceptButton.disabled = true;
            reviewAcceptButton.textContent = "Composition saved";
        }
        setReviewStatus(readiness.isReadyForCart
            ? (isShopifyStoreMode()
                ? "Your composition is ready to add to your cart."
                : "Your composition is ready to send.")
            : "The composition was saved, but it needs one more check before the next step.");
    }
    function updateProductionPayload() {
        const payload = buildProductionPayload();
        const validation = validateProductionPayload(payload);
        state.production.currentPayload = payload;
        state.production.validation = validation;
        state.production.lastUpdatedAt = payload
            ? payload.capturedAt
            : null;
        updateProductionPayloadField(payload);
        return payload;
    }
    function buildEtsyChannelBoundaryAudit() {
        const form = designCaptureForm;
        const reference = state.review.designReference || "";
        const referenceValidation = reference && state.review.acceptedPayload
            ? validateDesignReferenceAgainstPayload(reference, state.review.acceptedPayload)
            : null;
        const assertions = {
            isolatedBoundaryActive: isEtsyIsolatedBoundary(),
            designCaptureModeActive: state.page.mode === "design_capture",
            shopifyProductDataAbsent: !shopifyProductDataElement,
            cartMarkupAbsent: !cartPanel && !cartSubmitButton && !cartLink,
            priceMarkupAbsent: !variantPrice,
            deliveryMarkupAbsent: !deliveryNote,
            customerGuidanceFieldAbsent: !customerGuidanceInput,
            customerNameFieldAbsent: !form || !form.querySelector('[name="contact[name]"]'),
            visibleCustomerEmailFieldAbsent: !form || !form.querySelector('input[name="contact[email]"]:not([type="hidden"])'),
            freeFormBuyerMessageAbsent: !form || !form.querySelector('[name="contact[body]"]'),
            technicalEmailFieldIsHidden: Boolean(
                designCaptureTechnicalEmailField &&
                designCaptureTechnicalEmailField.type === "hidden"
            ),
            acceptedReferenceMatchesConfiguration: referenceValidation
                ? Boolean(referenceValidation.isValid && referenceValidation.matchesConfiguration)
                : true
        };
        return {
            checkedAt: new Date().toISOString(),
            release: MAP_DESIGNER_RELEASE,
            pageMode: buildPageModeMetadata(),
            referenceSchema: DESIGN_REFERENCE_SCHEMA,
            acceptedReferenceValidation: referenceValidation,
            assertions,
            pass: Object.values(assertions).every(Boolean)
        };
    }
    function exposeProductionDebugHelpers() {
        window.WonderfullyWoodMapDesigner =
            window.WonderfullyWoodMapDesigner || {};
        window.WonderfullyWoodMapDesigner.getMaintenanceContract =
            () => clonePlainObject(MAP_DESIGNER_MAINTENANCE_CONTRACT);
        window.WonderfullyWoodMapDesigner.getReleaseInfo =
            () => ({
                release: MAP_DESIGNER_RELEASE,
                brand: MAP_DESIGNER_BRAND,
                productionSchema: MAP_DESIGNER_PRODUCTION_SCHEMA,
                designReferenceSchema: isDesignCaptureMode() ? DESIGN_REFERENCE_SCHEMA : "store-legacy",
                channelBoundary: getChannelBoundary(),
                pageMode: state.page.mode,
                mapLibreVersion: typeof maplibregl !== "undefined" && maplibregl.version
                    ? maplibregl.version
                    : null,
                assistedEditing: "universal",
                debugNamespace: "WonderfullyWoodMapDesigner"
            });
        window.WonderfullyWoodMapDesigner.getDesignRecordState =
            () => ({
                schema: DESIGN_RECORD_SCHEMA,
                completionChannel: state.designRecord.completionChannel,
                status: state.designRecord.status,
                snapshot: state.designRecord.snapshot
                    ? clonePlainObject(state.designRecord.snapshot)
                    : null,
                renderedReference: state.designRecord.renderedReference,
                staticRequestUrls: [...state.designRecord.staticRequestUrls],
                lastError: state.designRecord.lastError
                    ? clonePlainObject(state.designRecord.lastError)
                    : null
            });
        window.WonderfullyWoodMapDesigner.getDesignRecordStaticRequests =
            () => getDesignRecordStaticRequests(state.designRecord.snapshot);
        window.WonderfullyWoodMapDesigner.renderDesignRecord =
            () => renderDesignRecord();
        window.WonderfullyWoodMapDesigner.getStaticMapIntegerViewport =
            (zoom, targetSize = DESIGN_RECORD_STATIC_STAGE_SIZE) => getStaticMapIntegerViewport(Number(zoom), Number(targetSize));
        window.WonderfullyWoodMapDesigner.getProductionPayload =
            () => updateProductionPayload();
        window.WonderfullyWoodMapDesigner.getProductionPayloadJson =
            () => serialiseProductionPayload(updateProductionPayload());
        window.WonderfullyWoodMapDesigner.getProductionValidation =
            () => {
                updateProductionPayload();
                return JSON.parse(JSON.stringify(state.production.validation));
            };
        window.WonderfullyWoodMapDesigner.getMapTitle =
            () => ({
                provided: Boolean(normaliseMapTitle(state.mapTitle.value)),
                value: normaliseMapTitle(state.mapTitle.value),
                source: normaliseMapTitle(state.mapTitle.value)
                    ? state.mapTitle.source
                    : "no_title",
                customerEdited: Boolean(state.mapTitle.customerEdited),
                suggestedFromSearch: state.mapTitle.suggestedFromSearch || "",
                searchOrigin: state.search.selectedLocation
                    ? clonePlainObject(state.search.selectedLocation)
                    : null
            });
        window.WonderfullyWoodMapDesigner.getAcceptedProductionPayload =
            () => state.review.acceptedPayload
                ? clonePlainObject(state.review.acceptedPayload)
                : null;
        window.WonderfullyWoodMapDesigner.getAcceptedProductionPayloadJson =
            () => serialiseProductionPayload(state.review.acceptedPayload);
        window.WonderfullyWoodMapDesigner.getInsetGeometry =
            () => {
                updateInsetGeometry();
                return clonePlainObject(buildInsetPayload());
            };
        window.WonderfullyWoodMapDesigner.getAcceptedInsetGeometry =
            () => state.review.acceptedPayload &&
                state.review.acceptedPayload.inset
                ? clonePlainObject(state.review.acceptedPayload.inset)
                : null;
        window.WonderfullyWoodMapDesigner.getInsetQgisProductionHandoff =
            () => {
                const payload = updateProductionPayload();
                return payload &&
                    payload.productionHandoff &&
                    payload.productionHandoff.insetQgis
                    ? clonePlainObject(payload.productionHandoff.insetQgis)
                    : null;
            };
        window.WonderfullyWoodMapDesigner.getAcceptedInsetQgisProductionHandoff =
            () => state.review.acceptedPayload &&
                state.review.acceptedPayload.productionHandoff &&
                state.review.acceptedPayload.productionHandoff.insetQgis
                ? clonePlainObject(state.review.acceptedPayload.productionHandoff.insetQgis)
                : null;
        window.WonderfullyWoodMapDesigner.getAcceptedInsetQgisProductionText =
            () => formatInsetQgisProductionText(window.WonderfullyWoodMapDesigner.getAcceptedInsetQgisProductionHandoff());
        window.WonderfullyWoodMapDesigner.copyAcceptedInsetQgisExtent3857 =
            async () => {
                const handoff = window.WonderfullyWoodMapDesigner
                    .getAcceptedInsetQgisProductionHandoff();
                const value = handoff && handoff.detailMap && handoff.detailMap.extent3857
                    ? handoff.detailMap.extent3857.qgisInput || ""
                    : "";
                if (value &&
                    navigator.clipboard &&
                    typeof navigator.clipboard.writeText === "function") {
                    try {
                        await navigator.clipboard.writeText(value);
                    }
                    catch (error) {
                        // Browser focus/permission restrictions must not block access.
                    }
                }
                return value;
            };
        window.WonderfullyWoodMapDesigner.copyAcceptedInsetQgisProductionText =
            async () => {
                const value = window.WonderfullyWoodMapDesigner
                    .getAcceptedInsetQgisProductionText();
                if (value &&
                    navigator.clipboard &&
                    typeof navigator.clipboard.writeText === "function") {
                    try {
                        await navigator.clipboard.writeText(value);
                    }
                    catch (error) {
                        // Browser focus/permission restrictions must not block access.
                    }
                }
                return value;
            };
        window.WonderfullyWoodMapDesigner.getAcceptedProductionPayloadField =
            () => acceptedProductionPayloadField;
        window.WonderfullyWoodMapDesigner.copyAcceptedProductionPayload =
            async () => {
                if (!state.review.acceptedPayload ||
                    !navigator.clipboard ||
                    typeof navigator.clipboard.writeText !== "function") {
                    return state.review.acceptedPayload;
                }
                await navigator.clipboard.writeText(serialiseProductionPayload(state.review.acceptedPayload));
                return state.review.acceptedPayload;
            };
        window.WonderfullyWoodMapDesigner.getAcceptedProductionValidation =
            () => {
                const readiness = updateReviewCartReadiness();
                return JSON.parse(JSON.stringify(readiness.validation));
            };
        window.WonderfullyWoodMapDesigner.isReadyForCart =
            () => {
                const readiness = updateCartReadinessGate();
                updateProductionPayload();
                return readiness.isReady;
            };
        window.WonderfullyWoodMapDesigner.canProceedToCart =
            () => {
                const readiness = updateCartReadinessGate();
                updateProductionPayload();
                return readiness.isReady;
            };
        window.WonderfullyWoodMapDesigner.getCartReadiness =
            () => {
                const readiness = updateCartReadinessGate();
                updateProductionPayload();
                return clonePlainObject(readiness);
            };
        window.WonderfullyWoodMapDesigner.getCartPayloadForFutureSubmission =
            () => getCartPayloadForFutureSubmission();
        window.WonderfullyWoodMapDesigner.getCartSubmission =
            () => {
                const submission = buildCartSubmission();
                return submission
                    ? clonePlainObject(submission)
                    : null;
            };
        window.WonderfullyWoodMapDesigner.getCartLineItemProperties =
            () => {
                const submission = buildCartSubmission();
                return submission && submission.items[0]
                    ? clonePlainObject(submission.items[0].properties)
                    : null;
            };
        window.WonderfullyWoodMapDesigner.getCartProductionRecord =
            () => {
                const readiness = updateCartReadinessGate();
                const record = readiness.isReady &&
                    state.review.acceptedPayload
                    ? buildCartProductionRecord(state.review.acceptedPayload, readiness.shopifyProductBinding)
                    : null;
                return record
                    ? clonePlainObject(record)
                    : null;
            };
        window.WonderfullyWoodMapDesigner.reconstructProductionRecordFromCartProperties =
            properties => reconstructProductionRecordFromCartProperties(properties);
        window.WonderfullyWoodMapDesigner.verifyCartProductionRecordIntegrity =
            properties => verifyCartProductionRecordIntegrity(properties);
        window.WonderfullyWoodMapDesigner.getCartSubmissionState =
            () => clonePlainObject(state.cart);
        window.WonderfullyWoodMapDesigner.addAcceptedDesignToCart =
            () => addAcceptedDesignToCart();
        window.WonderfullyWoodMapDesigner.getShopifyProductBinding =
            () => clonePlainObject(updateShopifyProductBinding());
        window.WonderfullyWoodMapDesigner.getShopifyProduct =
            () => state.commerce.product
                ? clonePlainObject(buildShopifyProductSummary(state.commerce.product))
                : null;
        window.WonderfullyWoodMapDesigner.getResolvedShopifyVariant =
            () => {
                const binding = updateShopifyProductBinding();
                return binding.variant
                    ? clonePlainObject(binding.variant)
                    : null;
            };
        window.WonderfullyWoodMapDesigner.getQgisProductionHandoff =
            () => {
                const payload = updateProductionPayload();
                return payload &&
                    payload.productionHandoff &&
                    payload.productionHandoff.qgis
                    ? clonePlainObject(payload.productionHandoff.qgis)
                    : null;
            };
        window.WonderfullyWoodMapDesigner.getAcceptedQgisProductionHandoff =
            () => state.review.acceptedPayload &&
                state.review.acceptedPayload.productionHandoff &&
                state.review.acceptedPayload.productionHandoff.qgis
                ? clonePlainObject(state.review.acceptedPayload.productionHandoff.qgis)
                : null;
        window.WonderfullyWoodMapDesigner.copyAcceptedQgisExtent3857 =
            async () => {
                const handoff = window.WonderfullyWoodMapDesigner
                    .getAcceptedQgisProductionHandoff();
                const value = handoff && handoff.extent3857
                    ? handoff.extent3857.qgisInput || ""
                    : "";
                if (value &&
                    navigator.clipboard &&
                    typeof navigator.clipboard.writeText === "function") {
                    try {
                        await navigator.clipboard.writeText(value);
                    }
                    catch (error) {
                        // Browser focus or permission restrictions must not block
                        // access to the generated production value.
                    }
                }
                return value;
            };
        window.WonderfullyWoodMapDesigner.copyAcceptedQgisExtent4326 =
            async () => {
                const handoff = window.WonderfullyWoodMapDesigner
                    .getAcceptedQgisProductionHandoff();
                const value = handoff && handoff.extent4326
                    ? handoff.extent4326.qgisInput || ""
                    : "";
                if (value &&
                    navigator.clipboard &&
                    typeof navigator.clipboard.writeText === "function") {
                    try {
                        await navigator.clipboard.writeText(value);
                    }
                    catch (error) {
                        // Browser focus or permission restrictions must not block
                        // access to the generated production value.
                    }
                }
                return value;
            };
        window.WonderfullyWoodMapDesigner.copyAcceptedQgisGeoJson4326 =
            async () => {
                const handoff = window.WonderfullyWoodMapDesigner
                    .getAcceptedQgisProductionHandoff();
                const value = handoff &&
                    handoff.extent4326 &&
                    handoff.extent4326.geoJson
                    ? JSON.stringify(handoff.extent4326.geoJson, null, 2)
                    : "";
                if (value &&
                    navigator.clipboard &&
                    typeof navigator.clipboard.writeText === "function") {
                    try {
                        await navigator.clipboard.writeText(value);
                    }
                    catch (error) {
                        // Browser focus or permission restrictions must not block
                        // access to the generated production value.
                    }
                }
                return value;
            };
        window.WonderfullyWoodMapDesigner.getAcceptedCustomerReceiptSummary =
            () => state.review.acceptedPayload &&
                state.review.acceptedPayload.summaries
                ? clonePlainObject(state.review.acceptedPayload.summaries.customerReceipt)
                : null;
        window.WonderfullyWoodMapDesigner.getAcceptedInternalProductionSummary =
            () => state.review.acceptedPayload &&
                state.review.acceptedPayload.summaries
                ? clonePlainObject(state.review.acceptedPayload.summaries.internalProduction)
                : null;
        window.WonderfullyWoodMapDesigner.getAcceptedSummaries =
            () => state.review.acceptedPayload &&
                state.review.acceptedPayload.summaries
                ? clonePlainObject(state.review.acceptedPayload.summaries)
                : null;
        window.WonderfullyWoodMapDesigner.getDesignReference =
            () => state.review.designReference;
        window.WonderfullyWoodMapDesigner.parseDesignReference =
            reference => clonePlainObject(parseDesignReference(reference));
        window.WonderfullyWoodMapDesigner.validateDesignReferenceAgainstAcceptedDesign =
            reference => state.review.acceptedPayload
                ? clonePlainObject(validateDesignReferenceAgainstPayload(reference, state.review.acceptedPayload))
                : null;
        window.WonderfullyWoodMapDesigner.validateDesignReferenceAgainstEtsySelection =
            (reference, selection) => clonePlainObject(
                validateDesignReferenceAgainstEtsySelection(reference, selection || {})
            );
        window.WonderfullyWoodMapDesigner.getEtsyChannelBoundaryAudit =
            () => clonePlainObject(buildEtsyChannelBoundaryAudit());
        window.WonderfullyWoodMapDesigner.getPageMode =
            () => state.page.mode;
        window.WonderfullyWoodMapDesigner.getPageModeMetadata =
            () => clonePlainObject(buildPageModeMetadata());
        window.WonderfullyWoodMapDesigner.getDesignCaptureForm =
            () => designCaptureForm;
        window.WonderfullyWoodMapDesigner.getDesignCaptureFormPayloadFields =
            () => ({
                pageMode: designCapturePageModeField
                    ? designCapturePageModeField.value
                    : "",
                designReference: designCaptureReferenceField
                    ? designCaptureReferenceField.value
                    : "",
                etsyConfiguration: designCaptureEtsyConfigurationField
                    ? designCaptureEtsyConfigurationField.value
                    : "",
                customerReceiptSummary: designCaptureReceiptSummaryField
                    ? designCaptureReceiptSummaryField.value
                    : "",
                productionRecord: designCaptureProductionSummaryField
                    ? designCaptureProductionSummaryField.value
                    : "",
                qgisProductionHandoff: designCapturePlainSummaryField
                    ? designCapturePlainSummaryField.value
                    : ""
            });
        window.WonderfullyWoodMapDesigner.getDesignCaptureConfirmationState =
            () => ({
                hasSubmissionSuccess: hasDesignCaptureSubmissionSuccess(),
                confirmationShownThisPage: designCaptureSubmissionConfirmationShown,
                storedSubmission: getStoredDesignCaptureSubmission(),
                visibleReference: designCaptureVisibleReference
                    ? designCaptureVisibleReference.textContent
                    : "",
                referencePanelVisible: designCaptureReferencePanel
                    ? !designCaptureReferencePanel.hidden
                    : false,
                successMessageVisible: designCaptureSuccessMessage
                    ? !designCaptureSuccessMessage.hidden
                    : false,
                etsyConfigurationVisible: designCaptureConfigurationPanel
                    ? !designCaptureConfigurationPanel.hidden
                    : false,
                etsyConfigurationText: designCaptureConfigurationSummary
                    ? designCaptureConfigurationSummary.textContent
                    : "",
                returnUrlAvailable: Boolean(getBestDesignCaptureReturnUrl()),
                returnLinkVisible: designCaptureReturnLink
                    ? !designCaptureReturnLink.hidden
                    : false,
                returnLinkHref: designCaptureReturnLink
                    ? designCaptureReturnLink.getAttribute("href") || ""
                    : ""
            });
        window.WonderfullyWoodMapDesigner.setPageModeForTesting =
            mode => clonePlainObject(setPageMode(mode, "debug_helper"));
        window.WonderfullyWoodMapDesigner.getProductionPayloadField =
            () => productionPayloadField;
        window.WonderfullyWoodMapDesigner.getState =
            () => JSON.parse(JSON.stringify(state));
        window.WonderfullyWoodMapDesigner.copyProductionPayload =
            async () => {
                const payload = updateProductionPayload();
                if (!payload ||
                    !navigator.clipboard ||
                    typeof navigator.clipboard.writeText !== "function") {
                    return payload;
                }
                await navigator.clipboard.writeText(serialiseProductionPayload(payload));
                return payload;
            };
    }
    //--------------------------------------------------
    // Search state helpers
    //--------------------------------------------------
    function updateSearchState(nextSearchState) {
        state.search = {
            ...state.search,
            ...nextSearchState
        };
    }
    function clearSearchMessage() {
        if (!searchMessage) {
            return;
        }
        searchMessage.textContent = "";
        searchMessage.hidden = true;
        searchMessage.classList.remove("is-error", "is-success");
    }
    function showSearchMessage(message, type = "status") {
        if (!searchMessage) {
            return;
        }
        searchMessage.textContent = message;
        searchMessage.hidden = false;
        searchMessage.classList.remove("is-error", "is-success");
        if (type === "error") {
            searchMessage.classList.add("is-error");
        }
        if (type === "success") {
            searchMessage.classList.add("is-success");
        }
    }
    function setSearchLoading(isLoading) {
        if (searchForm) {
            searchForm.setAttribute("aria-busy", String(isLoading));
        }
        if (!searchButton) {
            return;
        }
        searchButton.disabled = isLoading;
        searchButton.textContent = isLoading ? "Finding…" : "Find";
    }
    //--------------------------------------------------
    // Search request helpers
    //--------------------------------------------------
    function buildSearchUrl(query) {
        const params = new URLSearchParams({
            text: query,
            size: "1"
        });
        if (state.map.center) {
            params.set("focus.point.lat", String(state.map.center.lat));
            params.set("focus.point.lon", String(state.map.center.lng));
        }
        return `${SEARCH_ENDPOINT}?${params.toString()}`;
    }
    async function searchForPlace(query) {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => {
            controller.abort();
        }, SEARCH_TIMEOUT_MS);
        try {
            const response = await fetch(buildSearchUrl(query), {
                signal: controller.signal,
                headers: {
                    "Accept": "application/json"
                }
            });
            if (!response.ok) {
                const error = new Error("Search request failed");
                error.statusCode = response.status;
                throw error;
            }
            return await response.json();
        }
        finally {
            window.clearTimeout(timeout);
        }
    }
    function classifySearchError(error) {
        if (error && error.name === "AbortError") {
            return "timeout";
        }
        if (typeof navigator !== "undefined" && !navigator.onLine) {
            return "network";
        }
        if (error && error.statusCode) {
            if ([401, 403, 429].includes(error.statusCode)) {
                return "service";
            }
            if (error.statusCode >= 500) {
                return "service";
            }
            return "unknown";
        }
        if (error instanceof TypeError) {
            return "network";
        }
        return "unknown";
    }
    function getSearchErrorMessage(type) {
        if (type === "no_results") {
            if (state.search.noResultAttempts > 1) {
                return "We still could not find that place. Try the nearest town, city or landmark, or position the map manually.";
            }
            return "We could not find that exact place. Try a nearby town, city, landmark or simpler spelling.";
        }
        if (type === "network") {
            return "Search cannot connect right now. Your design is still here — reconnect, try again, or position the map manually.";
        }
        if (type === "timeout") {
            return "Search is taking longer than expected. Try a nearby town or continue positioning the map manually.";
        }
        if (type === "service") {
            return "Search is unavailable just now. Your design is still here — you can try again shortly or position the map manually.";
        }
        return "Search did not complete. Try a nearby town or landmark, or position the map manually.";
    }
    function getFeatureLabel(feature) {
        if (!feature || !feature.properties) {
            return "that place";
        }
        return feature.properties.label ||
            feature.properties.name ||
            "that place";
    }
    function getFeatureMapTitleSuggestion(feature, query) {
        const properties = feature && feature.properties
            ? feature.properties
            : {};
        const label = typeof properties.label === "string"
            ? properties.label.trim()
            : "";
        const firstLabelPart = label
            ? label.split(",")[0].trim()
            : "";
        return normaliseMapTitle(properties.name ||
            firstLabelPart ||
            query ||
            "");
    }
    function applyMapTitleSuggestion(feature, query) {
        if (state.mapTitle.customerEdited) {
            return;
        }
        const suggestion = getFeatureMapTitleSuggestion(feature, query);
        if (!suggestion) {
            return;
        }
        state.mapTitle.value = suggestion;
        state.mapTitle.source = "search_suggestion";
        state.mapTitle.suggestedFromSearch = suggestion;
        if (mapTitleInput) {
            mapTitleInput.value = suggestion;
            mapTitleInput.setCustomValidity("");
        }
    }
    function getFeatureZoom(feature) {
        const layer = feature && feature.properties
            ? feature.properties.layer
            : null;
        if (["address", "venue"].includes(layer)) {
            return 13.5;
        }
        if (["street", "neighbourhood", "locality"].includes(layer)) {
            return 11.5;
        }
        if (["county", "region"].includes(layer)) {
            return 7;
        }
        if (layer === "country") {
            return 4;
        }
        return 10.5;
    }
    function buildSelectedLocation(feature, query, lng, lat, label, zoom) {
        const properties = feature && feature.properties
            ? feature.properties
            : {};
        return {
            query,
            label,
            coordinates: {
                lng,
                lat
            },
            zoom,
            layer: properties.layer || null,
            source: "stadia",
            providerId: properties.gid || null
        };
    }
    function applySearchResult(feature, query) {
        const coordinates = feature &&
            feature.geometry &&
            Array.isArray(feature.geometry.coordinates)
            ? feature.geometry.coordinates
            : null;
        if (!coordinates || coordinates.length < 2) {
            return false;
        }
        const lng = Number(coordinates[0]);
        const lat = Number(coordinates[1]);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
            return false;
        }
        const label = getFeatureLabel(feature);
        const zoom = getFeatureZoom(feature);
        const selectedLocation = buildSelectedLocation(feature, query, lng, lat, label, zoom);
        updateSearchState({
            query,
            status: "success",
            result: selectedLocation,
            selectedLocation,
            error: null,
            noResultAttempts: 0,
            compositionAdjustedAfterSearch: false
        });
        applyMapTitleSuggestion(feature, query);
        beginProgrammaticMapMove();
        map.once("moveend", () => {
            endProgrammaticMapMove();
            updateMapState();
            updateProductionPayload();
        });
        map.flyTo({
            center: [lng, lat],
            zoom,
            duration: getSearchMoveDuration(),
            essential: true
        });
        showSearchMessage(`We found ${label}. Adjust the map until your story feels framed just right.`, "success");
        return true;
    }
    async function handleSearchSubmit(query) {
        updateSearchState({
            query,
            status: "searching",
            error: null,
            attempts: state.search.attempts + 1
        });
        setSearchLoading(true);
        showSearchMessage("Finding your special place…");
        try {
            const data = await searchForPlace(query);
            const features = data && Array.isArray(data.features)
                ? data.features
                : [];
            if (!features.length) {
                updateSearchState({
                    status: "error",
                    result: null,
                    error: {
                        type: "no_results",
                        query
                    },
                    noResultAttempts: state.search.noResultAttempts + 1
                });
                showSearchMessage(getSearchErrorMessage("no_results"), "error");
                return;
            }
            const resultApplied = applySearchResult(features[0], query);
            if (!resultApplied) {
                updateSearchState({
                    status: "error",
                    result: null,
                    error: {
                        type: "unknown",
                        query
                    }
                });
                showSearchMessage(getSearchErrorMessage("unknown"), "error");
            }
        }
        catch (error) {
            const errorType = classifySearchError(error);
            updateSearchState({
                status: "error",
                result: null,
                error: {
                    type: errorType,
                    query,
                    statusCode: error && error.statusCode ? error.statusCode : null
                }
            });
            showSearchMessage(getSearchErrorMessage(errorType), "error");
        }
        finally {
            setSearchLoading(false);
        }
    }
    //--------------------------------------------------
    // Search form
    //--------------------------------------------------
    if (searchInput) {
        searchInput.addEventListener("input", () => {
            const query = searchInput.value.trim();
            updateSearchState({
                query,
                status: query ? "editing" : "idle",
                result: null,
                error: null
            });
            clearSearchMessage();
        });
    }
    if (searchForm) {
        searchForm.addEventListener("submit", event => {
            event.preventDefault();
            const query = searchInput ? searchInput.value.trim() : "";
            if (!query) {
                updateSearchState({
                    query: "",
                    status: "error",
                    result: null,
                    error: {
                        type: "empty",
                        query: ""
                    }
                });
                showSearchMessage("Enter a town, city, landmark or special place to begin.", "error");
                return;
            }
            handleSearchSubmit(query);
        });
    }
    function updateInsetDiameterStepButtons() {
        insetDiameterStepButtons.forEach(button => {
            const target = button.dataset.insetDiameterStep;
            const direction = Number(button.dataset.stepDirection);
            const input = target === "source"
                ? insetSourceDiameterInput
                : target === "detail"
                    ? insetDetailDiameterInput
                    : null;
            if (!input || !Number.isFinite(direction)) {
                button.disabled = true;
                return;
            }
            const value = Number(input.value);
            const min = Number(input.min);
            const max = Number(input.max);
            const nextValue = value + direction;
            button.disabled =
                direction < 0
                    ? value <= min
                    : (value >= max ||
                        !isInsetDiameterCandidateValid(target, Math.min(max, nextValue)));
        });
    }
    //--------------------------------------------------
    // Detail inset controls
    // Release 2.0.0 Unit 1
    //--------------------------------------------------
    insetToggleButtons.forEach(button => {
        button.addEventListener("click", () => {
            const enabled = button.dataset.insetEnabled === "true";
            setInsetEnabled(enabled);
        });
    });
    if (insetSourceDiameterInput) {
        insetSourceDiameterInput.addEventListener("input", () => {
            const requestedValue = clampNumber(Number(insetSourceDiameterInput.value), INSET_SOURCE_MIN_DIAMETER_MM, INSET_SOURCE_MAX_DIAMETER_MM);
            const value = constrainInsetDiameterIncrease("source", requestedValue);
            state.inset.sourceDiameterMm = value;
            insetSourceDiameterInput.value = String(value);
            if (insetSourceDiameterOutput) {
                insetSourceDiameterOutput.textContent = `${value} mm`;
            }
            updateInsetGeometry();
            updateProductionPayload();
            updateInsetDiameterStepButtons();
        });
    }
    if (insetDetailDiameterInput) {
        insetDetailDiameterInput.addEventListener("input", () => {
            const requestedValue = clampNumber(Number(insetDetailDiameterInput.value), INSET_DETAIL_MIN_DIAMETER_MM, INSET_DETAIL_MAX_DIAMETER_MM);
            const value = constrainInsetDiameterIncrease("detail", requestedValue);
            state.inset.detailDiameterMm = value;
            insetDetailDiameterInput.value = String(value);
            if (insetDetailDiameterOutput) {
                insetDetailDiameterOutput.textContent = `${value} mm`;
            }
            if (state.inset.detailCenterMm) {
                const constrained = constrainDetailCenterToPerimeter(state.inset.detailCenterMm);
                state.inset.detailCenterMm = constrained.center;
                state.inset.attachment = constrained.attachment;
            }
            updateInsetGeometry();
            updateProductionPayload();
            updateInsetDiameterStepButtons();
        });
    }
    insetDiameterStepButtons.forEach(button => {
        button.addEventListener("click", () => {
            const target = button.dataset.insetDiameterStep;
            const direction = Number(button.dataset.stepDirection);
            const input = target === "source"
                ? insetSourceDiameterInput
                : target === "detail"
                    ? insetDetailDiameterInput
                    : null;
            if (!input || !Number.isFinite(direction) || direction === 0) {
                return;
            }
            if (direction < 0) {
                input.stepDown();
            }
            else {
                input.stepUp();
            }
            input.dispatchEvent(new Event("input", { bubbles: true }));
            input.focus({ preventScroll: true });
        });
    });
    updateInsetDiameterStepButtons();
    insetPrecisionTargetButtons.forEach(button => {
        button.addEventListener("click", () => {
            const target = button.dataset.insetPrecisionTarget;
            setInsetPrecisionTarget(target);
        });
    });
    insetSourceNudgeButtons.forEach(button => {
        bindInsetNudgePressAndHold(button, () => {
            const direction = button.dataset.insetSourceNudge;
            const step = INSET_KEYBOARD_STEP_MM;
            if (direction === "left") {
                nudgeInsetSource(-step, 0);
            }
            else if (direction === "right") {
                nudgeInsetSource(step, 0);
            }
            else if (direction === "up") {
                nudgeInsetSource(0, -step);
            }
            else if (direction === "down") {
                nudgeInsetSource(0, step);
            }
        });
    });
    insetDetailNudgeButtons.forEach(button => {
        bindInsetNudgePressAndHold(button, () => {
            const direction = Number(button.dataset.insetDetailNudge);
            if (!Number.isFinite(direction) || direction === 0) {
                return;
            }
            nudgeInsetDetailAlongPerimeter(direction, INSET_KEYBOARD_STEP_MM);
        });
    });
    updateInsetPrecisionControls();
    bindInsetDrag(insetSourceHandle, handleInsetSourcePointerMove, () => state.inset.sourceCenterMm);
    bindInsetDrag(insetDetailHandle, handleInsetDetailPointerMove, () => state.inset.detailCenterMm);
    if (insetSourceHandle) {
        insetSourceHandle.addEventListener("focus", () => setInsetInteractionFocus(insetSourceHandle));
        insetSourceHandle.addEventListener("keydown", moveInsetSourceByKeyboard);
    }
    if (insetDetailHandle) {
        insetDetailHandle.addEventListener("focus", () => setInsetInteractionFocus(insetDetailHandle));
        insetDetailHandle.addEventListener("keydown", moveInsetDetailByKeyboard);
    }
    //--------------------------------------------------
    // Map title
    // Release 2.0.0 Unit 5a
    //--------------------------------------------------
    if (mapTitleInput) {
        mapTitleInput.addEventListener("input", () => {
            state.mapTitle.value =
                mapTitleInput.value.slice(0, MAP_TITLE_MAX_LENGTH);
            state.mapTitle.source = "customer_map_title";
            state.mapTitle.customerEdited = true;
            mapTitleInput.setCustomValidity("");
            updateProductionPayload();
        });
    }
    //--------------------------------------------------
    // Customer guidance
    // Release 2.0.0 Unit 3b
    //--------------------------------------------------
    if (customerGuidanceInput) {
        customerGuidanceInput.addEventListener("input", () => {
            state.customerGuidance.note =
                customerGuidanceInput.value.slice(0, 300);
            updateProductionPayload();
        });
    }
    //--------------------------------------------------
    // Orientation buttons
    //--------------------------------------------------
    orientationButtons.forEach(button => {
        button.addEventListener("click", () => {
            const selectedOrientation = button.dataset.orientation;
            if (!selectedOrientation) {
                return;
            }
            if (state.orientation === selectedOrientation) {
                return;
            }
            const preservedSourceGeographicCenter = getInsetSourceGeographicCenterForPreservation();
            const preservedDetailAttachment = getInsetDetailAttachmentForPreservation();
            if (state.inspection.active) {
                exitInspectionView();
            }
            state.orientation = selectedOrientation;
            restoreInsetSourceCenterFromGeography(preservedSourceGeographicCenter);
            restoreInsetDetailAttachment(preservedDetailAttachment);
            setActiveButton(orientationButtons, button);
            announceConfigurationSelection(`Orientation: ${getSelectionAccessibleLabel(button)} selected.`);
            updateProductFrame();
            updateShopifyProductBinding();
            updateProductionPayload();
            updateAssistedConfigurationSummary();
        });
    });
    //--------------------------------------------------
    // Size buttons
    //--------------------------------------------------
    sizeButtons.forEach(button => {
        button.addEventListener("click", () => {
            const selectedSize = button.dataset.size;
            if (!selectedSize || !PRODUCTS[selectedSize]) {
                return;
            }
            if (state.size === selectedSize) {
                return;
            }
            const preservedSourceGeographicCenter = getInsetSourceGeographicCenterForPreservation();
            const preservedDetailAttachment = getInsetDetailAttachmentForPreservation();
            if (state.inspection.active) {
                exitInspectionView();
            }
            state.size = selectedSize;
            restoreInsetSourceCenterFromGeography(preservedSourceGeographicCenter);
            restoreInsetDetailAttachment(preservedDetailAttachment);
            setActiveButton(sizeButtons, button);
            announceConfigurationSelection(`Frame size: ${getSelectionAccessibleLabel(button)} selected.`);
            updateProductFrame();
            updateShopifyProductBinding();
            updateProductionPayload();
            updateAssistedConfigurationSummary();
        });
    });
    //--------------------------------------------------
    // Review preview buttons
    //--------------------------------------------------
    if (reviewPreviewButton) {
        reviewPreviewButton.addEventListener("click", () => {
            if (startNewDesignCaptureFromConfirmedView()) {
                return;
            }
            enterReviewPreview();
        });
    }
    if (reviewEditButton) {
        reviewEditButton.addEventListener("click", () => {
            exitReviewPreview();
        });
    }
    if (reviewAcceptButton) {
        reviewAcceptButton.addEventListener("click", () => {
            acceptReviewPreview();
        });
    }
    if (cartSubmitButton) {
        cartSubmitButton.addEventListener("click", () => {
            addAcceptedDesignToCart();
        });
    }
    if (designCaptureForm) {
        designCaptureForm.addEventListener("submit", () => {
            if (isDesignCaptureMode() &&
                state.review.acceptedPayload) {
                storeDesignCaptureSubmission(state.review.acceptedPayload);
            }
        });
    }
    if (designRecordOpenButton) {
        designRecordOpenButton.addEventListener("click", () => {
            renderDesignRecord();
        });
    }
    if (designRecordPrintButton) {
        designRecordPrintButton.addEventListener("click", () => {
            printDesignRecord();
        });
    }
    if (designRecordSaveButton) {
        designRecordSaveButton.addEventListener("click", () => {
            saveOrShareDesignRecord();
        });
    }
    //--------------------------------------------------
    // Apply default state
    //--------------------------------------------------
    initialiseSelectionAccessibility(orientationButtons);
    initialiseSelectionAccessibility(sizeButtons);
    initialiseSelectionAccessibility(insetToggleButtons);
    if (insetSourceDiameterOutput) {
        insetSourceDiameterOutput.textContent =
            `${state.inset.sourceDiameterMm} mm`;
    }
    if (insetDetailDiameterOutput) {
        insetDetailDiameterOutput.textContent =
            `${state.inset.detailDiameterMm} mm`;
    }
    if (mapTitleInput) {
        state.mapTitle.value =
            mapTitleInput.value.slice(0, MAP_TITLE_MAX_LENGTH);
        if (normaliseMapTitle(state.mapTitle.value)) {
            state.mapTitle.source = "customer_map_title";
            state.mapTitle.customerEdited = true;
        }
    }
    if (customerGuidanceInput) {
        state.customerGuidance.note =
            customerGuidanceInput.value.slice(0, 300);
    }
    updateProductFrame();
    updateAssistedConfigurationSummary();
    syncInspectionAvailability();
    updateShopifyProductBinding();
    updateProductionPayload();
    updateCartReadinessGate();
    updateDesignCaptureForm();
    updateCartInterface();
    updateDesignRecordInterface();
    exposeProductionDebugHelpers();
});
